import React from 'react';

import autoBind from 'react-autobind';

// import other components within the same project here

import Localization from "../Localization.jsx"

import {
  Modal,
  Box,
  Flex,
  Group,
  Stack,
  ScrollArea,
  NavLink,
  Text,
  Button,
  CloseButton,
  ActionIcon,
  Tooltip,
  TextInput,
  Textarea,
  Select,
  Switch,
  Table,
  Loader,
  Divider,
  SimpleGrid,
  Paper,
  FileButton,
  Badge,
  Checkbox,
  Alert,
  SegmentedControl,
  Collapse,
  Pill,
  UnstyledButton,
  Popover,
  Anchor,
} from '@mantine/core';
import {
  IconX,
  IconRefresh,
  IconEdit,
  IconLock,
  IconTrash,
  IconMap,
  IconShieldCheck,
  IconRestore,
  IconCheck,
  IconHourglass,
  IconUserPlus,
  IconUsers,
  IconSchool,
  IconBuildings,
  IconLicense,
  IconSearch,
  IconPlus,
  IconDownload,
  IconReceipt,
  IconUpload,
  IconUsersPlus,
  IconArrowLeft,
  IconChevronLeft,
  IconAlertTriangle,
  IconPlugConnected,
  IconLink,
  IconUnlink,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconBooks,
  IconFileTypePdf,
  IconFileUpload,
  IconHistory,
  IconMail,
  IconClock,
  IconFileImport,
  IconAdjustments,
  IconRobot,
  IconChartBar,
  IconLogin,
  IconDeviceMobile,
  IconDevices,
  IconCopy,
  IconCube,
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../theme/brandTheme.js";
import { DEFAULT_SORT_ORDER } from "./SortOrderToggle.jsx";
import {
  DEFAULT_SCHOOL_SORT_BY,
  DEFAULT_SCHOOL_SORT_DIR,
  isKeywordOnlySortColumn,
  nextSchoolSort,
  sortSchoolRows,
} from "../tools/schoolSort.js";
import { TableLoadingCard } from "./TableLoadingRow.jsx";
import {
  DEFAULT_EDCITY_SCRN_SORT_BY,
  DEFAULT_EDCITY_SCRN_SORT_DIR,
  nextEdcityScrnSort,
  sortEdcityScrnRows,
} from "../tools/edcityScrnSort.js";
import {
  EDCITY_IMPORT_MAX_ROWS,
  EDCITY_IMPORT_PLAN_LABELS,
  EDCITY_IMPORT_PLAN_LABELS_EN,
  buildEdcityImportResultWorkbook,
  buildEdcityImportTemplateWorkbook,
  edcityImportResultFileName,
  edcityImportTemplateFileName,
  isSpreadsheetFileName,
  parseEdcityImportWorkbook,
} from "../tools/edcityImportCsv.js";
import {
  DEFAULT_SUBSCRIPTION_SORT_BY,
  DEFAULT_SUBSCRIPTION_SORT_DIR,
  nextSubscriptionSort,
  sortSubscriptionRows,
} from "../tools/subscriptionSort.js";
import { buildExtraLocaleCsv } from "../tools/extraLocaleCsv.js";
import { downloadManual, manualUrl } from "../tools/manualDownload.js";
import { manualFileError, uploadManualPdf } from "../tools/manualUpload.js";
import FilePicker from "./FilePicker.jsx";
import {
  buildSelectionHistoryCsv,
  defaultSelectionRange,
  filterSelectionsByDayRange,
  isSelectionHistoryTruncated,
  selectionHistoryFileName,
  selectionRangeError,
  SELECTION_HISTORY_EXPORT_LIMIT,
} from "../tools/selectionHistoryCsv.js";
import {
  ELAFP_FILTER_KEYWORD,
  subscriptionKeywordPreset,
  filterSubscriptionsForPreset,
} from "../tools/subscriptionKeyword.js";
import { describeSchoolResultCount } from "../tools/schoolResultCount.js";
import {
  buildSchoolListCsv,
  schoolListCsvFileName,
  SCHOOL_TYPE_OPTIONS,
} from "../tools/schoolListCsv.js";

const PLAN_EXTRA_LOCALE = 'PLAN_EXTRA_LOCALE';
const LOADING_CARD_ICON_PROPS = { size: 40, stroke: 1.5, color: 'var(--mantine-color-gray-6)' };
const DEFAULT_SUBSCRIPTION_DURATION_MONTHS = '12';
const ADMIN_ACTIVE_TAB_STORAGE_KEY = 'ev_admin_active_tab';
const ADMIN_TAB_COUNT = 10;
// Sidebar index of the first "EdCity Login" section page.
const EDCITY_SCRN_TAB_INDEX = 5;
// Sidebar index of the Extra Locale (預製教材範本) section page — platform
// admins (role >= 3) only; the server enforces the same gate on every route.
const EXTRA_LOCALE_TAB_INDEX = 6;
// Second page of that section: who receives the scheduled Selection History
// mail. Its own sidebar item rather than a third SegmentedControl segment next
// to 範本管理 / 選擇紀錄, because that control switches between two views of the
// SAME ledger and this is configuration for it — a settings form sitting in
// that row would read as a third way to look at the data.
const EXTRA_LOCALE_REPORT_TAB_INDEX = 7;
// Platform dashboard (數據概覽). Its own sidebar section, placed below Extra
// Locales — not an item inside that group. Same role >= 3 gate.
const DASHBOARD_TAB_INDEX = 9;
// Second page of the EdCity Login section: which subscription TEMPLATE each
// plan tier is cloned from by the mapping Excel import (訂閱綁定).
const SUBSCRIPTION_BINDING_TAB_INDEX = 8;

// Kinds the "send now" button can ask for, and what each one's window is. The
// SERVER computes the dates (see selection_report_service.window_for) — these
// labels only have to agree with it, and the schedule they describe is the
// ACA job's: 09:00 Hong Kong, daily, with the weekly riding along on Mondays.
const REPORT_SEND_KINDS = ['daily', 'weekly'];

// ---------------------------------------------------------- subscription keyword filter
// School List can filter by ONE subscription (SubscriptionPicker -> subscription_id)
// or by a subscription KEYWORD, which is the union of every subscription whose
// name / name_en / remarks matches. The keyword mode is resolved entirely on the
// client — subscription_get(keyword) to find the ids, then one
// get_schools_by_subscription per id, merged and de-duplicated by school id — so
// it needs no new backend route.
//
// The eLAFP toggle is a preset keyword rather than a hardcoded id list, because
// the programme's rows are not named alike and future rounds add more of them.
// A preset resolves to several keywords and is then narrowed to NAME matches —
// tools/subscriptionKeyword.js carries the full reasoning, including the
// unrelated grant that a remarks match pulls in.
//
// Runaway guard on the fan-out, set to subscription_get's own server-side ceiling
// so the page it returns can never be shorter than the match set without the
// server having said so.
//
// This was 50, picked when the whole `subscription` table was a few dozen rows
// and the guard "was never expected to bind". The 26/27 eLAFP rollout creates ONE
// subscription per school (ids 39 upward, ~46 of them in August alone), so the
// eLAFP keyword reached exactly 50 matches on 2026-08-31 — one school short of
// silent loss, and silent in the worst direction: the lookup is ordered id ASC,
// so what falls off the end is always the most recently onboarded school. Hitting
// the cap is now reported (SCHOOL_SUBSCRIPTION_KEYWORD_CAPPED) rather than
// trusted.
const SUBSCRIPTION_KEYWORD_MAX_SUBSCRIPTIONS = 500;

// Row ceiling for the School List CSV export on the PLAIN list, which is the one
// search the server windows: the table shows a 50–500 page, but the export is
// meant to be the whole filtered set, so it re-requests at this limit instead of
// handing over whatever page happened to be on screen.
//
// 1000 is get_filtered_school_list's own ceiling (school_service: a limit above
// it does not clamp, it falls back to 100), so asking for more would quietly
// return FEWER rows. Coming back exactly this full is reported rather than
// trusted, for the same reason the list's own count says "可能未完" — that
// command returns rows, not a count, so a full page is the only truncation
// signal there is.
const SCHOOL_EXPORT_MAX_ROWS = 1000;

// generic_crud_service._UNLIMITED. Do NOT branch the UI on this value: real
// balances reach the same magnitude, so `epack_quota_unlimited` is the flag that
// actually distinguishes "not enforced" from "a very large balance".
const EPACK_QUOTA_UNLIMITED = 99999;

/** Display label for a subscription row, preferring the localized name. */
function subscriptionLabel(sub) {
  if (!sub) return '';
  return sub.name || sub.name_en || ('#' + sub.id);
}


function createSubscriptionLocalesDialogState(overrides = {}) {
  return {
    open: false,
    subscription: null,
    rows: [],
    loading: false,
    searchMode: "keyword",
    searchText: "",
    searchSourceResults: [],
    selectedSearchSource: null,
    searchResults: [],
    selectedLocaleIds: [],
    selectedAssignedLocaleIds: [],
    searchLoading: false,
    assigning: false,
    removing: false,
    ...overrides,
  };
}

function createSubscriptionCreateDialogState(overrides = {}) {
  return {
    open: false,
    name: "",
    name_en: "",
    remarks: "",
    is_template: false,
    loading: false,
    templates: [],
    templatesLoading: false,
    copyExtraLocales: false,
    sourcePrivileges: [],
    sourcePrivilegesLoading: false,
    returnToSchoolSubscription: false,
    ...overrides,
  };
}

function createEdcityScrnCreateDialogState(overrides = {}) {
  return {
    open: false,
    school_code: "",
    school_id: "",     // Mantine Select value — string school id or ""
    note: "",
    loading: false,
    ...overrides,
  };
}

function createEdcityScrnConnectDialogState(overrides = {}) {
  return {
    open: false,
    row: null,         // the mapping row being connected
    school_id: "",     // Mantine Select value — string school id or ""
    note: "",
    loading: false,
    ...overrides,
  };
}

// The server's row model is extra="forbid": send only what it declares.
function edcityImportRowForServer(row) {
  return {
    scrn: row.scrn,
    school_name_edcity: row.school_name_edcity,
    school_id: row.school_id,
    plans: row.plans,
    line: row.line,
  };
}

function createEdcityImportDialogState(overrides = {}) {
  return {
    open: false,
    step: 'upload',      // 'upload' | 'confirm' | 'result'
    fileName: "",
    rows: [],            // parsed workbook rows (tools/edcityImportCsv.js), by index
    reviewRows: [],      // server dry-run rows
    summary: null,       // server dry-run summary (counts, ai status)
    selected: {},        // index -> bool, which review rows to commit
    resultRows: [],
    result: null,
    isLoading: false,
    fileInputKey: 0,
    ...overrides,
  };
}

function createSubscriptionBindingState(overrides = {}) {
  return {
    rows: [],            // server bindings, one per plan
    edits: {},           // plan -> {subscription_id (string), name_pattern, name_pattern_en}
    // The grant window every imported subscription gets (YYYY-MM-DD or "").
    dateStart: "",
    dateExpire: "",
    editDateStart: "",
    editDateExpire: "",
    templates: [],
    templatesLoading: false,
    loading: false,
    loaded: false,
    saving: false,
    ...overrides,
  };
}

function createExtraLocaleEditDialogState(overrides = {}) {
  return {
    open: false,
    row: null,          // null = create, otherwise the catalog row being edited
    category: "",
    remarks: "",
    // The template IS an epack, picked by NAME: the admin types part of the
    // LOCALE name, a debounced search_epack_by_keyword fills the option list,
    // and choosing an option pins epack_id. Typing again un-pins it, so a
    // stale pick can never be saved under a new search text.
    epack_id: 0,
    resolvedEpackName: "",
    epackSearchText: "",
    epackSearchOptions: [],   // [{id, name, school_name}]
    epackSearchLoading: false,
    epackSearchDone: false,   // a search finished for the current text (shows 沒有符合)
    category_index: "", // orders the CATEGORY group in the selection form
    display_index: "",
    is_enabled: true,
    // The 教師參考手冊 (teacher reference manual) PDF. The FilePicker below
    // uploads it through the ordinary two-phase blob flow the moment a file is
    // dropped — so the `file` row exists before Save — and these three fields
    // are what the PATCH then links and what the picker re-renders from.
    // manual_file_id 0 with the dialog opened on a row that HAD one is the
    // detach, and it is sent as 0 rather than omitted (see handleExtraLocaleEditSave).
    manual_file_id: 0,
    manual_path: "",
    manual_display_name: "",
    loading: false,
    ...overrides,
  };
}

function loadAdminActiveTab() {
  try {
    const stored = window.localStorage.getItem(ADMIN_ACTIVE_TAB_STORAGE_KEY);
    if (stored != null) {
      const index = parseInt(stored, 10);
      if (Number.isInteger(index) && index >= 0 && index < ADMIN_TAB_COUNT) {
        return index;
      }
    }
  } catch (e) {
    // localStorage throws in private mode / when storage is disabled.
  }
  return 0;
}

function saveAdminActiveTab(index) {
  try {
    window.localStorage.setItem(ADMIN_ACTIVE_TAB_STORAGE_KEY, String(index));
  } catch (e) {
    // localStorage throws in private mode / when storage is disabled.
  }
}

function formatDashboardCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '0';
  }
  return Math.trunc(n).toLocaleString('en-US');
}

function dashboardDeltaLabel(current, previous) {
  const diff = (Number(current) || 0) - (Number(previous) || 0);
  if (diff === 0) {
    return { text: '0', color: 'gray' };
  }
  const sign = diff > 0 ? '+' : '';
  return { text: sign + formatDashboardCount(diff), color: diff > 0 ? 'teal' : 'red' };
}

function dashboardRangeText(from, to) {
  if (!from) {
    return '';
  }
  if (!to || from === to) {
    return from;
  }
  return `${from} – ${to}`;
}

function DashboardBars({ series }) {
  const rows = Array.isArray(series) ? series : [];
  if (rows.length <= 1) {
    return null;
  }
  const max = Math.max(1, ...rows.map((row) => Number(row.total) || 0));
  return (
    <Group gap={3} align="flex-end" h={32} wrap="nowrap" mt={8}>
      {rows.map((row) => (
        <Tooltip key={row.date} label={`${row.date}: ${formatDashboardCount(row.total)}`}>
          <Box
            style={{
              flex: 1,
              minWidth: 4,
              height: Math.max(2, Math.round(((Number(row.total) || 0) / max) * 28)),
              background: BRAND_PRIMARY,
              borderRadius: 2,
              opacity: 0.85,
            }}
          />
        </Tooltip>
      ))}
    </Group>
  );
}

function DashboardNotes() {
  const [open, setOpen] = React.useState(false);
  const strings = Localization.strings;
  return (
    <Paper withBorder radius="md" p="md" bg="gray.0">
      <UnstyledButton
        onClick={() => setOpen((value) => !value)}
        w="100%"
        aria-expanded={open}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={600}>{strings.DASHBOARD_NOTES_TITLE || '這些數字怎麼計算'}</Text>
          <IconChevronDown
            size={16}
            style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
          />
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack gap={4} mt={6}>
          <Text size="xs" c="dimmed">{strings.DASHBOARD_NOTE_WINDOW}</Text>
          <Text size="xs" c="dimmed">{strings.DASHBOARD_NOTE_ACTIVITY}</Text>
          <Text size="xs" c="dimmed">{strings.DASHBOARD_NOTE_LOCALES}</Text>
          <Text size="xs" c="dimmed">{strings.DASHBOARD_NOTE_REST}</Text>
        </Stack>
      </Collapse>
    </Paper>
  );
}

function DashboardCard({ title, hint, total, previous, series, icon, extra }) {
  const delta = dashboardDeltaLabel(total, previous);
  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed" fw={600}>{title}</Text>
        <Box c="var(--mantine-color-gray-6)">{icon}</Box>
      </Group>
      <Group align="flex-end" gap="sm" mt={8}>
        <Text fz={32} fw={700} lh={1}>{formatDashboardCount(total)}</Text>
        <Badge variant="light" color={delta.color} size="sm">{delta.text}</Badge>
      </Group>
      {extra}
      {hint ? <Text size="xs" c="dimmed" mt={6}>{hint}</Text> : null}
      <DashboardBars series={series} />
    </Paper>
  );
}

function isSubscriptionActive(dateStart, dateExpire) {
  const start = String(dateStart || '').slice(0, 10);
  const expire = String(dateExpire || '').slice(0, 10);
  if (!start || !expire) return false;

  const now = new Date();
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  return start <= today && today <= expire;
}

function getTodayDateString() {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function getSubscriptionEndDate(dateStart, durationMonths) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStart || '');
  const months = Number(durationMonths);
  if (!match || !Number.isInteger(months) || months <= 0) return '';

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const targetMonthIndex = monthIndex + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const boundary = new Date(Date.UTC(targetYear, targetMonth, Math.min(day, daysInTargetMonth)));

  // The subscription ends the day before the matching date in the target month.
  boundary.setUTCDate(boundary.getUTCDate() - 1);
  return [
    boundary.getUTCFullYear(),
    String(boundary.getUTCMonth() + 1).padStart(2, '0'),
    String(boundary.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

class SubscriptionPicker extends React.Component {
  constructor(props) {
    super(props);
    const selected = props.selected || null;
    this.state = {
      inputValue: selected ? (selected.name || selected.name_en || "") : "",
      suggestions: [],
      selected,
      loading: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.fetchSuggestions = this.fetchSuggestions.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  handleChange(e) {
    const val = e.target.value;
    this.setState({ inputValue: val, selected: null });
    if (val && val.trim().length > 0) {
      this.fetchSuggestions(val.trim());
    } else {
      this.setState({ suggestions: [] });
    }
  }

  handleSelect(s) {
    this.setState({
      selected: this.props.searchOnly ? null : s,
      inputValue: this.props.searchOnly ? "" : (s.name || s.name_en || ""),
      suggestions: [],
    });
    if (this.props.onSelect) {
      this.props.onSelect(s);
    }
  }

  handleBlur() {
    if (this.props.searchOnly) {
      this.setState({ suggestions: [] });
      return;
    }
    if (!this.state.selected) {
      this.setState({ inputValue: "", suggestions: [] });
      if (this.props.onSelect) {
        this.props.onSelect(null);
      }
    }
  }

  fetchSuggestions(keyword) {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({ loading: true });
    this.props.root.refs.API.request(
      "subscription_get",
      { keyword, limit: 10, sort_by: "date_create", sort_dir: DEFAULT_SORT_ORDER },
      (res) => {
        const list = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState({ suggestions: list, loading: false });
      },
      () => {
        this.setState({ suggestions: [], loading: false });
      }
    );
  }

  render() {
    const { placeholder, disabled } = this.props;
    return (
      <div style={{ position: 'relative' }}>
        <TextInput
          value={this.state.inputValue}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          leftSection={this.props.searchOnly ? <IconSearch size={16} /> : undefined}
          rightSection={this.state.loading ? <Loader size={16} /> : undefined}
        />
        {this.state.suggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 10
          }}>
            {this.state.suggestions.map((s, idx) => (
              <div
                key={idx}
                onMouseDown={() => this.handleSelect(s)}
                style={{ padding: '8px 10px', cursor: 'pointer' }}
              >
                <div>{s.name || '-'}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{s.name_en || ''}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

class AdminTaskDialog extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      // aNumber : 1,
      // aString: "abc",
      // anArray: [],
      // anObject: {},
      open: false,
      activeTab: loadAdminActiveTab(),
      searchText: "",
      totalPage: 0,
      currentPage: 0,
      schoolList: [],
      schoolSearchResults: [],
      schoolSearchText: "",
      schoolFilters: {
        school_type: "",
        order_by: DEFAULT_SCHOOL_SORT_BY,
        order: DEFAULT_SCHOOL_SORT_DIR,
        limit: 100,
        // Subscription-filtered searches drop lapsed grants by default; the
        // checkbox beside the eLAFP button turns them back on. Only
        // get_schools_by_subscription reads it — see handleSchoolSearchClicked.
        include_expired: false,
      },
      schoolSubscriptionFilter: null,
      // Non-empty => subscription KEYWORD mode (union of every matching
      // subscription). Mutually exclusive with schoolSubscriptionFilter.
      schoolSubscriptionKeyword: "",
      // Bumped to remount SubscriptionPicker so its internal input clears when
      // the keyword filter takes over from a specific picked subscription.
      schoolSubscriptionPickerKey: 0,
      // Keyword mode only: the merged list BEFORE the result limit trims it, so
      // a header click can re-sort the whole set instead of just the page.
      schoolKeywordAllResults: [],
      hasSchoolSearched: false,
      schoolLoading: false,
      schoolExporting: false,
      schoolNavigationDirection: null,
      newUser: {
        username: "",
        displayName: "",
        email: "",
        password0: "",
        accessRight: 0,
        schoolID: "",
      },
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      },
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      },
      tableRows: [],
      subscriptionList: [],
      subscriptionSearchText: "",
      subscriptionSortBy: DEFAULT_SUBSCRIPTION_SORT_BY,
      subscriptionSortDir: DEFAULT_SUBSCRIPTION_SORT_DIR,
      subscriptionLimit: 100,
      hasSubscriptionSearched: false,
      subscriptionLoading: false,
      subscriptionEditingCell: null,
      subscriptionUpdatingFields: {},
      subscriptionNavigationDirection: null,
      subscriptionLocalesDialog: createSubscriptionLocalesDialogState(),
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      },
      subscriptionCreateDialog: createSubscriptionCreateDialogState(),
      subscriptionDeleteDialog: {
        open: false,
        subscription: null,
        loading: false,
        adminPassword: "",
        assignedSchools: [],
        blocked: false,
      },
      subscriptionCopyPrivilegeFrom: null,
      schoolSubscriptionDialog: {
        open: false,
        school: null,
        rows: [],
        loading: false,
        addSelected: null,
        addLoading: false,
        addDateStart: getTodayDateString(),
        addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
        pickerKey: 0,
        editingRow: null,
        editingField: null,
        updateLoading: false,
        deleteLoading: false,
        deleteConfirmRow: null,
      },
      // Password change dialog state
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      },
      // Delete user dialog state
      deleteUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        isLoading: false,
      },
      // Edit school dialog state
      editSchoolDialog: {
        open: false,
        targetSchoolId: 0,
        name: "",
        nameEng: "",
        key: "",
        locale: "",
        schoolType: 0,
        schoolLevel: 0,
        adminPassword: "",
        isLoading: false,
      },
      // Edit user dialog state
      editUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        displayName: "",
        email: "",
        role: 0,
        adminPassword: "",
        isLoading: false,
      },
      // Batch create user (CSV) dialog state
      batchCreateDialog: this.getInitialBatchCreateDialogState(),
      // EdCity Login > EdCity School ID Mapping (school_edcity_scrn)
      edcityScrnRows: [],
      edcityScrnLoading: false,
      hasEdcityScrnSearched: false,
      edcityScrnSearchText: "",
      edcityScrnMappedFilter: "ALL",   // 'ALL' | '1' mapped | '0' not mapped
      edcityScrnSortBy: DEFAULT_EDCITY_SCRN_SORT_BY,
      edcityScrnSortDir: DEFAULT_EDCITY_SCRN_SORT_DIR,
      edcityScrnCreateDialog: createEdcityScrnCreateDialogState(),
      edcityScrnConnectDialog: createEdcityScrnConnectDialogState(),
      edcityScrnDeleteRow: null,
      edcityScrnDeleteLoading: false,
      edcityImportDialog: createEdcityImportDialogState(),
      subscriptionBinding: createSubscriptionBindingState(),
      // Extra Locale (預製教材範本) — catalog management + selection history
      extraLocaleView: "catalog",   // 'catalog' | 'history'
      extraLocaleRows: [],
      extraLocaleLoading: false,
      hasExtraLocaleLoaded: false,
      extraLocaleTogglingIds: {},   // {id: true} while an enable-switch PATCH is in flight
      extraLocaleHistoryRows: [],
      extraLocaleHistoryLoading: false,
      hasExtraLocaleHistoryLoaded: false,
      extraLocaleHistorySearchText: "",
      // Start/end CALENDAR DAYS the Selection History CSV covers, both ends
      // inclusive, as bare 'YYYY-MM-DD' — the shape <input type="date"> emits
      // and the shape the route's date_from/date_to take. Never turned into a
      // Date: parsing a bare day string lands on UTC midnight and would drop
      // the end day's afternoon (see selectionHistoryCsv.js's header).
      //
      // Scopes the EXPORT ONLY — the table keeps listing the newest records
      // whatever is picked here, which is why the controls are labelled as an
      // export range. An empty end means unbounded on that side.
      ...(() => {
        const range = defaultSelectionRange(new Date());
        return {
          extraLocaleHistoryFrom: range.from,
          extraLocaleHistoryTo: range.to,
        };
      })(),
      extraLocaleHistoryExporting: false,
      extraLocaleEditDialog: createExtraLocaleEditDialogState(),
      extraLocaleDeleteRow: null,
      extraLocaleDeleteLoading: false,
      extraLocaleExporting: false,
      // 教師參考手冊, managed straight from the LIST — {catalogId: true} while an
      // upload or a detach is in flight, so one row's spinner cannot disable the
      // rest of the table.
      extraLocaleManualBusyIds: {},
      // The row whose manual a confirm dialog is asking about. Detaching is not
      // destructive to the TEMPLATE, but it does take the download away from
      // every school holding it, so it is worth one click of friction.
      extraLocaleManualDeleteRow: null,
      // Bumped per row so re-picking the SAME file fires onChange again — a
      // FileButton compares by identity and would otherwise ignore the retry.
      extraLocaleManualPickerKey: 0,
      // Extra Locale > scheduled report settings (GET/PUT reportSettings).
      //
      // `reportRecipientsText` is the TEXTAREA's value and the only field the
      // user edits freely; the server stores it normalised (one address per
      // line, invalid tokens refused with a 400 that names them) and the page
      // reloads from the server after every save, so what is on screen is
      // always what the job will actually use. `reportSettings` holds the
      // server's last answer — including `smtp_configured`, which is not a
      // setting but the reason a correctly-filled page can still deliver
      // nothing.
      reportSettings: null,
      reportRecipientsText: "",
      reportDailyEnabled: true,
      reportWeeklyEnabled: true,
      reportSendWhenEmpty: true,
      reportLoading: false,
      reportSaving: false,
      reportSendingKind: "",     // 'daily' | 'weekly' while a manual send is in flight
      hasReportLoaded: false,
      // True once a field diverges from `reportSettings`, so the Save button can
      // say whether there is anything to save. Tracked rather than derived so a
      // save that fails leaves the edits (and the dirty state) intact.
      reportDirty: false,
      // Extra Locales > Dashboards. `dashboard.period` is what the payload
      // describes; `dashboardPeriod` is what the control is asking for. They
      // differ while a switch is in flight, which is how the page avoids
      // showing last week's numbers under "Today".
      dashboardPeriod: 'day',
      dashboard: null,
      dashboardLoading: false,
      dashboardError: false,
    };
    this.subscriptionLocaleSearchTimer = null;
    this.subscriptionLocaleSearchRequestId = 0;
    // School search is now multi-request (keyword mode fans out over N
    // subscriptions), so every response has to prove it belongs to the newest
    // search before it may touch state.
    this.schoolSearchRequestId = 0;
    this.dashboardRequestId = 0;
    autoBind(this);
    
    // Only load school list if API ref is ready
    if (this.props.root.refs && this.props.root.refs.API) {
      this.props.root.refs.API.request("get_school_list", {}, this.getSchoolListOK, this.getSchoolListFailed);
    }

  }

  componentWillUnmount() {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
    }
    if (this.extraLocaleEpackResolveTimer) {
      clearTimeout(this.extraLocaleEpackResolveTimer);
    }
    this.subscriptionLocaleSearchRequestId += 1;
  }

  getTasks() {
    const all = [
      Localization.strings.CREATE_USER,
      Localization.strings.USER_LIST,
      Localization.strings.CREATE_SCHOOL,
      Localization.strings.SCHOOL_LIST,
      Localization.strings.SUBSCRIPTION_TAB,
    ];
    // School admins: the two "my school" pages only (see isSchoolAdminOnly).
    return this.isSchoolAdminOnly() ? all.slice(0, 2) : all;
  }

  loadSchoolList() {
    // Load school list if API ref is ready
    if (this.props.root.refs && this.props.root.refs.API) {
      this.props.root.refs.API.request("get_school_list", {}, this.getSchoolListOK, this.getSchoolListFailed);
    }
  }

  getSchoolListOK(res) {
    let tmpList = [];
    let schoolList = Array.isArray(res?.body?.result) ? res.body.result : [];
    for (let i = 0; i < schoolList.length; i++) {
      let schoolRow = schoolList[i];
      tmpList.push({
        school_id: schoolRow['id'],
        shcool_name: schoolRow['name'],
        school_eng_name: schoolRow['name_eng'],
        school_locale: schoolRow['locale'],
        school_key: schoolRow['key'],
        school_type: schoolRow['school_type']
      });
    }
    this.setState({
      schoolList: tmpList,
    });
    this.state.schoolList = tmpList;
  }

  getSchoolListFailed(err) {
    console.log("get school list failed");
  }

  changeTab(index) {
    let newIndex = this.state.activeTab;
    if (newIndex == index) {
      return;
    }
    newIndex = index;
    this.setState({
      activeTab: newIndex,
      searchText: "",
      tableRows: [],
    });
    this.state.activeTab = newIndex;
    this.state.searchText = "";
    this.state.tableRows = [];
    saveAdminActiveTab(newIndex);
    this.ensureActiveTabData();
  }

  ensureActiveTabData() {
    const tab = this.state.activeTab;
    const platform = this.isPlatformAdmin();

    // School admins: the User List IS their roster, so it opens already
    // listed — an empty keyword asks the server for the whole school (the
    // route pins them to it). changeTab cleared searchText/tableRows, so this
    // fires on every entry and never stacks a stale keyword.
    if (tab === 1 && !platform) {
      setTimeout(() => {
        this.handleSearchClicked();
      }, 100);
    }

    // Everything below is a platform-only page; a school admin never sees the
    // sidebar item and the loads would only 403 into error toasts.
    // Auto-search for school list when entering that tab for the first time
    if (tab === 3 && platform && !this.state.hasSchoolSearched) {
      setTimeout(() => {
        this.handleSchoolSearchClicked();
      }, 100);
    }

    // Auto-search subscriptions on first enter
    if (tab === 4 && platform && !this.state.hasSubscriptionSearched) {
      setTimeout(() => {
        this.handleSubscriptionSearch();
      }, 100);
    }

    // Auto-load the EdCity SCRN mapping list on first enter
    if (tab === EDCITY_SCRN_TAB_INDEX && platform && !this.state.hasEdcityScrnSearched) {
      setTimeout(() => {
        this.handleEdcityScrnSearch();
      }, 100);
    }

    // Auto-load the plan -> template bindings on first enter.
    if (tab === SUBSCRIPTION_BINDING_TAB_INDEX && platform && !this.state.subscriptionBinding.loaded) {
      setTimeout(() => {
        this.loadSubscriptionBinding();
      }, 100);
    }

    // Auto-load the Extra Locale catalog on first enter. The platform-admin
    // test matters here too: a role-2 admin can RESTORE this tab index from
    // localStorage without ever seeing the sidebar item, and the load would
    // just 403 into an error toast on open.
    if (tab === EXTRA_LOCALE_TAB_INDEX && !this.state.hasExtraLocaleLoaded
        && this.isPlatformAdmin()) {
      setTimeout(() => {
        this.loadExtraLocaleCatalog();
      }, 100);
    }

    // Auto-load the scheduled-report settings on first enter, under the same
    // platform-admin test — the route is require_platform_admin too.
    if (tab === EXTRA_LOCALE_REPORT_TAB_INDEX && !this.state.hasReportLoaded
        && this.isPlatformAdmin()) {
      setTimeout(() => {
        this.loadReportSettings();
      }, 100);
    }

    // Dashboards are a snapshot, so every visit refetches. A school admin can
    // restore this index from localStorage; the load would 403.
    if (tab === DASHBOARD_TAB_INDEX && this.isPlatformAdmin()) {
      setTimeout(() => {
        this.loadDashboard(this.state.dashboardPeriod || 'day');
      }, 100);
    }
  }

  // Extra Locale management is PLATFORM-admin only (user.role >= 3, the CLST
  // Administrator role); ordinary role-2 admins keep the rest of this dialog.
  // The server enforces the same gate on every /api/admin/extraLocale route.
  isPlatformAdmin() {
    const userInfo = this.props.root.refs && this.props.root.refs.userDataProfile
      ? this.props.root.refs.userDataProfile.state.userInfo
      : null;
    return !!userInfo && parseInt(userInfo.role) >= 3;
  }

  /**
   * A SCHOOL admin — user.role = 2, below the platform tier. Since 2026-09-18
   * this panel is, for them, their OWN school's user management and nothing
   * else: Create User (into their school, no school id to type) and User List
   * (their roster). Every other page is hidden here and refused on the server
   * (ev_web_v2 admin.py `_require_school_scope` / `_assert_target_in_scope`,
   * misc.create_school), so this is presentation, not the gate.
   */
  isSchoolAdminOnly() {
    return !this.isPlatformAdmin();
  }

  /** The sidebar indices this admin may open. */
  isTabAllowed(index) {
    return this.isPlatformAdmin() || index === 0 || index === 1;
  }

  /**
   * Roles this admin may assign. A school admin (role 2) gets 一般用戶 and
   * 進階用戶 only — not 管理員. `currentRole` keeps an already-stored 管理員
   * visible while editing that account. Platform admins (role >= 3) may assign
   * 管理員, and on create may also assign a CLST administrator.
   */
  assignableRoleOptions({ includePlatformAdmin = false, currentRole = null } = {}) {
    const options = [
      { value: '0', label: this.getRoleText(0) },
      { value: '1', label: this.getRoleText(1) },
    ];
    const stored = parseInt(currentRole, 10);
    if (!this.isSchoolAdminOnly() || stored === 2) {
      options.push({ value: '2', label: this.getRoleText(2) });
    }
    if (includePlatformAdmin && !this.isSchoolAdminOnly()) {
      options.push({
        value: '3',
        label: Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator',
      });
    }
    return options;
  }

  /** The admin's own school, named in the current language, for the read-only
   *  school field on Create User. */
  mySchoolLabel() {
    const profile = this.props.root.refs && this.props.root.refs.userDataProfile
      ? this.props.root.refs.userDataProfile.state
      : null;
    const school = profile && profile.schoolInfo ? profile.schoolInfo : {};
    const name = Localization.strings.getLanguage() === 'en'
      ? (school.name_eng || school.name || '')
      : (school.name || school.name_eng || '');
    const id = this.props.root.getMySchoolId ? this.props.root.getMySchoolId() : null;
    return name ? `${name} (#${id})` : (id ? `#${id}` : '');
  }

  openDialog() {
    // A school admin can carry a platform-only tab index in localStorage (from
    // an earlier session, or a role change). Land them on Create User rather
    // than on a page the sidebar no longer offers.
    if (!this.isTabAllowed(this.state.activeTab)) {
      this.setState({ activeTab: 0, searchText: "", tableRows: [] });
      this.state.activeTab = 0;
      this.state.searchText = "";
      this.state.tableRows = [];
      saveAdminActiveTab(0);
    }
    this.setState({ open: true });
    this.state.open = true;
    this.ensureActiveTabData();
  }

  handleTabChange = (event, index) => {
    this.changeTab(index);
  }

  close() {
    this.setState({ open: false });
    this.state.open = false;
  }

  handleClose() {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    // Reset form data, but keep the last side-menu tab for next open.
    this.setState({ 
      searchText: "",
      schoolSearchText: "",
      tableRows: [],
      schoolSubscriptionFilter: null,
      subscriptionList: [],
      subscriptionSearchText: "",
      subscriptionSortBy: DEFAULT_SUBSCRIPTION_SORT_BY,
      subscriptionSortDir: DEFAULT_SUBSCRIPTION_SORT_DIR,
      subscriptionLimit: 100,
      hasSubscriptionSearched: false,
      subscriptionLoading: false,
      subscriptionEditingCell: null,
      subscriptionUpdatingFields: {},
      subscriptionNavigationDirection: null,
      subscriptionLocalesDialog: createSubscriptionLocalesDialogState(),
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      },
      subscriptionCreateDialog: createSubscriptionCreateDialogState(),
      subscriptionCopyPrivilegeFrom: null,
      schoolSubscriptionFilter: null,
      schoolSubscriptionKeyword: "",
      schoolKeywordAllResults: [],
      schoolSubscriptionDialog: {
        open: false,
        school: null,
        rows: [],
        loading: false,
        addSelected: null,
        addLoading: false,
        addDateStart: getTodayDateString(),
        addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
        pickerKey: 0,
        editingRow: null,
        editingField: null,
        updateLoading: false,
        deleteLoading: false,
        deleteConfirmRow: null,
      },
      schoolSearchResults: [],
      hasSchoolSearched: false,
      schoolLoading: false,
      schoolExporting: false,
      schoolNavigationDirection: null,
      newUser: {
        username: "",
        displayName: "",
        email: "",
        password0: "",
        accessRight: 0,
        schoolID: "",
      },
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      },
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      },
      schoolFilters: {
        school_type: "",
        order_by: DEFAULT_SCHOOL_SORT_BY,
        order: DEFAULT_SCHOOL_SORT_DIR,
        limit: 100,
        // Subscription-filtered searches drop lapsed grants by default; the
        // checkbox beside the eLAFP button turns them back on. Only
        // get_schools_by_subscription reads it — see handleSchoolSearchClicked.
        include_expired: false,
      },
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      },
      subscriptionDeleteDialog: {
        open: false,
        subscription: null,
        loading: false,
        adminPassword: "",
        assignedSchools: [],
        blocked: false,
      },
      batchCreateDialog: this.getInitialBatchCreateDialogState(),
      edcityScrnRows: [],
      edcityScrnLoading: false,
      hasEdcityScrnSearched: false,
      edcityScrnSearchText: "",
      edcityScrnMappedFilter: "ALL",
      edcityScrnSortBy: DEFAULT_EDCITY_SCRN_SORT_BY,
      edcityScrnSortDir: DEFAULT_EDCITY_SCRN_SORT_DIR,
      edcityScrnCreateDialog: createEdcityScrnCreateDialogState(),
      edcityScrnConnectDialog: createEdcityScrnConnectDialogState(),
      edcityScrnDeleteRow: null,
      edcityScrnDeleteLoading: false,
      edcityImportDialog: createEdcityImportDialogState(),
      subscriptionBinding: createSubscriptionBindingState(),
      extraLocaleView: "catalog",
      extraLocaleRows: [],
      extraLocaleLoading: false,
      hasExtraLocaleLoaded: false,
      extraLocaleTogglingIds: {},
      extraLocaleHistoryRows: [],
      extraLocaleHistoryLoading: false,
      hasExtraLocaleHistoryLoaded: false,
      extraLocaleHistorySearchText: "",
      ...(() => {
        const range = defaultSelectionRange(new Date());
        return {
          extraLocaleHistoryFrom: range.from,
          extraLocaleHistoryTo: range.to,
        };
      })(),
      extraLocaleHistoryExporting: false,
      extraLocaleEditDialog: createExtraLocaleEditDialogState(),
      extraLocaleDeleteRow: null,
      extraLocaleDeleteLoading: false,
      extraLocaleExporting: false,
    });

    // Update state variables directly as well
    this.state.searchText = "";
    this.state.schoolSearchText = "";
    this.state.tableRows = [];
    this.state.schoolSubscriptionFilter = null;
    this.state.schoolSubscriptionKeyword = "";
    this.state.schoolKeywordAllResults = [];
    this.state.subscriptionList = [];
    this.state.subscriptionSearchText = "";
    this.state.subscriptionSortBy = DEFAULT_SUBSCRIPTION_SORT_BY;
    this.state.subscriptionSortDir = DEFAULT_SUBSCRIPTION_SORT_DIR;
    this.state.subscriptionLimit = 100;
    this.state.hasSubscriptionSearched = false;
    this.state.subscriptionLoading = false;
    this.state.subscriptionEditingCell = null;
    this.state.subscriptionUpdatingFields = {};
    this.state.subscriptionNavigationDirection = null;
    this.state.subscriptionLocalesDialog = createSubscriptionLocalesDialogState();
    this.state.subscriptionPrivilegeDialog = {
      open: false,
      subscription: null,
      rows: [],
      loading: false,
    };
    this.state.subscriptionCreateDialog = createSubscriptionCreateDialogState();
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.state.schoolSubscriptionDialog = {
      open: false,
      school: null,
      rows: [],
      loading: false,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: 0,
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.state.schoolSearchResults = [];
    this.state.hasSchoolSearched = false;
    this.state.schoolLoading = false;
    this.state.schoolExporting = false;
    this.state.schoolNavigationDirection = null;
    this.state.newUser = {
      username: "",
      displayName: "",
      email: "",
      password0: "",
      accessRight: 0,
      schoolID: "",
    };
    this.state.importedSchoolInfo = {
      id: "",
      name: "",
      name_eng: "",
      isImported: false,
    };
    this.state.newSchool = {
      name: "",
      name_eng: "",
      key: "",
      locale: "",
      school_type: 0,
      school_level: 0,
    };
    this.state.schoolFilters = {
      school_type: "",
      school_level: "",
      order_by: DEFAULT_SCHOOL_SORT_BY,
      order: DEFAULT_SCHOOL_SORT_DIR,
      limit: 100,
    };
    this.state.passwordChangeDialog = {
      open: false,
      targetUserId: 0,
      targetUsername: "",
      adminPassword: "",
      newPassword: "",
      confirmPassword: "",
      isLoading: false,
    };
    this.state.subscriptionDeleteDialog = {
      open: false,
      subscription: null,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.state.editUserDialog = {
      open: false,
      targetUserId: 0,
      targetUsername: "",
      displayName: "",
      email: "",
      role: 0,
      adminPassword: "",
      isLoading: false,
    };
    this.state.editSchoolDialog = {
      open: false,
      targetSchoolId: 0,
      name: "",
      nameEng: "",
      key: "",
      locale: "",
      schoolType: 0,
      schoolLevel: 0,
      adminPassword: "",
      isLoading: false,
    };
    this.state.batchCreateDialog = this.getInitialBatchCreateDialogState();
    this.state.edcityScrnRows = [];
    this.state.edcityScrnLoading = false;
    this.state.hasEdcityScrnSearched = false;
    this.state.edcityScrnSearchText = "";
    this.state.edcityScrnMappedFilter = "ALL";
    this.state.edcityScrnSortBy = DEFAULT_EDCITY_SCRN_SORT_BY;
    this.state.edcityScrnSortDir = DEFAULT_EDCITY_SCRN_SORT_DIR;
    this.state.edcityScrnCreateDialog = createEdcityScrnCreateDialogState();
    this.state.edcityScrnConnectDialog = createEdcityScrnConnectDialogState();
    this.state.edcityScrnDeleteRow = null;
    this.state.edcityScrnDeleteLoading = false;
    this.state.edcityImportDialog = createEdcityImportDialogState();
    this.state.subscriptionBinding = createSubscriptionBindingState();
    this.state.extraLocaleView = "catalog";
    this.state.extraLocaleRows = [];
    this.state.extraLocaleLoading = false;
    this.state.hasExtraLocaleLoaded = false;
    this.state.extraLocaleTogglingIds = {};
    this.state.extraLocaleHistoryRows = [];
    this.state.extraLocaleHistoryLoading = false;
    this.state.hasExtraLocaleHistoryLoaded = false;
    this.state.extraLocaleHistorySearchText = "";
    const resetRange = defaultSelectionRange(new Date());
    this.state.extraLocaleHistoryFrom = resetRange.from;
    this.state.extraLocaleHistoryTo = resetRange.to;
    this.state.extraLocaleHistoryExporting = false;
    this.state.extraLocaleEditDialog = createExtraLocaleEditDialogState();
    this.state.extraLocaleDeleteRow = null;
    this.state.extraLocaleDeleteLoading = false;
    this.state.extraLocaleExporting = false;

    this.close();
  }

  handleChange = name => event => {
    let user = this.state.newUser;
    user[name] = event.target.value;
    this.setState({ newUser: user });
    this.state.newUser = user;
    
    // If user manually edits schoolID, clear imported school info
    if (name === 'schoolID' && this.state.importedSchoolInfo.isImported) {
      this.setState({
        importedSchoolInfo: {
          id: "",
          name: "",
          name_eng: "",
          isImported: false,
        }
      });
      this.state.importedSchoolInfo = {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      };
    }
  }

  handleSchoolChange = name => event => {
    let school = this.state.newSchool;
    school[name] = event.target.value;
    this.setState({ newSchool: school });
    this.state.newSchool = school;
  }

  createUserClicked() {
    let user = this.state.newUser;
    const S = Localization.strings;
    const schoolAdmin = this.isSchoolAdminOnly();

    // Validation with proper error messages
    if (!user.username || user.username.trim() === "") {
      this.props.root.addNotification(S.ADMIN_ERR_USERNAME_REQUIRED || '請輸入用戶名稱', false);
      return;
    }

    if (!user.password0 || user.password0.trim() === "") {
      this.props.root.addNotification(S.ADMIN_ERR_PASSWORD_REQUIRED || '請輸入密碼', false);
      return;
    }

    if (user.password0.length < 6) {
      this.props.root.addNotification(S.ADMIN_ERR_PASSWORD_TOO_SHORT || '密碼長度至少需要6個字符', false);
      return;
    }

    // A school admin has no school to type: the server stamps the token's.
    if (!schoolAdmin && (!user.schoolID || user.schoolID.trim() === "")) {
      this.props.root.addNotification(S.ADMIN_ERR_SCHOOL_ID_REQUIRED || '請輸入學校編號', false);
      return;
    }

    let requestObj = {
      username: user.username.trim(),
      display_name: user.displayName.trim(),
      email: user.email.trim(),
      role: user.accessRight,
      new_password: user.password0,
    }
    if (!schoolAdmin) {
      // Only a platform admin names a school; for a school admin the route
      // uses the token's school and refuses any other (403).
      requestObj.school_id = user.schoolID.trim();
    }
    const submitCreateUser = () => {
      this.props.root.refs.API.request("user_admin_create_user", requestObj, this.createUserOK, this.createUserFailed);
    };

    if (Number(user.accessRight) >= 3) {
      const title = Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator';
      const warning = Localization.strings.PLATFORM_ADMIN_CREATE_WARNING
        || 'This account can manage data across all schools. Create it only for authorized platform staff.';
      const confirmDialog = this.props.root?.refs?.confirmDialog;
      if (confirmDialog) {
        confirmDialog.show(
          { title, content: warning },
          submitCreateUser,
          () => {}
        );
      } else if (window.confirm(warning)) {
        submitCreateUser();
      }
      return;
    }

    submitCreateUser();
  }

  createUserOK(res) {
    console.log("create user ok");
    this.props.root.addNotification(Localization.strings.CREATED, true);
    
    // Reset imported school info after successful user creation
    this.setState({
      importedSchoolInfo: {
        id: "",
        name: "",
        name_eng: "",
        isImported: false,
      }
    });
    this.state.importedSchoolInfo = {
      id: "",
      name: "",
      name_eng: "",
      isImported: false,
    };
  }

  createUserFailed(err) {
    console.log("create user failed", err);
    const S = Localization.strings;

    let errorMessage = S.ADMIN_CREATE_USER_FAILED || '建立用戶失敗';

    // Try to extract specific error message from the response
    if (err && err.body) {
      if (err.body.remarks) {
        errorMessage += ": " + err.body.remarks;
      } else if (err.body.result === "-1") {
        errorMessage += ": " + (S.ADMIN_CHECK_INPUT || '請檢查輸入資料是否正確');
      }
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    } else if (err && typeof err === 'string') {
      errorMessage += ": " + err;
    } else {
      errorMessage += ": " + (S.ADMIN_CHECK_NETWORK || '請檢查網路連線或聯絡系統管理員');
    }
    
    this.props.root.addNotification(errorMessage, false);
  }

  createSchoolClicked() {
    let school = this.state.newSchool;
    
    // Validation: Check required fields
    if (
      school.name.trim() === "" ||
      school.name_eng.trim() === "" ||
      school.key.trim() === ""
    ) {
      this.props.root.addNotification("Please fill in all required fields (Name, English Name, and Key)", false);
      return;
    }

    // Validate key format (should be alphanumeric and underscores only)
    const keyPattern = /^[a-zA-Z0-9_]+$/;
    if (!keyPattern.test(school.key)) {
      this.props.root.addNotification("School Key should contain only letters, numbers, and underscores", false);
      return;
    }

    let requestObj = {
      name: school.name.trim(),
      name_eng: school.name_eng.trim(),
      key: school.key.trim(),
      locale: school.locale.trim(),
      school_type: parseInt(school.school_type) || 0,
      school_level: parseInt(school.school_level) || 0,
    };

    this.props.root.refs.API.request("create_school", requestObj, this.createSchoolOK, this.createSchoolFailed);
  }

  createSchoolOK(res) {
    console.log("create school ok");
    this.props.root.addNotification("School created successfully", true);
    
    // Reset form
    this.setState({
      newSchool: {
        name: "",
        name_eng: "",
        key: "",
        locale: "",
        school_type: 0,
        school_level: 0,
      }
    });
    this.state.newSchool = {
      name: "",
      name_eng: "",
      key: "",
      locale: "",
      school_type: 0,
      school_level: 0,
    };
  }

  createSchoolFailed(err) {
    console.log("create school failed " + err);
    this.props.root.addNotification("Failed to create school: " + err, false);
  }

  // Import latest school methods
  handleImportLatestSchool = () => {
    // Call API to get the latest school (limit 1, order by id DESC)
    let requestObj = {
      order_by: "id",
      order: DEFAULT_SORT_ORDER,
      limit: 1
    };

    this.props.root.refs.API.request("get_filtered_school_list", requestObj, this.importLatestSchoolOK, this.importLatestSchoolFailed);
  }

  importLatestSchoolOK = (res) => {
    console.log("import latest school ok", res);
    let schoolList = res.body['result'];
    
    if (Array.isArray(schoolList) && schoolList.length > 0) {
      let latestSchool = schoolList[0];
      
      // Update the schoolID field
      let user = { ...this.state.newUser };
      user.schoolID = latestSchool.id.toString();
      
      // Update imported school info
      let importedInfo = {
        id: latestSchool.id.toString(),
        name: latestSchool.name || "",
        name_eng: latestSchool.name_eng || "",
        isImported: true,
      };
      
      this.setState({ 
        newUser: user,
        importedSchoolInfo: importedInfo 
      });
      this.state.newUser = user;
      this.state.importedSchoolInfo = importedInfo;
      
      this.props.root.addNotification(
        String(Localization.strings.ADMIN_IMPORTED_SCHOOL_ID || '已導入最新學校 ID: {0}').replace('{0}', String(latestSchool.id)),
        true
      );
    } else {
      this.props.root.addNotification(Localization.strings.ADMIN_NO_SCHOOL_FOUND || '沒有找到學校資料', false);
    }
  }

  importLatestSchoolFailed = (err) => {
    console.log("import latest school failed", err);
    this.props.root.addNotification(Localization.strings.ADMIN_IMPORT_SCHOOL_FAILED || '獲取最新學校失敗', false);
  }

  // ===== Batch create user (CSV) =====

  getInitialBatchCreateDialogState() {
    return {
      open: false,
      step: 'upload', // 'upload' | 'confirm' | 'result'
      fileName: "",
      users: [],       // parsed data rows sent to the server
      lines: [],       // original CSV line number of each row (for display)
      reviewRows: [],  // per-row validation results from the dry-run
      resultRows: [],  // per-row results from the real insert
      validCount: 0,
      invalidCount: 0,
      insertedCount: 0,
      adminPassword: "",
      isLoading: false,
      fileInputKey: 0,
    };
  }

  setBatchCreateDialog(patch) {
    const dialog = { ...this.state.batchCreateDialog, ...patch };
    this.setState({ batchCreateDialog: dialog });
    this.state.batchCreateDialog = dialog;
  }

  handleBatchCreateOpen = () => {
    this.setBatchCreateDialog({ ...this.getInitialBatchCreateDialogState(), open: true });
  }

  handleBatchCreateClose = () => {
    if (this.state.batchCreateDialog.isLoading) {
      return;
    }
    this.setBatchCreateDialog(this.getInitialBatchCreateDialogState());
  }

  handleBatchCreateBackToUpload = () => {
    this.setBatchCreateDialog({
      ...this.getInitialBatchCreateDialogState(),
      open: true,
      fileInputKey: this.state.batchCreateDialog.fileInputKey + 1,
    });
  }

  handleBatchTemplateDownload = () => {
    // UTF-8 BOM so Excel opens the file correctly
    const content = "\uFEFF" + "username,password,school_id,display_name,email,role\r\n";
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_create_user_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Excel with some regional settings exports semicolon- or tab-separated files
  sniffCSVDelimiter(text) {
    const firstLine = text.split(/\r\n|\r|\n/, 1)[0] || "";
    let best = ',';
    let bestCount = 0;
    [',', ';', '\t'].forEach((d) => {
      const count = firstLine.split(d).length - 1;
      if (count > bestCount) {
        best = d;
        bestCount = count;
      }
    });
    return best;
  }

  // Minimal CSV parser: quoted fields, escaped quotes ("") and CR/LF/CRLF line ends
  parseCSVText(text, delimiter) {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field);
        field = "";
      } else if (c === '\n' || c === '\r') {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
        if (c === '\r' && text[i + 1] === '\n') {
          i++;
        }
      } else {
        field += c;
      }
    }
    if (field !== "" || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  normalizeBatchHeaderName(name) {
    const key = String(name || "").replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[\s-]+/g, '_');
    const aliases = {
      username: 'username', user_name: 'username', account: 'username',
      password: 'password', pwd: 'password', pass: 'password',
      school_id: 'school_id', schoolid: 'school_id', school: 'school_id', school_key: 'school_id',
      display_name: 'display_name', displayname: 'display_name', display: 'display_name', nickname: 'display_name',
      email: 'email', e_mail: 'email', mail: 'email',
      role: 'role', access_right: 'role', permission: 'role',
    };
    return aliases[key] || null;
  }

  handleBatchFileSelected = (file) => {
    if (!file) {
      return;
    }
    // bump the FileButton key so picking the same file again re-triggers onChange
    this.setBatchCreateDialog({ fileInputKey: this.state.batchCreateDialog.fileInputKey + 1 });
    if (file.size > 2 * 1024 * 1024) {
      this.props.root.addNotification("CSV 檔案過大 (上限 2MB)", false);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.processBatchCSVText(String(reader.result || ""), file.name);
    };
    reader.onerror = () => {
      this.props.root.addNotification("讀取檔案失敗", false);
    };
    reader.readAsText(file, 'utf-8');
  }

  processBatchCSVText = (rawText, fileName) => {
    const text = rawText.replace(/^\uFEFF/, "");
    if (text.trim() === "") {
      this.props.root.addNotification("CSV 檔案是空的", false);
      return;
    }
    const parsed = this.parseCSVText(text, this.sniffCSVDelimiter(text));
    // keep original line numbers, then drop fully empty rows
    const entries = parsed
      .map((cells, i) => ({ cells, line: i + 1 }))
      .filter((e) => e.cells.some((c) => String(c).trim() !== ""));
    if (entries.length < 2) {
      this.props.root.addNotification("CSV 沒有資料行 (第一行必須是欄位標題，第二行開始才是用戶資料)", false);
      return;
    }
    const columnMap = {};
    entries[0].cells.forEach((h, idx) => {
      const canonical = this.normalizeBatchHeaderName(h);
      if (canonical && columnMap[canonical] === undefined) {
        columnMap[canonical] = idx;
      }
    });
    const missing = ['username', 'password', 'school_id'].filter((h) => columnMap[h] === undefined);
    if (missing.length > 0) {
      this.props.root.addNotification("CSV 缺少必要欄位: " + missing.join(", "), false);
      return;
    }
    const dataEntries = entries.slice(1);
    if (dataEntries.length > 500) {
      this.props.root.addNotification("一次最多只能建立 500 名用戶，請分批上傳", false);
      return;
    }
    const pick = (cells, key) => {
      const idx = columnMap[key];
      return (idx === undefined || idx >= cells.length) ? "" : String(cells[idx]).trim();
    };
    const users = [];
    const lines = [];
    dataEntries.forEach((e) => {
      users.push({
        username: pick(e.cells, 'username'),
        password: pick(e.cells, 'password'),
        school_id: pick(e.cells, 'school_id'),
        display_name: pick(e.cells, 'display_name'),
        email: pick(e.cells, 'email'),
        role: pick(e.cells, 'role'),
      });
      lines.push(e.line);
    });

    this.setBatchCreateDialog({ fileName, users, lines, isLoading: true });
    // dry-run: server validates every row (duplicates, existing usernames, school lookup)
    this.props.root.refs.API.request(
      "user_admin_batch_create_users",
      { users: users, validate_only: true },
      this.batchValidateOK,
      this.batchValidateFailed
    );
  }

  batchValidateOK = (res) => {
    const result = res && res.body ? res.body.result : null;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("驗證失敗: 伺服器回應格式錯誤", false);
      this.setBatchCreateDialog({ isLoading: false });
      return;
    }
    const lines = this.state.batchCreateDialog.lines;
    this.setBatchCreateDialog({
      step: 'confirm',
      reviewRows: result.rows.map((r) => ({ ...r, line: lines[r.index] })),
      validCount: result.valid_count || 0,
      invalidCount: result.invalid_count || 0,
      isLoading: false,
    });
  }

  batchValidateFailed = (err) => {
    let msg = "CSV 驗證失敗";
    if (err && err.body && err.body.remarks) {
      msg += ": " + err.body.remarks;
    }
    this.props.root.addNotification(msg, false);
    this.setBatchCreateDialog({ isLoading: false });
  }

  handleBatchAdminPasswordChange = (event) => {
    this.setBatchCreateDialog({ adminPassword: event.target.value });
  }

  handleBatchConfirmSubmit = () => {
    const dialog = this.state.batchCreateDialog;
    if (dialog.validCount <= 0) {
      this.props.root.addNotification("沒有可建立的用戶", false);
      return;
    }
    if (!dialog.adminPassword) {
      this.props.root.addNotification("請輸入管理員密碼以確認", false);
      return;
    }
    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }
    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }
    this.setBatchCreateDialog({ isLoading: true });
    this.props.root.refs.API.request(
      "user_admin_batch_create_users",
      {
        users: dialog.users,
        validate_only: false,
        admin_id: adminId,
        admin_password: dialog.adminPassword,
      },
      this.batchInsertOK,
      this.batchInsertFailed
    );
  }

  batchInsertOK = (res) => {
    const result = res && res.body ? res.body.result : null;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("建立失敗: 伺服器回應格式錯誤", false);
      this.setBatchCreateDialog({ isLoading: false });
      return;
    }
    const lines = this.state.batchCreateDialog.lines;
    const insertedCount = result.inserted_count || 0;
    this.setBatchCreateDialog({
      step: 'result',
      resultRows: result.rows.map((r) => ({ ...r, line: lines[r.index] })),
      insertedCount: insertedCount,
      invalidCount: result.invalid_count || 0,
      isLoading: false,
      adminPassword: "",
    });
    this.props.root.addNotification(`成功建立 ${insertedCount} 名用戶`, insertedCount > 0);
  }

  batchInsertFailed = (err) => {
    let msg = "批量建立用戶失敗";
    if (err && err.body && err.body.remarks) {
      msg += ": " + err.body.remarks;
    }
    this.props.root.addNotification(msg, false);
    this.setBatchCreateDialog({ isLoading: false });
  }

  getBatchReasonText(reason) {
    const map = {
      invalid_row: '資料格式錯誤',
      missing_username: '缺少用戶名稱',
      username_too_long: '用戶名稱過長 (最多 100 字元)',
      reserved_username: '用戶名稱不可以 "Deleted-" 開頭',
      missing_password: '缺少密碼',
      password_too_short: '密碼長度至少需要6個字符',
      missing_school: '缺少學校編號',
      invalid_role: '權限值無效 (只接受 0, 1, 2)',
      invalid_email: '電郵格式錯誤',
      display_name_too_long: '顯示名稱過長 (最多 100 字元)',
      duplicate_in_file: 'CSV 檔案內用戶名稱重複',
      school_not_found: '找不到學校 (可輸入學校編號或 School Key)',
      username_exists: '用戶名稱已存在',
      insert_failed: '寫入資料庫失敗',
    };
    return map[reason] || reason || '';
  }

  createUser() {
    const S = Localization.strings;
    const imported = this.state.importedSchoolInfo;
    const schoolAdmin = this.isSchoolAdminOnly();
    const roleOptions = this.assignableRoleOptions({ includePlatformAdmin: true });
    const optional = (label) => `${label} (${S.OPTIONAL || 'optional'})`;
    return (
      <Stack gap="lg" maw={900}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
          <TextInput
            label={S.USERNAME || 'Username'}
            value={this.state.newUser.username}
            onChange={this.handleChange('username')}
          />
          <TextInput
            label={optional(S.DISPLAY_NAME || 'Display Name')}
            value={this.state.newUser.displayName}
            onChange={this.handleChange('displayName')}
          />
          <TextInput
            label={S.PASSWORD || 'Password'}
            type="password"
            value={this.state.newUser.password0}
            onChange={this.handleChange('password0')}
          />
          <TextInput
            label={optional(S.EMAIL || 'Email')}
            value={this.state.newUser.email}
            onChange={this.handleChange('email')}
          />
          {schoolAdmin ? (
            // A school admin creates accounts in their OWN school only: the
            // school is shown, not typed, and the request carries no school_id
            // (the server stamps the token's and refuses any other).
            <TextInput
              label={S.SCHOOL || 'School'}
              value={this.mySchoolLabel()}
              readOnly
              disabled
            />
          ) : (
          <Box>
            <TextInput
              label={S.ADMIN_SCHOOL_ID || 'School ID'}
              value={this.state.newUser.schoolID}
              onChange={this.handleChange('schoolID')}
            />
            <Button
              variant="light"
              size="xs"
              mt={8}
              leftSection={<IconDownload size={14} />}
              onClick={this.handleImportLatestSchool}
            >
              {S.ADMIN_IMPORT_LATEST_SCHOOL || 'Import latest school ID'}
            </Button>
            {imported.isImported && (
              <Box
                mt={8}
                style={{
                  fontSize: 12,
                  color: '#666',
                  padding: '6px 10px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 6,
                  border: '1px solid #e0e0e0',
                }}
              >
                <strong>{S.ADMIN_SCHOOL_INFO || 'School'}:</strong> {imported.name}
                {imported.name_eng && imported.name_eng !== imported.name && (
                  <span style={{ color: '#999', marginLeft: 8 }}>({imported.name_eng})</span>
                )}
              </Box>
            )}
          </Box>
          )}
          <Select
            label={S.ADMIN_ROLE_LABEL || 'Role'}
            allowDeselect={false}
            value={String(this.state.newUser.accessRight)}
            onChange={(value) => this.handleChange('accessRight')({ target: { value: Number(value) } })}
            data={roleOptions}
          />
        </SimpleGrid>
        {Number(this.state.newUser.accessRight) >= 3 && (
          <Alert
            color="orange"
            variant="light"
            title={Localization.strings.PLATFORM_ADMIN_ROLE || 'CLST Administrator'}
            icon={<IconAlertTriangle size={20} />}
          >
            {Localization.strings.PLATFORM_ADMIN_CREATE_WARNING
              || 'This account can manage data across all schools. Create it only for authorized platform staff.'}
          </Alert>
        )}
        <Group>
          <Button onClick={this.createUserClicked} leftSection={<IconUserPlus size={16} />}>
            {Localization.strings.CREATE}
          </Button>
          {/* Batch CSV creation is cross-school by nature (every row names a
              school) — platform admins only, and the server refuses the route
              for anyone else. */}
          {!schoolAdmin && (
            <Button variant="light" onClick={this.handleBatchCreateOpen} leftSection={<IconUsersPlus size={16} />}>
              {S.ADMIN_BATCH_CREATE_USERS || '批量建立用戶 (CSV)'}
            </Button>
          )}
        </Group>
      </Stack>
    );
  }

  createSchool() {
    const resetForm = () => {
      const blank = { name: "", name_eng: "", key: "", locale: "", school_type: 0, school_level: 0 };
      this.setState({ newSchool: blank });
      this.state.newSchool = blank;
    };
    return (
      <Stack gap="lg" maw={900}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
          <TextInput
            label="學校名稱 (中文)"
            required
            value={this.state.newSchool.name}
            onChange={this.handleSchoolChange('name')}
          />
          <TextInput
            label="學校名稱 (英文)"
            required
            value={this.state.newSchool.name_eng}
            onChange={this.handleSchoolChange('name_eng')}
          />
          <TextInput
            label="School Key"
            required
            placeholder="AB1234"
            description="Unique identifier (letters, numbers, underscores only)"
            value={this.state.newSchool.key}
            onChange={this.handleSchoolChange('key')}
          />
          <TextInput
            label="地區 (Optional)"
            placeholder="e.g.: hk, cn, tw, mo, us"
            value={this.state.newSchool.locale}
            onChange={this.handleSchoolChange('locale')}
          />
          <Select
            label="學校類型 (Optional)"
            allowDeselect={false}
            value={String(this.state.newSchool.school_type)}
            onChange={(value) => this.handleSchoolChange('school_type')({ target: { value: Number(value) } })}
            data={[
              { value: '0', label: 'Unclassified' },
              { value: '1', label: 'Primary School' },
              { value: '2', label: 'Secondary School' },
              { value: '3', label: 'University/Post-secondary College/Government Institution' },
              { value: '4', label: 'Special School' },
              { value: '5', label: 'Management Organization' },
              { value: '6', label: 'Commercial Organization' },
            ]}
          />
          <Select
            label="學校級別 (Optional)"
            allowDeselect={false}
            value={String(this.state.newSchool.school_level)}
            onChange={(value) => this.handleSchoolChange('school_level')({ target: { value: Number(value) } })}
            data={[
              { value: '0', label: 'Not Specified' },
              { value: '1', label: 'Level 1' },
              { value: '2', label: 'Level 2' },
              { value: '3', label: 'Level 3' },
              { value: '4', label: 'Level 4' },
              { value: '5', label: 'Level 5' },
            ]}
          />
        </SimpleGrid>
        <Group justify="center">
          <Button variant="default" onClick={resetForm}>
            {Localization.strings.CLEAR}
          </Button>
          <Button onClick={this.createSchoolClicked} leftSection={<IconSchool size={16} />}>
            {Localization.strings.CREATE}
          </Button>
        </Group>
      </Stack>
    );
  }

  userList() {
    const S = Localization.strings;
    const rows = this.state.tableRows;
    // School admins see ONE school's roster (the server pins the search to
    // it), so the school column would repeat their own name on every row.
    const showSchool = !this.isSchoolAdminOnly();
    const columnCount = showSchool ? 8 : 7;
    return (
      <Stack gap="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            style={{ flex: 1, minWidth: 280 }}
            value={this.state.searchText}
            onChange={this.searchTextChanged}
            onKeyDown={this.handleSearchEnterPressed}
            placeholder={(S.SEARCH || 'Search') + " (" + (S.ADMIN_USER_SEARCH_PLACEHOLDER || 'username, display name, email') + ")..."}
            leftSection={<IconSearch size={16} />}
          />
          <Button onClick={this.handleSearchClicked} leftSection={<IconSearch size={16} />}>
            {S.SEARCH || 'Search'}
          </Button>
          <Tooltip label={S.ADMIN_REFRESH_RESULTS || 'Refresh results'}>
            <ActionIcon variant="light" size="lg" onClick={this.handleSearchClicked}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{S.ADMIN_COL_ID || 'ID'}</Table.Th>
                <Table.Th>{S.USERNAME || 'Username'}</Table.Th>
                <Table.Th>{S.DISPLAY_NAME || 'Display Name'}</Table.Th>
                <Table.Th>{S.EMAIL || 'Email'}</Table.Th>
                <Table.Th>{S.ADMIN_ROLE_LABEL || 'Role'}</Table.Th>
                {showSchool && <Table.Th>{S.SCHOOL_NAME || 'School'}</Table.Th>}
                <Table.Th>{S.ADMIN_COL_LAST_LOGIN || 'Last sign-in'}</Table.Th>
                <Table.Th>{S.ACTION || 'Action'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((user, i) => (
                <Table.Tr key={i}>
                  <Table.Td>{user.id}</Table.Td>
                  <Table.Td>{user.username}</Table.Td>
                  <Table.Td>{user.display_name || '-'}</Table.Td>
                  <Table.Td>{user.email || '-'}</Table.Td>
                  <Table.Td>{this.getRoleText(user.role)}</Table.Td>
                  {showSchool && <Table.Td>{this.formatSchoolName(user.school_name, user.school_name_eng)}</Table.Td>}
                  <Table.Td>{this.formatLastLogin(user.last_login_time)}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label={S.ADMIN_EDIT_USER || 'Edit user'}>
                        <ActionIcon variant="subtle" color="brand" onClick={() => this.handleEditUserOpen(user)}>
                          <IconEdit size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={S.CHANGE_PASSWORD || 'Change password'}>
                        <ActionIcon variant="subtle" color="orange" onClick={() => this.handlePasswordChangeOpen(user.id, user.username)}>
                          <IconLock size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={S.ADMIN_DELETE_USER || 'Delete user'}>
                        <ActionIcon variant="subtle" color="red" onClick={() => this.handleDeleteUserOpen(user.id, user.username)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={columnCount} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                    {showSchool
                      ? (S.ADMIN_USER_SEARCH_HINT || 'Enter a keyword and click Search')
                      : (S.ADMIN_USER_LIST_EMPTY || 'No users')}
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    );
  }

  getRoleText(role) {
    const S = Localization.strings;
    switch(parseInt(role)) {
      case -1: return S.ADMIN_ROLE_STUDENT || '學生';
      case 0: return S.ADMIN_ROLE_0 || '一般用戶';
      case 1: return S.ADMIN_ROLE_1 || '進階用戶';
      case 2: return S.ADMIN_ROLE_2 || '管理員';
      case 3: return S.PLATFORM_ADMIN_ROLE || 'CLST Administrator';
      default: return S.ADMIN_ROLE_UNKNOWN || '未知';
    }
  }

  formatLastLogin(lastLoginTime) {
    if (!lastLoginTime || lastLoginTime === '0000-00-00 00:00:00') {
      return Localization.strings.ADMIN_NEVER_LOGGED_IN || '從未登入';
    }
    
    try {
      // Convert MySQL datetime to readable format in 24-hour format
      const date = new Date(lastLoginTime);
      if (isNaN(date.getTime())) {
        return Localization.strings.ADMIN_INVALID_DATE || '無效日期';
      }
      
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // Use 24-hour format
      });
    } catch (e) {
      return Localization.strings.ADMIN_DATE_FORMAT_ERROR || '格式錯誤';
    }
  }

  formatSchoolName(schoolName, schoolNameEng) {
    // If no school name, return dash
    if (!schoolName) {
      return '-';
    }

    // If no English name or English name is empty, return only Chinese name
    if (!schoolNameEng || schoolNameEng.trim().length === 0) {
      return schoolName;
    }

    // If English name is the same as Chinese name, return only Chinese name
    if (schoolNameEng.trim() === schoolName.trim()) {
      return schoolName;
    }

    // If English name is different, display both with English name in gray
    return (
      <div>
        <div>{schoolName}</div>
        <div style={{ color: '#999', fontSize: '12px', marginTop: '2px' }}>
          {schoolNameEng}
        </div>
      </div>
    );
  }

  renderEditSchoolPage() {
    const dialog = this.state.editSchoolDialog;
    const title = `編輯學校資料 - ${dialog.name || `ID ${dialog.targetSchoolId}`}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.handleEditSchoolClose, dialog.isLoading)}
        <Box maw={900}>
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>目標學校：</strong> ID: {dialog.targetSchoolId}
            </Box>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
              <TextInput
                label="學校名稱 (中文)"
                required
                value={dialog.name}
                onChange={this.handleEditSchoolFieldChange('name')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="學校名稱 (英文)"
                required
                value={dialog.nameEng}
                onChange={this.handleEditSchoolFieldChange('nameEng')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="代碼 (Key)"
                required
                description="只能包含英文字母、數字及底線"
                value={dialog.key}
                onChange={this.handleEditSchoolFieldChange('key')}
                disabled={dialog.isLoading}
              />
              <TextInput
                label="地區"
                placeholder="e.g.: hk, cn, tw, mo, us"
                value={dialog.locale}
                onChange={this.handleEditSchoolFieldChange('locale')}
                disabled={dialog.isLoading}
              />
              <Select
                label="學校類型"
                allowDeselect={false}
                disabled={dialog.isLoading}
                value={String(dialog.schoolType)}
                onChange={(v) => this.handleEditSchoolFieldChange('schoolType')({ target: { value: Number(v) } })}
                data={[
                  { value: '0', label: 'Unclassified' },
                  { value: '1', label: 'Primary School' },
                  { value: '2', label: 'Secondary School' },
                  { value: '3', label: 'University / Post-secondary / Gov.' },
                  { value: '4', label: 'Special School' },
                  { value: '5', label: 'Management Organization' },
                  { value: '6', label: 'Commercial Organization' },
                ]}
              />
              <Select
                label="學校級別"
                allowDeselect={false}
                disabled={dialog.isLoading}
                value={String(dialog.schoolLevel)}
                onChange={(v) => this.handleEditSchoolFieldChange('schoolLevel')({ target: { value: Number(v) } })}
                data={[
                  { value: '0', label: 'Not Specified' },
                  { value: '1', label: 'Level 1' },
                  { value: '2', label: 'Level 2' },
                  { value: '3', label: 'Level 3' },
                  { value: '4', label: 'Level 4' },
                  { value: '5', label: 'Level 5' },
                ]}
              />
            </SimpleGrid>
            <Divider />
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_PASSWORD || '管理員密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_DESC || '輸入您的管理員密碼以確認身份'}
              value={dialog.adminPassword}
              onChange={this.handleEditSchoolFieldChange('adminPassword')}
              disabled={dialog.isLoading}
              maw={440}
            />
            <Group justify="flex-end" mt="sm">
              <Button onClick={this.handleEditSchoolSubmit} loading={dialog.isLoading}>
                儲存變更
              </Button>
            </Group>
          </Stack>
        </Box>
      </Stack>
    );
  }

  renderSchoolSubscriptionPage() {
    const dialog = this.state.schoolSubscriptionDialog;
    const schoolName = dialog.school
      ? (dialog.school.name || dialog.school.name_eng || '')
      : '';
    const title = `${Localization.strings.SCHOOL_SUBSCRIPTION_DIALOG_TITLE || '學校訂閱'}${schoolName ? ` - ${schoolName}` : ''}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.closeSchoolSubscriptionDialog)}
        <Group justify="flex-start" align="flex-end" wrap="nowrap">
          <Box w="45%" miw={260} maw={440}>
            <SubscriptionPicker
              key={dialog.pickerKey}
              root={this.props.root}
              placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_SEARCH_PLACEHOLDER || '搜尋訂閱'}
              disabled={dialog.addLoading || dialog.loading}
              selected={dialog.addSelected}
              onSelect={(sub) => {
                const updated = { ...dialog, addSelected: sub };
                this.setState({ schoolSubscriptionDialog: updated });
                this.state.schoolSubscriptionDialog = updated;
              }}
            />
          </Box>
          <Button
            variant="light"
            onClick={this.openSchoolSubscriptionCreatePage}
            disabled={dialog.addLoading || dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.SCHOOL_SUBSCRIPTION_CREATE || '創建訂閱'}
          </Button>
        </Group>
        <Group justify="flex-start" align="flex-end">
          <TextInput
            type="date"
            label={Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}
            value={dialog.addDateStart}
            disabled={dialog.addLoading || dialog.loading}
            onChange={(e) => {
              const updated = { ...dialog, addDateStart: e.target.value };
              this.setState({ schoolSubscriptionDialog: updated });
              this.state.schoolSubscriptionDialog = updated;
            }}
            w={180}
          />
          <Select
            label={Localization.strings.SCHOOL_SUBSCRIPTION_DURATION || '期限'}
            value={dialog.addDurationMonths}
            data={[
              { value: '6', label: Localization.strings.SCHOOL_SUBSCRIPTION_HALF_YEAR || '半年' },
              { value: '12', label: Localization.strings.SCHOOL_SUBSCRIPTION_ONE_YEAR || '1 年' },
              { value: '24', label: Localization.strings.SCHOOL_SUBSCRIPTION_TWO_YEARS || '2 年' },
              { value: '36', label: Localization.strings.SCHOOL_SUBSCRIPTION_THREE_YEARS || '3 年' },
            ]}
            allowDeselect={false}
            disabled={dialog.addLoading || dialog.loading}
            onChange={(value) => {
              const updated = {
                ...dialog,
                addDurationMonths: value || DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
              };
              this.setState({ schoolSubscriptionDialog: updated });
              this.state.schoolSubscriptionDialog = updated;
            }}
            w={180}
          />
          <Button
            onClick={this.handleSchoolSubscriptionAdd}
            loading={dialog.addLoading}
            disabled={dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.SCHOOL_SUBSCRIPTION_ADD || '為學校加入訂閱'}
          </Button>
        </Group>
        {dialog.loading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={900}>
            <Table striped verticalSpacing="sm" style={{ tableLayout: 'fixed', minWidth: 900 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 56 }}>ID</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_ZH}</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN}</Table.Th>
                  <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}</Table.Th>
                  <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}</Table.Th>
                  <Table.Th style={{ width: 92 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_STATUS || '狀態'}</Table.Th>
                  <Table.Th>{Localization.strings.REMARK}</Table.Th>
                  <Table.Th style={{ width: 76 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dialog.rows.map((row, idx) => {
                  const isEditing = dialog.editingRow?.school_subscription_id === row.school_subscription_id;
                  const editingRow = dialog.editingRow;
                  const isUpdateLoading = dialog.updateLoading;
                  const isDeleteLoading = dialog.deleteLoading;
                  const isBusy = isUpdateLoading || isDeleteLoading || dialog.loading;
                  const canStartEditing = !editingRow && !isBusy;
                  const displayedDates = isEditing ? editingRow : row;
                  const isActive = isSubscriptionActive(displayedDates.date_start, displayedDates.date_expire);
                  const editableCellStyle = {
                    width: '100%',
                    minHeight: 30,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: canStartEditing ? 'text' : 'default',
                  };
                  return (
                    <Table.Tr
                      key={row.school_subscription_id || idx}
                      onBlur={isEditing ? this.handleSchoolSubscriptionEditBlur : undefined}
                    >
                      <Table.Td>{row.subscription_id}</Table.Td>
                      <Table.Td>{row.subscription_name || '-'}</Table.Td>
                      <Table.Td>{row.subscription_name_en || '-'}</Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            type="date"
                            size="xs"
                            value={editingRow.date_start}
                            autoFocus={dialog.editingField === 'date_start'}
                            disabled={isUpdateLoading}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, date_start: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_start')}
                          >
                            {row.date_start || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            type="date"
                            size="xs"
                            value={editingRow.date_expire}
                            autoFocus={dialog.editingField === 'date_expire'}
                            disabled={isUpdateLoading}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, date_expire: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_expire')}
                          >
                            {row.date_expire || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={isActive ? 'green' : 'red'} variant="light" radius="sm">
                          {isActive
                            ? (Localization.strings.SCHOOL_SUBSCRIPTION_ACTIVE || 'Active')
                            : (Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRED || 'Expired')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {isEditing ? (
                          <TextInput
                            size="xs"
                            value={editingRow.remark}
                            autoFocus={dialog.editingField === 'remark'}
                            disabled={isUpdateLoading}
                            placeholder={Localization.strings.REMARK || '備註'}
                            onChange={(e) => {
                              const updated = { ...dialog, editingRow: { ...editingRow, remark: e.target.value } };
                              this.setState({ schoolSubscriptionDialog: updated });
                              this.state.schoolSubscriptionDialog = updated;
                            }}
                          />
                        ) : (
                          <Box
                            component="button"
                            type="button"
                            disabled={!canStartEditing}
                            style={editableCellStyle}
                            onClick={() => this.handleSchoolSubscriptionEditStart(row, 'remark')}
                          >
                            {row.school_subscription_remark || '-'}
                          </Box>
                        )}
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label={Localization.strings.SAVE || '儲存'}>
                              <ActionIcon color="green" variant="subtle" onClick={this.handleSchoolSubscriptionUpdate} loading={isUpdateLoading}>
                                <IconCheck size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={Localization.strings.CANCEL || '取消'}>
                              <ActionIcon color="red" variant="subtle" onClick={this.handleSchoolSubscriptionEditCancel} disabled={isUpdateLoading}>
                                <IconX size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        ) : (
                          <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}>
                            <ActionIcon variant="subtle" color="red" onClick={() => this.handleSchoolSubscriptionDelete(row)} disabled={isBusy} loading={isDeleteLoading}>
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {dialog.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 12 }}>
                      {Localization.strings.SCHOOL_SUBSCRIPTION_NONE || '暫無訂閱'}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  schoolList() {
    const direction = this.state.schoolNavigationDirection;
    const animationClassName = direction
      ? `admin-page-transition admin-page-transition--${direction}`
      : 'admin-page-transition';

    if (this.state.subscriptionCreateDialog.open && this.state.subscriptionCreateDialog.returnToSchoolSubscription) {
      return (
        <Box key="subscription-create" className={animationClassName}>
          {this.renderSubscriptionCreatePage()}
        </Box>
      );
    }
    if (this.state.editSchoolDialog.open) {
      return (
        <Box key="school-edit" className={animationClassName}>
          {this.renderEditSchoolPage()}
        </Box>
      );
    }
    if (this.state.schoolSubscriptionDialog.open) {
      return (
        <Box key="school-subscriptions" className={animationClassName}>
          {this.renderSchoolSubscriptionPage()}
        </Box>
      );
    }

    const results = this.state.schoolSearchResults;
    // school_type filter is mixed-type: "" means "all", otherwise a number.
    // Mantine Select is string-only, so map "" <-> 'ALL' sentinel at the boundary.
    const typeFilterValue = this.state.schoolFilters.school_type === "" ? 'ALL' : String(this.state.schoolFilters.school_type);
    // In subscription keyword mode the rows are a union across several
    // subscriptions, so an extra column names which one(s) each school matched —
    // without it a merged Pri + Sec list is indistinguishable from either half.
    const keywordActive = this.isSchoolSubscriptionKeywordActive();
    const elafpActive = this.isElafpFilterActive();
    // Either way of filtering by subscription — the keyword union or one picked
    // row — goes through get_schools_by_subscription, so both have a term.
    const subscriptionFiltered = keywordActive
      || !!(this.state.schoolSubscriptionFilter && this.state.schoolSubscriptionFilter.id);
    const subscriptionPicked = !!(this.state.schoolSubscriptionFilter
      && this.state.schoolSubscriptionFilter.id);
    // Printed above the table. In keyword mode the union is usually longer than
    // the page on screen, so the count has to name both — see the module doc for
    // why one rows.length would be wrong on two of the three searches.
    const resultCount = describeSchoolResultCount({
      keywordActive,
      subscriptionPicked,
      rows: this.state.schoolSearchResults,
      keywordAllRows: this.state.schoolKeywordAllResults,
      limit: this.state.schoolFilters.limit,
    });
    // id, action, name, name_eng, key, locale, type, level, last subscription
    // update, ePack quota — plus the matched-subscription column while the
    // keyword filter is on.
    const columnCount = keywordActive ? 11 : 10;
    return (
      <Box
        key="school-list"
        className={animationClassName}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            style={{ flex: 1, minWidth: 220 }}
            value={this.state.schoolSearchText}
            onChange={this.schoolSearchTextChanged}
            onKeyDown={this.handleSchoolSearchEnterPressed}
            placeholder="搜尋關鍵詞(學校名稱、英文名稱或代碼)..."
            leftSection={<IconSearch size={16} />}
          />
          <Select
            w={170}
            allowDeselect={false}
            value={typeFilterValue}
            onChange={(v) => this.handleSchoolFilterChange('school_type')({ target: { value: v === 'ALL' ? "" : Number(v) } })}
            // Same catalogue the CSV export writes — single-sourced in
            // tools/schoolListCsv.js so the file cannot label a type the
            // filter above it calls something else.
            data={[
              { value: 'ALL', label: '所有學校類型' },
              ...SCHOOL_TYPE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label })),
            ]}
          />
          <Box w={230}>
            <SubscriptionPicker
              key={this.state.schoolSubscriptionPickerKey}
              root={this.props.root}
              placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_FILTER || '搜尋訂閱以篩選學校'}
              onSelect={(sub) => {
                const prevId = this.state.schoolSubscriptionFilter && this.state.schoolSubscriptionFilter.id;
                const nextId = sub && sub.id;
                // Picking one subscription takes over from the keyword filter,
                // taking the keyword-only sort column down with it.
                const clearKeyword = sub != null && this.isSchoolSubscriptionKeywordActive();
                const filters = (clearKeyword && isKeywordOnlySortColumn(this.state.schoolFilters.order_by))
                  ? { ...this.state.schoolFilters, order_by: DEFAULT_SCHOOL_SORT_BY, order: DEFAULT_SCHOOL_SORT_DIR }
                  : this.state.schoolFilters;
                this.setState({
                  schoolSubscriptionFilter: sub,
                  schoolSubscriptionKeyword: clearKeyword ? "" : this.state.schoolSubscriptionKeyword,
                  schoolFilters: filters,
                });
                this.state.schoolSubscriptionFilter = sub;
                if (clearKeyword) {
                  this.state.schoolSubscriptionKeyword = "";
                }
                this.state.schoolFilters = filters;
                if (prevId !== nextId || clearKeyword) {
                  this.handleSchoolSearchClicked();
                }
              }}
            />
          </Box>
          <Tooltip label={Localization.strings.SCHOOL_ELAFP_FILTER_TOOLTIP || '只顯示持有 eLAFP 訂閱的學校'}>
            <Button
              variant={elafpActive ? 'filled' : 'default'}
              leftSection={<IconLicense size={16} />}
              aria-pressed={elafpActive}
              onClick={this.handleElafpFilterToggle}
            >
              eLAFP
            </Button>
          </Tooltip>
          {/* Only rendered under a subscription filter, because that is the only
              search whose rows have a term at all — on the plain school list it
              would be a control that does nothing. */}
          {subscriptionFiltered && (
            <Tooltip label={Localization.strings.SCHOOL_INCLUDE_EXPIRED_TOOLTIP
              || '連同已過期的訂閱一併顯示'}>
              <Checkbox
                checked={!!this.state.schoolFilters.include_expired}
                onChange={(e) => this.handleSchoolFilterChange('include_expired')(
                  { target: { value: e.currentTarget.checked } },
                )}
                label={Localization.strings.SCHOOL_INCLUDE_EXPIRED_LABEL || '包括已過期'}
              />
            </Tooltip>
          )}
          {/* Sorting lives on the column headers, as on the Subscriptions and
              EdCity SCRN tables — every column is sortable there, so a separate
              sort dropdown + direction toggle would only duplicate a subset. */}
          <Select
            w={120}
            allowDeselect={false}
            value={String(this.state.schoolFilters.limit)}
            onChange={(v) => this.handleSchoolFilterChange('limit')({ target: { value: Number(v) } })}
            data={[
              { value: '50', label: '50 個結果' },
              { value: '100', label: '100 個結果' },
              { value: '200', label: '200 個結果' },
              { value: '500', label: '500 個結果' },
            ]}
          />
          <Tooltip label="重新整理搜尋結果">
            <ActionIcon variant="light" size="lg" onClick={this.handleSchoolRefreshClicked}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button onClick={this.handleSchoolSearchClicked} leftSection={<IconSearch size={16} />}>
            搜尋
          </Button>
          {/* Exports the whole filtered set, which under the keyword filter and
              on the plain list is MORE than the page on screen — see
              handleSchoolExportClicked.

              NEVER DISABLED, and that is the fix rather than an oversight. It
              used to require a finished search (`schoolLoading ||
              !hasSchoolSearched`), which from the outside read as "the export
              only works under the eLAFP filter" — toggling eLAFP runs a search,
              so that was the click that happened to unlock it, and anything
              leaving `schoolLoading` stuck true left the button grey for good
              with no way back. The gate also bought nothing on the search it
              was written for: the plain list re-requests at its own row
              ceiling, so the export never depended on the page on screen. The
              two subscription modes DO export what the client is holding, and
              they now say a search is still running rather than reporting it as
              an empty result — see handleSchoolExportClicked. `loading` still
              blocks a second click while a file is being built. */}
          <Tooltip label={Localization.strings.SCHOOL_EXPORT_CSV_TOOLTIP
            || '匯出目前篩選出的所有學校為 CSV'}>
            <Button
              onClick={this.handleSchoolExportClicked}
              leftSection={<IconDownload size={16} />}
              variant="light"
              loading={this.state.schoolExporting}
            >
              {Localization.strings.SCHOOL_EXPORT_CSV || '匯出 CSV'}
            </Button>
          </Tooltip>
        </Group>

        {/* The count sits above the table rather than in a toast: the eLAFP
            button's whole purpose is "how many schools are on this programme",
            and a notification is gone by the time the operator has read the
            list. Hidden until a search has actually run, so an empty table on
            first open does not claim "0 schools" before it has looked. */}
        {!this.state.schoolLoading && this.state.hasSchoolSearched && (
          <Text size="sm" c="dimmed" mb="xs">
            {resultCount.truncated
              ? (Localization.strings.SCHOOL_RESULT_COUNT_TRUNCATED
                  || '顯示 {0} 間，共 {1} 間學校')
                .replace('{0}', resultCount.shown).replace('{1}', resultCount.total)
              : resultCount.capped
                ? (Localization.strings.SCHOOL_RESULT_COUNT_CAPPED
                    || '顯示前 {0} 間學校，可能未完')
                  .replace('{0}', resultCount.shown)
                : (Localization.strings.SCHOOL_RESULT_COUNT || '共 {0} 間學校')
                  .replace('{0}', resultCount.total)}
          </Text>
        )}

        {this.state.schoolLoading ? (
          <TableLoadingCard icon={<IconSchool {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={keywordActive ? 1610 : 1310}>
            <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  {this.renderSchoolSortableTh('id', '編號', { width: 90 })}
                  <Table.Th style={{ width: 110 }}>操作</Table.Th>
                  {this.renderSchoolSortableTh('name', '學校名稱')}
                  {this.renderSchoolSortableTh('name_eng', '英文名稱')}
                  {this.renderSchoolSortableTh('key', '代碼', { width: 120 })}
                  {this.renderSchoolSortableTh('locale', '地區', { width: 110 })}
                  {this.renderSchoolSortableTh('school_type', '類型', { width: 150 })}
                  {this.renderSchoolSortableTh('school_level', '級別', { width: 120 })}
                  {this.renderSchoolSortableTh(
                    'last_subscription_update',
                    Localization.strings.SCHOOL_LAST_SUBSCRIPTION_UPDATE || '最近訂閱更新',
                    { width: 170 },
                  )}
                  {this.renderSchoolSortableTh(
                    'epack_quota',
                    Localization.strings.SCHOOL_EPACK_QUOTA || 'ePack 配額',
                    { width: 130 },
                  )}
                  {keywordActive && this.renderSchoolSortableTh(
                    'matched_subscriptions',
                    Localization.strings.SCHOOL_MATCHED_SUBSCRIPTION || '符合的訂閱',
                    { minWidth: 280 },
                  )}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {results.map((school, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{school.id}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="編輯學校">
                          <ActionIcon variant="subtle" color="brand" onClick={() => this.handleEditSchoolOpen(school)}>
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_ACTION || '訂閱'}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => this.openSchoolSubscriptionDialog(school)}>
                            <IconReceipt size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td>{school.name || '-'}</Table.Td>
                    <Table.Td>{school.name_eng || '-'}</Table.Td>
                    <Table.Td>{school.key || '-'}</Table.Td>
                    <Table.Td>{school.locale || '-'}</Table.Td>
                    <Table.Td>{this.getSchoolTypeText(school.school_type)}</Table.Td>
                    <Table.Td>{this.getSchoolLevelText(school.school_level)}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      {this.formatDateTime(school.last_subscription_update)}
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      {this.renderEpackQuota(school)}
                    </Table.Td>
                    {keywordActive && (
                      <Table.Td>
                        <Group gap={4} wrap="wrap" align="flex-start">
                          {(school.matched_subscriptions || []).map((label, k) => (
                            <Badge
                              key={k}
                              variant="light"
                              size="sm"
                              style={{
                                height: 'auto',
                                maxWidth: '100%',
                                overflow: 'visible',
                                lineHeight: 1.4,
                                paddingTop: 4,
                                paddingBottom: 4,
                              }}
                              styles={{
                                label: {
                                  whiteSpace: 'normal',
                                  overflow: 'visible',
                                  textOverflow: 'clip',
                                  textAlign: 'left',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              {label}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
                {results.length === 0 && this.state.hasSchoolSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={columnCount} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      沒有找到符合條件的學校
                    </Table.Td>
                  </Table.Tr>
                )}
                {results.length === 0 && !this.state.hasSchoolSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={columnCount} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      請點擊搜尋按鈕以載入學校列表
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        </Stack>
      </Box>
    );
  }

  // A school-name cell on the EdCity mapping list: clickable when the row is
  // connected to an existing school — jumps to the School List tab.
  renderEdcityScrnSchoolCell(row, value) {
    if (!value) {
      return '-';
    }
    return (
      <Tooltip label={Localization.strings.EDCITY_SCRN_VIEW_IN_SCHOOL_LIST || '在學校列表中檢視'}>
        <Anchor component="button" type="button" size="sm" onClick={() => this.gotoSchoolListForEdcityRow(row)} style={{ textAlign: 'left' }}>
          {value}
        </Anchor>
      </Tooltip>
    );
  }

  renderSortableTh(column, label, extraStyle, sortBy, sortDir, onSort) {
    const active = sortBy === column;
    const dir = String(sortDir).toUpperCase();
    const SortIcon = !active ? IconSelector : (dir === 'ASC' ? IconChevronUp : IconChevronDown);
    const ariaSort = !active ? 'none' : (dir === 'ASC' ? 'ascending' : 'descending');
    return (
      <Table.Th style={extraStyle} aria-sort={ariaSort}>
        <UnstyledButton
          onClick={() => onSort(column)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontWeight: 700,
            fontSize: 'inherit',
            color: 'inherit',
            width: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{label}</span>
          <SortIcon size={14} style={{ opacity: active ? 1 : 0.35, flexShrink: 0 }} />
        </UnstyledButton>
      </Table.Th>
    );
  }

  renderEdcityScrnSortableTh(column, label, extraStyle) {
    return this.renderSortableTh(
      column,
      label,
      extraStyle,
      this.state.edcityScrnSortBy,
      this.state.edcityScrnSortDir,
      this.handleEdcityScrnSort,
    );
  }

  renderSubscriptionSortableTh(column, label, extraStyle) {
    return this.renderSortableTh(
      column,
      label,
      extraStyle,
      this.state.subscriptionSortBy,
      this.state.subscriptionSortDir,
      this.handleSubscriptionSort,
    );
  }

  renderSchoolSortableTh(column, label, extraStyle) {
    return this.renderSortableTh(
      column,
      label,
      extraStyle,
      this.state.schoolFilters.order_by,
      this.state.schoolFilters.order,
      this.handleSchoolSort,
    );
  }

  // EdCity Login > EdCity School ID Mapping — list of school_edcity_scrn rows
  // LEFT JOINed to school, so a bare SCRN ("not yet mapped") still shows.
  edcityScrnTab() {
    const rows = sortEdcityScrnRows(
      this.state.edcityScrnRows,
      this.state.edcityScrnSortBy,
      this.state.edcityScrnSortDir,
    );
    return (
      <Box
        key="edcity-scrn-list"
        className="admin-page-transition"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Group gap="sm" wrap="wrap" align="flex-end">
            <TextInput
              style={{ flex: 1, minWidth: 220 }}
              value={this.state.edcityScrnSearchText}
              onChange={this.edcityScrnSearchTextChanged}
              onKeyDown={this.handleEdcityScrnSearchEnterPressed}
              placeholder={Localization.strings.EDCITY_SCRN_SEARCH_PLACEHOLDER || '搜尋 SCRN 或學校名稱...'}
              leftSection={<IconSearch size={16} />}
            />
            <Select
              w={170}
              allowDeselect={false}
              value={this.state.edcityScrnMappedFilter}
              onChange={this.handleEdcityScrnMappedFilterChanged}
              data={[
                { value: 'ALL', label: Localization.strings.EDCITY_SCRN_FILTER_ALL || '所有記錄' },
                { value: '1', label: Localization.strings.EDCITY_SCRN_FILTER_MAPPED || '只顯示已對接' },
                { value: '0', label: Localization.strings.EDCITY_SCRN_FILTER_UNMAPPED || '只顯示未對接' },
              ]}
            />
            <Tooltip label="重新整理搜尋結果">
              <ActionIcon variant="light" size="lg" onClick={this.handleEdcityScrnSearch}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button onClick={this.handleEdcityScrnSearch} leftSection={<IconSearch size={16} />}>
              搜尋
            </Button>
            <Button onClick={this.openEdcityScrnCreateDialog} leftSection={<IconPlus size={16} />} variant="filled" color="teal">
              {Localization.strings.EDCITY_SCRN_CREATE || '新增對接記錄'}
            </Button>
            <Button onClick={this.handleEdcityImportOpen} leftSection={<IconFileImport size={16} />} variant="light">
              {Localization.strings.EDCITY_IMPORT_BUTTON || '匯入對接 Excel'}
            </Button>
            <Tooltip label={Localization.strings.EDCITY_IMPORT_DOWNLOAD_TEMPLATE_TOOLTIP || '下載「匯入對接」的空白 Excel 範本 (SCRN 欄已設為文字格式)'}>
              <Button onClick={this.handleEdcityImportTemplateDownload} leftSection={<IconDownload size={16} />} variant="subtle">
                {Localization.strings.EDCITY_IMPORT_DOWNLOAD_TEMPLATE || '下載 Excel 範本'}
              </Button>
            </Tooltip>
          </Group>

          {this.state.edcityScrnLoading ? (
            <TableLoadingCard icon={<IconPlugConnected {...LOADING_CARD_ICON_PROPS} />} />
          ) : (
            <Table.ScrollContainer minWidth={1180}>
              <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    {this.renderEdcityScrnSortableTh('school_code', Localization.strings.EDCITY_SCRN_CODE || 'SCRN')}
                    {this.renderEdcityScrnSortableTh('note', Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱')}
                    {this.renderEdcityScrnSortableTh('school_id', Localization.strings.EDCITY_SCRN_OUR_SCHOOL_ID || '學校 ID', { width: 110 })}
                    {this.renderEdcityScrnSortableTh('school_name', Localization.strings.EDCITY_SCRN_OUR_SCHOOL_NAME || '學校名稱')}
                    {this.renderEdcityScrnSortableTh('school_name_eng', Localization.strings.EDCITY_SCRN_OUR_SCHOOL_NAME_ENG || '學校英文名稱')}
                    {this.renderEdcityScrnSortableTh('created_at', Localization.strings.EDCITY_SCRN_CREATED || Localization.strings.CREATED_DATE || '建立日期', { width: 160, whiteSpace: 'nowrap' })}
                    <Table.Th style={{ width: 100 }}>操作</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row, i) => {
                    const isMapped = row.school_id != null && parseInt(row.school_id) > 0;
                    // school_id set but the school row is gone — flag it in red.
                    const isDangling = isMapped && row.matched_school_id == null;
                    return (
                      <Table.Tr key={row.school_code || i}>
                        <Table.Td style={{ fontFamily: 'monospace' }}>{row.school_code}</Table.Td>
                        <Table.Td>{row.note || '-'}</Table.Td>
                        <Table.Td>
                          {isMapped ? (
                            <Text span c={isDangling ? 'red' : undefined} fw={isDangling ? 600 : undefined}>
                              {row.school_id}
                            </Text>
                          ) : (
                            <Badge color="orange" variant="light">
                              {Localization.strings.EDCITY_SCRN_NOT_MAPPED || '未對接'}
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>{this.renderEdcityScrnSchoolCell(row, row.school_name)}</Table.Td>
                        <Table.Td>{this.renderEdcityScrnSchoolCell(row, row.school_name_eng)}</Table.Td>
                        <Table.Td style={{ whiteSpace: 'nowrap' }}>{this.formatDateTime(row.created_at)}</Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label={Localization.strings.EDCITY_SCRN_CONNECT || '對接學校'}>
                              <ActionIcon variant="subtle" color="brand" onClick={() => this.openEdcityScrnConnectDialog(row)}>
                                <IconLink size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={Localization.strings.EDCITY_SCRN_REMOVE || '移除'}>
                              <ActionIcon variant="subtle" color="red" onClick={() => this.openEdcityScrnDeleteDialog(row)}>
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {rows.length === 0 && this.state.hasEdcityScrnSearched && (
                    <Table.Tr>
                      <Table.Td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                        {Localization.strings.EDCITY_SCRN_NO_RESULT || '沒有找到對接記錄'}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Box>
    );
  }

  // ------------------------------------------------------------------------
  // EdCity Login > Subscription Binding (訂閱綁定) — which TEMPLATE each plan
  // tier (體驗版 / 基本版 / 優越版) is cloned from by the mapping Excel import,
  // and how the created subscriptions are named. Stored server-side in
  // system_config; any admin can read it, saving is platform-admin only.
  // ------------------------------------------------------------------------

  setSubscriptionBinding(patch) {
    const next = { ...this.state.subscriptionBinding, ...patch };
    this.setState({ subscriptionBinding: next });
    this.state.subscriptionBinding = next;
  }

  loadSubscriptionBinding = () => {
    if (!this.props.root?.refs?.API) {
      return;
    }
    this.setSubscriptionBinding({ loading: true, templatesLoading: true });
    this.props.root.refs.API.request(
      "admin_subscription_binding_get",
      {},
      (res) => {
        const payload = res?.body?.result || {};
        const rows = Array.isArray(payload.bindings) ? payload.bindings : [];
        const edits = {};
        rows.forEach((row) => {
          edits[row.plan] = {
            subscription_id: row.subscription_id ? String(row.subscription_id) : "",
            name_pattern: row.name_pattern || "",
            name_pattern_en: row.name_pattern_en || "",
          };
        });
        const dateStart = payload.date_start || "";
        const dateExpire = payload.date_expire || "";
        this.setSubscriptionBinding({
          rows, edits, dateStart, dateExpire,
          editDateStart: dateStart, editDateExpire: dateExpire,
          loading: false, loaded: true,
        });
      },
      (err) => {
        this.setSubscriptionBinding({ loading: false, loaded: true });
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.SUBSCRIPTION_BINDING_LOAD_FAILED || '載入訂閱綁定失敗',
          false,
        );
      }
    );
    // Template options come from the same query the create-subscription
    // dialog's "copy from template" picker uses.
    this.props.root.refs.API.request(
      "subscription_get",
      { is_template: 1, limit: 500, sort_by: "name", sort_dir: "ASC" },
      (res) => {
        const templates = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setSubscriptionBinding({ templates, templatesLoading: false });
      },
      () => {
        this.setSubscriptionBinding({ templates: [], templatesLoading: false });
      }
    );
  }

  handleSubscriptionBindingEdit = (plan, field, value) => {
    const edits = { ...this.state.subscriptionBinding.edits };
    edits[plan] = { ...(edits[plan] || {}), [field]: value == null ? "" : value };
    this.setSubscriptionBinding({ edits });
  }

  subscriptionBindingIsDirty() {
    const { rows, edits, dateStart, dateExpire, editDateStart, editDateExpire } = this.state.subscriptionBinding;
    if ((dateStart || "") !== (editDateStart || "") || (dateExpire || "") !== (editDateExpire || "")) {
      return true;
    }
    return (rows || []).some((row) => {
      const e = edits[row.plan];
      if (!e) return false;
      return String(row.subscription_id || "") !== String(e.subscription_id || "")
        || (row.name_pattern || "") !== (e.name_pattern || "")
        || (row.name_pattern_en || "") !== (e.name_pattern_en || "");
    });
  }

  handleSubscriptionBindingSave = () => {
    const { rows, edits, saving, editDateStart, editDateExpire } = this.state.subscriptionBinding;
    if (saving) return;
    const dateStart = (editDateStart || "").trim();
    const dateExpire = (editDateExpire || "").trim();
    if ((dateStart === "") !== (dateExpire === "")) {
      this.props.root.addNotification(
        Localization.strings.SUBSCRIPTION_BINDING_DATES_BOTH || '請同時填寫開始日及到期日，或兩者留空',
        false,
      );
      return;
    }
    if (dateStart && dateExpire && dateExpire < dateStart) {
      this.props.root.addNotification(
        Localization.strings.SUBSCRIPTION_BINDING_DATES_ORDER || '到期日不可早於開始日',
        false,
      );
      return;
    }
    const bindings = (rows || []).map((row) => {
      const e = edits[row.plan] || {};
      return {
        plan: row.plan,
        subscription_id: e.subscription_id ? parseInt(e.subscription_id, 10) : 0,
        name_pattern: (e.name_pattern || "").trim(),
        name_pattern_en: (e.name_pattern_en || "").trim(),
      };
    });
    const bad = bindings.find((b) => !b.name_pattern || !b.name_pattern_en
      || (!b.name_pattern.includes('{school}') && !b.name_pattern.includes('{school_en}'))
      || (!b.name_pattern_en.includes('{school}') && !b.name_pattern_en.includes('{school_en}')));
    if (bad) {
      this.props.root.addNotification(
        Localization.strings.SUBSCRIPTION_BINDING_PATTERN_HINT
          || '名稱格式必須包含 {school} 或 {school_en}',
        false,
      );
      return;
    }
    this.setSubscriptionBinding({ saving: true });
    this.props.root.refs.API.request(
      "admin_subscription_binding_put",
      { bindings, date_start: dateStart, date_expire: dateExpire },
      (res) => {
        const payload = res?.body?.result;
        const saved = payload && Array.isArray(payload.bindings) ? payload.bindings : null;
        if (!saved) {
          this.setSubscriptionBinding({ saving: false });
          this.props.root.addNotification(
            res?.body?.remarks || Localization.strings.SUBSCRIPTION_BINDING_SAVE_FAILED || '儲存訂閱綁定失敗',
            false,
          );
          return;
        }
        const nextEdits = {};
        saved.forEach((row) => {
          nextEdits[row.plan] = {
            subscription_id: row.subscription_id ? String(row.subscription_id) : "",
            name_pattern: row.name_pattern || "",
            name_pattern_en: row.name_pattern_en || "",
          };
        });
        const savedStart = payload.date_start || "";
        const savedExpire = payload.date_expire || "";
        this.setSubscriptionBinding({
          rows: saved, edits: nextEdits, saving: false,
          dateStart: savedStart, dateExpire: savedExpire,
          editDateStart: savedStart, editDateExpire: savedExpire,
        });
        this.props.root.addNotification(
          Localization.strings.SUBSCRIPTION_BINDING_SAVED || '已儲存訂閱綁定', true);
      },
      (err) => {
        this.setSubscriptionBinding({ saving: false });
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.SUBSCRIPTION_BINDING_SAVE_FAILED || '儲存訂閱綁定失敗',
          false,
        );
      }
    );
  }

  // The name the import will give a school's subscription, for the preview
  // column — the same substitution the server does (render_name).
  previewBindingName(pattern, plan, sample, sampleEn) {
    const labels = EDCITY_IMPORT_PLAN_LABELS;
    const labelsEn = EDCITY_IMPORT_PLAN_LABELS_EN;
    return String(pattern || "")
      .replace(/\{school_en\}/g, sampleEn || sample)
      .replace(/\{school\}/g, sample)
      .replace(/\{plan_en\}/g, labelsEn[plan] || plan)
      .replace(/\{plan\}/g, labels[plan] || plan)
      .replace(/\s+/g, ' ')
      .trim();
  }

  subscriptionBindingTab() {
    const sb = this.state.subscriptionBinding;
    const canSave = this.isPlatformAdmin();
    const templateOptions = (sb.templates || []).map((t) => ({
      value: String(t.id),
      label: `[${t.id}] ${t.name || ''}${t.name_en ? ' / ' + t.name_en : ''}`,
    }));
    // A bound id whose subscription is no longer a template (or is gone)
    // must still appear in the picker, or the page would show it as unbound.
    (sb.rows || []).forEach((row) => {
      const e = sb.edits[row.plan];
      const sid = e ? e.subscription_id : "";
      if (sid && !templateOptions.some((o) => o.value === sid)) {
        templateOptions.push({
          value: sid,
          label: row.missing
            ? `[${sid}] (${Localization.strings.SUBSCRIPTION_BINDING_MISSING || '訂閱已不存在'})`
            : `[${sid}] ${row.subscription_name || ''}`,
        });
      }
    });
    const dirty = this.subscriptionBindingIsDirty();
    return (
      <Box
        key="subscription-binding"
        className="admin-page-transition"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" maw={1100}>
          <Box>
            <Text fw={600} fz="lg">
              {Localization.strings.SUBSCRIPTION_BINDING_TAB || '訂閱綁定'}
            </Text>
            <Text c="dimmed" fz="sm" mt={4}>
              {Localization.strings.SUBSCRIPTION_BINDING_DESC
                || '設定「匯入對接 Excel」時，每種訂閱類型會複製哪個訂閱範本，以及新訂閱的名稱格式。可用變數：{school} 學校中文名稱、{school_en} 學校英文名稱、{plan} 類型 (體驗版/基本版/優越版)、{plan_en} 類型英文。'}
            </Text>
          </Box>
          {!canSave && (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
              {Localization.strings.SUBSCRIPTION_BINDING_READONLY
                || '只有平台管理員可以更改訂閱綁定。'}
            </Alert>
          )}
          {sb.loading ? (
            <TableLoadingCard icon={<IconAdjustments {...LOADING_CARD_ICON_PROPS} />} />
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table verticalSpacing="md" style={{ tableLayout: 'fixed', minWidth: 900 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 110 }}>{Localization.strings.SUBSCRIPTION_BINDING_PLAN || '訂閱類型'}</Table.Th>
                    <Table.Th style={{ width: 300 }}>{Localization.strings.SUBSCRIPTION_BINDING_TEMPLATE || '訂閱範本'}</Table.Th>
                    <Table.Th>{Localization.strings.SUBSCRIPTION_BINDING_PATTERN || '新訂閱名稱格式'}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(sb.rows || []).map((row) => {
                    const e = sb.edits[row.plan] || {};
                    const sample = Localization.strings.SUBSCRIPTION_BINDING_SAMPLE_SCHOOL || 'XXX中學';
                    const sampleEn = Localization.strings.SUBSCRIPTION_BINDING_SAMPLE_SCHOOL_EN || 'XXX Secondary School';
                    return (
                      <Table.Tr key={row.plan}>
                        <Table.Td style={{ verticalAlign: 'top', paddingTop: 18 }}>
                          <Text fw={600}>{row.label}</Text>
                          <Text c="dimmed" fz="xs">{row.label_en}</Text>
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          <Select
                            searchable
                            clearable
                            limit={50}
                            placeholder={Localization.strings.SUBSCRIPTION_BINDING_PICK_TEMPLATE || '選擇訂閱範本'}
                            data={templateOptions}
                            value={e.subscription_id || null}
                            onChange={(value) => this.handleSubscriptionBindingEdit(row.plan, 'subscription_id', value)}
                            disabled={!canSave || sb.saving}
                            rightSection={sb.templatesLoading ? <Loader size={14} /> : undefined}
                            error={row.missing && String(row.subscription_id) === String(e.subscription_id)
                              ? (Localization.strings.SUBSCRIPTION_BINDING_MISSING || '訂閱已不存在')
                              : undefined}
                          />
                          {!e.subscription_id && (
                            <Text c="orange" fz="xs" mt={4}>
                              {Localization.strings.SUBSCRIPTION_BINDING_UNBOUND
                                || '未綁定：匯入 CSV 時標記此類型的學校會被拒絕'}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          <Stack gap={6}>
                            <TextInput
                              value={e.name_pattern || ""}
                              onChange={(ev) => this.handleSubscriptionBindingEdit(row.plan, 'name_pattern', ev.target.value)}
                              disabled={!canSave || sb.saving}
                              maxLength={200}
                              leftSection={<Text fz="xs" c="dimmed">中</Text>}
                            />
                            <TextInput
                              value={e.name_pattern_en || ""}
                              onChange={(ev) => this.handleSubscriptionBindingEdit(row.plan, 'name_pattern_en', ev.target.value)}
                              disabled={!canSave || sb.saving}
                              maxLength={200}
                              leftSection={<Text fz="xs" c="dimmed">EN</Text>}
                            />
                            <Text c="dimmed" fz="xs">
                              {(Localization.strings.SUBSCRIPTION_BINDING_PREVIEW || '例：')}
                              {this.previewBindingName(e.name_pattern, row.plan, sample, sampleEn)}
                              {' / '}
                              {this.previewBindingName(e.name_pattern_en, row.plan, sample, sampleEn)}
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          <Box>
            <Text fw={600} mb={4}>
              {Localization.strings.SUBSCRIPTION_BINDING_DATES_TITLE || '學校訂閱期限'}
            </Text>
            <Text c="dimmed" fz="sm" mb="sm">
              {Localization.strings.SUBSCRIPTION_BINDING_DATES_DESC
                || '匯入 CSV 時，為每間學校新增的訂閱都會使用這個開始日及到期日。未設定時，匯入只能對接 SCRN，不能建立訂閱。'}
            </Text>
            <Group align="flex-end">
              <TextInput
                type="date"
                label={Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}
                value={sb.editDateStart || ""}
                onChange={(ev) => this.setSubscriptionBinding({ editDateStart: ev.target.value })}
                disabled={!canSave || sb.loading || sb.saving}
                w={190}
              />
              <TextInput
                type="date"
                label={Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}
                value={sb.editDateExpire || ""}
                onChange={(ev) => this.setSubscriptionBinding({ editDateExpire: ev.target.value })}
                disabled={!canSave || sb.loading || sb.saving}
                error={sb.editDateStart && sb.editDateExpire && sb.editDateExpire < sb.editDateStart
                  ? (Localization.strings.SUBSCRIPTION_BINDING_DATES_ORDER || '到期日不可早於開始日')
                  : undefined}
                w={190}
              />
              {!sb.loading && !(sb.dateStart && sb.dateExpire) && (
                <Text c="orange" fz="sm" pb={8}>
                  {Localization.strings.SUBSCRIPTION_BINDING_DATES_UNSET || '未設定：匯入時無法建立訂閱'}
                </Text>
              )}
            </Group>
          </Box>
          <Group>
            <Button
              onClick={this.handleSubscriptionBindingSave}
              loading={sb.saving}
              disabled={!canSave || sb.loading || !dirty}
              leftSection={<IconCheck size={16} />}
            >
              {Localization.strings.SAVE || '儲存'}
            </Button>
            <Button
              variant="default"
              onClick={this.loadSubscriptionBinding}
              disabled={sb.loading || sb.saving}
              leftSection={<IconRefresh size={16} />}
            >
              {Localization.strings.SUBSCRIPTION_BINDING_RELOAD || '重新載入'}
            </Button>
          </Group>
        </Stack>
      </Box>
    );
  }

  // ------------------------------------------------------------------------
  // EdCity School ID Mapping > Import Mapping Excel
  //
  // upload  → parse the .xlsx locally (tools/edcityImportCsv.js) → server DRY RUN with
  //           the AI name check →
  // confirm → admin unticks / ticks rows (AI-flagged rows start unticked),
  //           picks the grant window → server COMMIT of the ticked rows →
  // result  → per-row outcome, mapping list refreshed on close.
  // ------------------------------------------------------------------------

  setEdcityImportDialog(patch) {
    const dialog = { ...this.state.edcityImportDialog, ...patch };
    this.setState({ edcityImportDialog: dialog });
    this.state.edcityImportDialog = dialog;
  }

  handleEdcityImportOpen = () => {
    this.setEdcityImportDialog({ ...createEdcityImportDialogState(), open: true });
  }

  handleEdcityImportClose = () => {
    const dialog = this.state.edcityImportDialog;
    if (dialog.isLoading) {
      return;
    }
    const imported = dialog.step === 'result';
    this.setEdcityImportDialog(createEdcityImportDialogState());
    if (imported) {
      this.handleEdcityScrnSearch();
    }
  }

  handleEdcityImportBackToUpload = () => {
    this.setEdcityImportDialog({
      ...createEdcityImportDialogState(),
      open: true,
      fileInputKey: this.state.edcityImportDialog.fileInputKey + 1,
    });
  }

  downloadEdcityImportResult = (rows) => {
    const blob = new Blob([buildEdcityImportResultWorkbook(rows || [])], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = edcityImportResultFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  handleEdcityImportResultDownload = () => {
    const rows = (this.state.edcityImportDialog && this.state.edcityImportDialog.resultRows) || [];
    this.downloadEdcityImportResult(rows);
  }

  handleEdcityImportTemplateDownload = () => {
    // An .xlsx, not a CSV: its SCRN column is pre-formatted as Text so Excel
    // never turns a 12-digit code into "1E+11" (see tools/edcityImportCsv.js).
    const blob = new Blob([buildEdcityImportTemplateWorkbook()], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = edcityImportTemplateFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  handleEdcityImportFileSelected = (file) => {
    if (!file) {
      return;
    }
    this.setEdcityImportDialog({ fileInputKey: this.state.edcityImportDialog.fileInputKey + 1 });
    if (file.size > 2 * 1024 * 1024) {
      this.props.root.addNotification("檔案過大 (上限 2MB)", false);
      return;
    }
    if (!isSpreadsheetFileName(file.name)) {
      this.props.root.addNotification(
        Localization.strings.EDCITY_IMPORT_EXCEL_ONLY || '請上傳 Excel 檔案 (.xlsx)，可先下載 Excel 範本', false);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { rows, error } = parseEdcityImportWorkbook(reader.result);
      if (error) {
        this.props.root.addNotification(error, false);
        return;
      }
      this.setEdcityImportDialog({ fileName: file.name, rows, isLoading: true });
      this.props.root.refs.API.request(
        "admin_edcity_scrn_import",
        { rows: rows.map(edcityImportRowForServer), validate_only: true, run_ai: true },
        this.edcityImportValidateOK,
        this.edcityImportFailed,
      );
    };
    reader.onerror = () => {
      this.props.root.addNotification("讀取檔案失敗", false);
    };
    reader.readAsArrayBuffer(file);
  }

  edcityImportValidateOK = (res) => {
    const result = res?.body?.result;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("驗證失敗: 伺服器回應格式錯誤", false);
      this.setEdcityImportDialog({ isLoading: false });
      return;
    }
    // A plan cell the parser could not read is an error only the client knows
    // about (the server never saw the text) — refuse the row here.
    const parsedRows = this.state.edcityImportDialog.rows || [];
    const reviewRows = result.rows.map((row) => {
      const parsed = parsedRows[row.index];
      if (parsed && parsed.scrn_exponent) {
        // The server only sees "not 12 digits"; the parser knows it was Excel.
        return { ...row, status: 'error', reason: 'scrn_excel_exponent', plans: [] };
      }
      if (parsed && parsed.plan_invalid !== undefined) {
        return { ...row, status: 'error', reason: 'invalid_plan', plan_invalid: parsed.plan_invalid, plans: [] };
      }
      if (parsed && parsed.missing && parsed.missing.length) {
        return { ...row, status: 'error', reason: 'missing_' + parsed.missing[0], plans: [] };
      }
      return row;
    });
    // Default selection: every valid row the AI (or an exact name match)
    // confirmed. Flagged rows are opted IN by the admin, never silently.
    const selected = {};
    reviewRows.forEach((row) => {
      selected[row.index] = row.status === 'ok' && !row.needs_review;
    });
    const invalidCount = reviewRows.filter((r) => r.status === 'error').length;
    this.setEdcityImportDialog({
      step: 'confirm',
      reviewRows,
      summary: { ...result, valid_count: reviewRows.length - invalidCount, invalid_count: invalidCount },
      selected,
      isLoading: false,
    });
  }

  edcityImportFailed = (err) => {
    let msg = Localization.strings.EDCITY_IMPORT_FAILED || 'Excel 匯入失敗';
    if (err?.body?.remarks) {
      msg += ": " + err.body.remarks;
    }
    this.props.root.addNotification(msg, false);
    this.setEdcityImportDialog({ isLoading: false });
  }

  handleEdcityImportToggleRow = (index, checked) => {
    const selected = { ...this.state.edcityImportDialog.selected, [index]: !!checked };
    this.setEdcityImportDialog({ selected });
  }

  handleEdcityImportSelectAllValid = (includeReview) => {
    const dialog = this.state.edcityImportDialog;
    const selected = {};
    (dialog.reviewRows || []).forEach((row) => {
      selected[row.index] = row.status === 'ok' && (includeReview || !row.needs_review);
    });
    this.setEdcityImportDialog({ selected });
  }

  edcityImportSelectedRows() {
    const dialog = this.state.edcityImportDialog;
    return (dialog.reviewRows || []).filter((row) => row.status === 'ok' && dialog.selected[row.index]);
  }

  handleEdcityImportConfirm = () => {
    const dialog = this.state.edcityImportDialog;
    const picked = this.edcityImportSelectedRows();
    if (picked.length === 0) {
      this.props.root.addNotification(Localization.strings.EDCITY_IMPORT_NOTHING_SELECTED || '沒有選擇任何學校', false);
      return;
    }
    // The grant window is configured once on the 訂閱綁定 page; the server
    // applies it. Refuse here (before any write) when a picked row would
    // create a subscription and no window is set.
    const dates = (dialog.summary && dialog.summary.dates) || {};
    const createsPlan = picked.some((r) => (r.plans || []).some((p) => p.action === 'create'));
    if (createsPlan && !(dates.date_start && dates.date_expire)) {
      this.props.root.addNotification(
        Localization.strings.EDCITY_IMPORT_DATES_UNSET
          || '尚未設定學校訂閱期限，請先於「訂閱綁定」頁面設定開始日及到期日',
        false,
      );
      return;
    }
    // Send the ORIGINAL parsed rows (with their CSV line) for the picked
    // indexes; the server re-validates them without the AI.
    const rows = picked.map((r) => dialog.rows[r.index]).filter(Boolean).map(edcityImportRowForServer);
    this.setEdcityImportDialog({ isLoading: true });
    this.props.root.refs.API.request(
      "admin_edcity_scrn_import",
      { rows, validate_only: false },
      this.edcityImportCommitOK,
      this.edcityImportFailed,
    );
  }

  edcityImportCommitOK = (res) => {
    const result = res?.body?.result;
    if (!result || !Array.isArray(result.rows)) {
      this.props.root.addNotification("匯入失敗: 伺服器回應格式錯誤", false);
      this.setEdcityImportDialog({ isLoading: false });
      return;
    }
    this.setEdcityImportDialog({
      step: 'result',
      resultRows: result.rows,
      result,
      isLoading: false,
    });
    const created = result.subscription_created_count || 0;
    const done = result.done_count || 0;
    this.props.root.addNotification(
      (Localization.strings.EDCITY_IMPORT_DONE_TOAST || '已匯入 {0} 間學校，建立 {1} 個訂閱')
        .replace('{0}', done).replace('{1}', created),
      done > 0,
    );
    // A new password exists only in this response. Download it immediately
    // so closing the dialog does not lose the only copy.
    if ((result.rows || []).some((row) => row.school_admin && row.school_admin.password)) {
      this.downloadEdcityImportResult(result.rows);
    }
  }

  getEdcityImportReasonText(reason) {
    const map = {
      invalid_scrn: 'SCRN 須為 12 位數字',
      scrn_excel_exponent: Localization.strings.EDCITY_IMPORT_SCRN_EXCEL
        || 'SCRN 已被 Excel 轉成科學記號 (如 1E+11)，數字已遺失。請在 Excel 將 SCRN 欄設為「文字」格式，重新輸入 12 位數字後再儲存 CSV',
      invalid_plan: '訂閱類型無效 (只接受 體驗版 / 基本版 / 優越版)',
      missing_scrn: '缺少 SCRN',
      missing_school_name_edcity: '缺少 School Name (EdCity)',
      missing_school_id: '缺少 School_id',
      missing_plan: '缺少訂閱類型 (體驗版 / 基本版 / 優越版)',
      invalid_school_id: '學校 ID 無效',
      school_not_found: '找不到學校 ID',
      duplicate_scrn_in_file: 'CSV 檔案內 SCRN 重複',
      duplicate_school_in_file: 'CSV 檔案內學校 ID 重複',
      scrn_mapped_to_other_school: 'SCRN 已對接至另一間學校',
      plan_not_bound: '訂閱類型未綁定範本 (請先設定訂閱綁定)',
      school_mapped_to_other_scrn: '此學校已有另一個 SCRN',
      already_assigned: '學校已有同名訂閱，略過',
      mapping_failed: '寫入對接記錄失敗',
      school_admin_failed: '未能建立學校管理員',
      plan_failed: '部分訂閱未能建立',
      subscription_create_failed: '建立訂閱失敗',
      assign_failed: '訂閱已建立，但指派至學校失敗',
    };
    return map[reason] || reason || '';
  }

  renderEdcityImportAdminPassword(admin) {
    if (!admin) {
      return <Text c="dimmed" fz="xs">-</Text>;
    }
    if (admin.password) {
      return <Text fz="sm" style={{ fontFamily: 'monospace' }}>{admin.password}</Text>;
    }
    if (admin.existed) {
      return (
        <Text fz="xs" c="dimmed">
          {Localization.strings.EDCITY_IMPORT_ADMIN_PASSWORD_UNAVAILABLE || '已有帳號，密碼無法取回'}
        </Text>
      );
    }
    if (admin.status === 'username_taken') {
      return (
        <Text fz="xs" c="red">
          {Localization.strings.EDCITY_IMPORT_ADMIN_USERNAME_TAKEN || '帳號名稱已被其他用戶使用'}
        </Text>
      );
    }
    if (admin.status === 'error') {
      return (
        <Text fz="xs" c="red">
          {Localization.strings.EDCITY_IMPORT_ADMIN_FAILED || '未能建立學校管理員'}
        </Text>
      );
    }
    return <Text c="dimmed" fz="xs">-</Text>;
  }

  renderEdcityImportAiBadge(ai) {
    if (!ai) {
      return <Text c="dimmed" fz="xs">-</Text>;
    }
    const colors = { match: 'teal', mismatch: 'red', uncertain: 'yellow', unavailable: 'gray', skipped: 'gray' };
    const labels = {
      match: Localization.strings.EDCITY_IMPORT_AI_MATCH || '相符',
      mismatch: Localization.strings.EDCITY_IMPORT_AI_MISMATCH || '不相符',
      uncertain: Localization.strings.EDCITY_IMPORT_AI_UNCERTAIN || '不確定',
      unavailable: Localization.strings.EDCITY_IMPORT_AI_UNAVAILABLE || 'AI 無法核對',
      skipped: Localization.strings.EDCITY_IMPORT_AI_SKIPPED || '未核對',
    };
    return (
      <Stack gap={2}>
        <Badge color={colors[ai.verdict] || 'gray'} variant="light" size="sm" style={{ width: 'fit-content' }}>
          {labels[ai.verdict] || ai.verdict}
        </Badge>
        {ai.reason ? <Text fz="xs" c="dimmed" style={{ whiteSpace: 'normal' }}>{ai.reason}</Text> : null}
      </Stack>
    );
  }

  renderEdcityImportPlanCells(plans, isResult) {
    if (!plans || plans.length === 0) {
      return <Text c="dimmed" fz="xs">-</Text>;
    }
    const colorFor = (action) => {
      if (action === 'created') return 'teal';
      if (action === 'create') return 'blue';
      if (action === 'skip') return 'gray';
      return 'red';
    };
    return (
      <Stack gap={4}>
        {plans.map((p) => (
          <Group key={p.plan} gap={6} wrap="nowrap" align="flex-start">
            <Badge color={colorFor(p.action)} variant="light" size="sm" style={{ flexShrink: 0 }}>
              {p.label}
            </Badge>
            <Text fz="xs" style={{ whiteSpace: 'normal' }}>
              {p.action === 'skip' || p.action === 'error'
                ? this.getEdcityImportReasonText(p.reason)
                : p.subscription_name}
              {isResult && p.subscription_id ? ` (#${p.subscription_id})` : ''}
              {isResult && p.reused ? ' ' + (Localization.strings.EDCITY_IMPORT_REUSED || '(沿用已存在的訂閱)') : ''}
            </Text>
          </Group>
        ))}
      </Stack>
    );
  }

  renderEdcityImportRowsTable(rows, mode) {
    const dialog = this.state.edcityImportDialog;
    const isConfirm = mode === 'confirm';
    return (
      <ScrollArea.Autosize mah={360} type="auto">
        <Table striped highlightOnHover verticalSpacing="xs" stickyHeader style={{ minWidth: mode === 'result' ? 1320 : 980 }}>
          <Table.Thead>
            <Table.Tr>
              {isConfirm && <Table.Th w={54}>{Localization.strings.EDCITY_IMPORT_COL_PICK || '匯入'}</Table.Th>}
              <Table.Th w={54}>CSV 行</Table.Th>
              <Table.Th w={130}>SCRN</Table.Th>
              <Table.Th>{Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱'}</Table.Th>
              <Table.Th w={70}>{Localization.strings.EDCITY_SCRN_OUR_SCHOOL_ID || '學校 ID'}</Table.Th>
              <Table.Th>{Localization.strings.EDCITY_SCRN_OUR_SCHOOL_NAME || '學校名稱'}</Table.Th>
              <Table.Th w={200}>{Localization.strings.EDCITY_IMPORT_COL_PLANS || '訂閱'}</Table.Th>
              <Table.Th w={150}>
                <Group gap={4} wrap="nowrap"><IconRobot size={14} />{Localization.strings.EDCITY_IMPORT_COL_AI || 'AI 核對'}</Group>
              </Table.Th>
              <Table.Th w={170}>{Localization.strings.EDCITY_IMPORT_COL_STATUS || '狀態'}</Table.Th>
              {mode === 'result' && (
                <Table.Th w={170}>{Localization.strings.EDCITY_IMPORT_COL_ADMIN_USER || 'School Admin Username'}</Table.Th>
              )}
              {mode === 'result' && (
                <Table.Th w={200}>{Localization.strings.EDCITY_IMPORT_COL_ADMIN_PASSWORD || 'School Admin Password'}</Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => {
              const isError = row.status === 'error';
              const statusText = (() => {
                if (isError) return this.getEdcityImportReasonText(row.reason);
                if (row.status === 'done') return Localization.strings.EDCITY_IMPORT_ROW_DONE || '已匯入';
                if (row.status === 'partial') return this.getEdcityImportReasonText(row.reason);
                const bits = [];
                if (row.mapping_action === 'create') bits.push(Localization.strings.EDCITY_IMPORT_MAP_CREATE || '新增對接');
                if (row.mapping_action === 'connect') bits.push(Localization.strings.EDCITY_IMPORT_MAP_CONNECT || '對接現有 SCRN');
                if (row.mapping_action === 'keep') bits.push(Localization.strings.EDCITY_IMPORT_MAP_KEEP || '對接已存在');
                (row.warnings || []).forEach((w) => bits.push(this.getEdcityImportReasonText(w)));
                return bits.join('；');
              })();
              const statusColor = isError || row.status === 'partial' ? 'red' : (row.status === 'done' ? 'teal' : undefined);
              return (
                <Table.Tr key={row.index} style={isError ? { opacity: 0.7 } : undefined}>
                  {isConfirm && (
                    <Table.Td>
                      <Checkbox
                        checked={!!dialog.selected[row.index]}
                        disabled={isError || dialog.isLoading}
                        onChange={(e) => this.handleEdcityImportToggleRow(row.index, e.currentTarget.checked)}
                      />
                    </Table.Td>
                  )}
                  <Table.Td>{row.line != null ? row.line : row.index + 1}</Table.Td>
                  <Table.Td style={{ fontFamily: 'monospace' }}>{row.scrn || '-'}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'normal' }}>{row.school_name_edcity || '-'}</Table.Td>
                  <Table.Td>{row.school_id != null ? row.school_id : '-'}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'normal' }}>
                    {row.school_name || '-'}
                    {row.school_name_eng ? <Text fz="xs" c="dimmed">{row.school_name_eng}</Text> : null}
                  </Table.Td>
                  <Table.Td>{this.renderEdcityImportPlanCells(row.plans, mode === 'result')}</Table.Td>
                  <Table.Td>{mode === 'result' ? <Text c="dimmed" fz="xs">-</Text> : this.renderEdcityImportAiBadge(row.ai)}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'normal' }}>
                    <Text fz="sm" c={statusColor}>
                      {statusText || '-'}
                      {row.reason === 'invalid_plan' && row.plan_invalid ? `：${row.plan_invalid}` : ''}
                    </Text>
                  </Table.Td>
                  {mode === 'result' && (
                    <Table.Td style={{ fontFamily: 'monospace', whiteSpace: 'normal' }}>
                      {(row.school_admin && row.school_admin.username) || '-'}
                    </Table.Td>
                  )}
                  {mode === 'result' && (
                    <Table.Td style={{ fontFamily: 'monospace', whiteSpace: 'normal' }}>
                      {this.renderEdcityImportAdminPassword(row.school_admin)}
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    );
  }

  renderEdcityImportDialog() {
    const dialog = this.state.edcityImportDialog;
    const summary = dialog.summary || {};
    const picked = dialog.step === 'confirm' ? this.edcityImportSelectedRows() : [];
    const pickedPlanCount = picked.reduce(
      (n, r) => n + (r.plans || []).filter((p) => p.action === 'create').length, 0);
    const dates = summary.dates || {};
    const datesSet = !!(dates.date_start && dates.date_expire);
    const aiStatus = summary.ai;
    const result = dialog.result || {};
    return (
      <Modal
        opened={dialog.open}
        onClose={this.handleEdcityImportClose}
        closeOnClickOutside={!dialog.isLoading}
        closeOnEscape={!dialog.isLoading}
        withCloseButton={!dialog.isLoading}
        title={<Text fw={600}>{Localization.strings.EDCITY_IMPORT_TITLE || '匯入對接 Excel'}</Text>}
        size={dialog.step === 'upload' ? 'lg' : '90%'}
        centered
        zIndex={2000}
      >
        {dialog.step === 'upload' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
              <div>{Localization.strings.EDCITY_IMPORT_HELP_1
                || '上傳 Excel 檔案 (.xlsx)，一次過對接多間學校的 EdCity SCRN，並按需要為每間學校建立及指派訂閱。第一行必須是欄位標題：'}</div>
              <div style={{ marginTop: 6 }}>
                <strong>{Localization.strings.EDCITY_IMPORT_HELP_REQUIRED || '必要欄位 (每行均須填寫):'}</strong>
                {' '}SCRN (12 位數字)、School Name (EdCity)、School_id (本系統的學校 ID)、體驗版/基本版/優越版 (填其中一個訂閱類型)
              </div>
              <div style={{ marginTop: 6, color: '#c05621' }}>
                {Localization.strings.EDCITY_IMPORT_HELP_EXCEL
                  || '請使用下方的 Excel 範本：其 SCRN 欄已設為「文字」格式，12 位數字不會被 Excel 變成 1E+11。'}
              </div>
              <div style={{ marginTop: 6, color: '#888' }}>
                {(Localization.strings.EDCITY_IMPORT_HELP_2
                  || '每次最多 {0} 行。上傳後伺服器會先驗證每行資料，並以 AI 核對 EdCity 學校名稱與本系統學校是否相符；有疑問的學校會標示出來，由你決定是否匯入。確認後才會寫入資料庫。訂閱會按「訂閱綁定」頁面設定的範本建立。')
                  .replace('{0}', EDCITY_IMPORT_MAX_ROWS)}
              </div>
            </Box>
            <Group>
              <Button variant="light" leftSection={<IconDownload size={16} />} onClick={this.handleEdcityImportTemplateDownload}>
                {Localization.strings.EDCITY_IMPORT_DOWNLOAD_TEMPLATE || '下載 Excel 範本'}
              </Button>
              <FileButton
                key={dialog.fileInputKey}
                onChange={this.handleEdcityImportFileSelected}
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              >
                {(props) => (
                  <Button {...props} leftSection={<IconUpload size={16} />} loading={dialog.isLoading}>
                    {Localization.strings.EDCITY_IMPORT_UPLOAD || '上傳 Excel 檔案'}
                  </Button>
                )}
              </FileButton>
              {dialog.isLoading && (
                <Text fz="sm" c="dimmed">
                  {Localization.strings.EDCITY_IMPORT_VALIDATING || '正在驗證及以 AI 核對學校名稱，請稍候...'}
                </Text>
              )}
            </Group>
          </Stack>
        )}

        {dialog.step === 'confirm' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <div><strong>檔案:</strong> {dialog.fileName} (共 {summary.total || 0} 行資料)</div>
              <Group gap="md" mt={4}>
                <Text component="span" c="teal" fw={600}>{summary.valid_count || 0} 間學校可匯入</Text>
                {summary.review_count > 0 && (
                  <Text component="span" c="orange" fw={600}>{summary.review_count} 間需人手核對 (預設不匯入)</Text>
                )}
                {summary.invalid_count > 0 && (
                  <Text component="span" c="red" fw={600}>{summary.invalid_count} 行有問題 (將被略過)</Text>
                )}
                <Text component="span" c="dimmed">將建立 {summary.plan_create_count || 0} 個訂閱
                  {summary.plan_skip_count > 0 ? `，略過 ${summary.plan_skip_count} 個已存在的訂閱` : ''}</Text>
              </Group>
              {aiStatus && aiStatus.error && (
                <Alert color="yellow" variant="light" mt="sm" icon={<IconAlertTriangle size={18} />}>
                  {(Localization.strings.EDCITY_IMPORT_AI_ERROR || 'AI 核對未能完成：{0}。未核對的學校已標示為需人手核對。')
                    .replace('{0}', aiStatus.error)}
                </Alert>
              )}
            </Box>
            <Group justify="space-between" align="center" wrap="wrap">
              <Text fz="sm" c={datesSet ? 'dimmed' : 'orange'}>
                {datesSet
                  ? (Localization.strings.EDCITY_IMPORT_DATES || '學校訂閱期限：{0} 至 {1} (於「訂閱綁定」頁面設定)')
                    .replace('{0}', dates.date_start).replace('{1}', dates.date_expire)
                  : (Localization.strings.EDCITY_IMPORT_DATES_UNSET
                    || '尚未設定學校訂閱期限，請先於「訂閱綁定」頁面設定開始日及到期日')}
              </Text>
              <Group gap="xs">
                <Button variant="subtle" size="xs" onClick={() => this.handleEdcityImportSelectAllValid(false)} disabled={dialog.isLoading}>
                  {Localization.strings.EDCITY_IMPORT_SELECT_CONFIRMED || '只選 AI 相符'}
                </Button>
                <Button variant="subtle" size="xs" onClick={() => this.handleEdcityImportSelectAllValid(true)} disabled={dialog.isLoading}>
                  {Localization.strings.EDCITY_IMPORT_SELECT_ALL_VALID || '選擇所有可匯入'}
                </Button>
              </Group>
            </Group>
            {this.renderEdcityImportRowsTable(dialog.reviewRows, 'confirm')}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleEdcityImportBackToUpload} disabled={dialog.isLoading}>
                重新選擇檔案
              </Button>
              <Button
                onClick={this.handleEdcityImportConfirm}
                loading={dialog.isLoading}
                disabled={picked.length === 0 || (pickedPlanCount > 0 && !datesSet)}
                leftSection={<IconFileImport size={16} />}
              >
                {(Localization.strings.EDCITY_IMPORT_CONFIRM || '確認匯入 {0} 間學校 (建立 {1} 個訂閱)')
                  .replace('{0}', picked.length).replace('{1}', pickedPlanCount)}
              </Button>
            </Group>
          </Stack>
        )}

        {dialog.step === 'result' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: (result.done_count || 0) > 0 ? '#e6f7ee' : '#fdecea', borderRadius: 6 }}>
              <Text fw={600}>
                {(Localization.strings.EDCITY_IMPORT_RESULT_TITLE || '已匯入 {0} 間學校')
                  .replace('{0}', result.done_count || 0)}
              </Text>
              <Text fz="sm" mt={4}>
                對接：新增 {result.mapping_created_count || 0}，對接現有 SCRN {result.mapping_connected_count || 0}；
                訂閱：建立 {result.subscription_created_count || 0}，略過 {result.subscription_skipped_count || 0}；
                期限 {result.date_start} 至 {result.date_expire}
              </Text>
              {(result.partial_count || 0) > 0 && (
                <Text c="red" fz="sm" mt={4}>{result.partial_count} 間學校只完成部分步驟，詳情見下表</Text>
              )}
              {(result.invalid_count || 0) > 0 && (
                <Text c="red" fz="sm" mt={4}>{result.invalid_count} 行未能匯入，原因見下表</Text>
              )}
              <Text fz="sm" mt={4}>
                {(Localization.strings.EDCITY_IMPORT_ADMIN_SUMMARY || '學校管理員：新建 {0} 個，已有 {1} 個。')
                  .replace('{0}', result.school_admin_created_count || 0)
                  .replace('{1}', result.school_admin_existed_count || 0)}
              </Text>
              <Text fz="sm" c="dimmed" mt={4}>
                {Localization.strings.EDCITY_IMPORT_ADMIN_PASSWORD_NOTE
                  || '新建的學校管理員密碼只會在這次結果及下載的 Excel 中顯示，請立即下載。已有帳號的密碼以單向雜湊儲存，無法取回，該欄留空。'}
              </Text>
            </Box>
            {this.renderEdcityImportRowsTable(dialog.resultRows, 'result')}
            <Group justify="flex-end" mt="sm">
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={this.handleEdcityImportResultDownload}
              >
                {Localization.strings.EDCITY_IMPORT_DOWNLOAD_RESULT || '下載對接 Excel（含學校管理員）'}
              </Button>
              <Button onClick={this.handleEdcityImportClose}>完成</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    );
  }

  // ------------------------------------------------------------------------
  // Extra Locale (預製教材範本) — catalog management + selection history.
  // Platform admins (role >= 3) only; every route repeats the gate server-side.
  // Catalog deletes never touch schools' selection records (they snapshot the
  // label), which is why the delete confirm says so instead of warning about
  // data loss.
  // ------------------------------------------------------------------------

  loadExtraLocaleCatalog = () => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({ extraLocaleLoading: true });
    this.state.extraLocaleLoading = true;
    this.props.root.refs.API.request("admin_extra_locale_list", {}, this.extraLocaleCatalogOK, this.extraLocaleCatalogFailed);
  }

  extraLocaleCatalogOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    this.setState({ extraLocaleRows: rows, extraLocaleLoading: false, hasExtraLocaleLoaded: true });
    this.state.extraLocaleRows = rows;
    this.state.extraLocaleLoading = false;
    this.state.hasExtraLocaleLoaded = true;
  }

  extraLocaleCatalogFailed = (err) => {
    console.log("admin_extra_locale_list failed", err);
    this.setState({ extraLocaleLoading: false, hasExtraLocaleLoaded: true });
    this.state.extraLocaleLoading = false;
    this.state.hasExtraLocaleLoaded = true;
    this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_LOAD_FAILED || '載入預製教材範本失敗', false);
  }

  loadExtraLocaleHistory = () => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    const requestObj = { limit: 500 };
    if (this.state.extraLocaleHistorySearchText && this.state.extraLocaleHistorySearchText.trim() !== "") {
      requestObj.keyword = this.state.extraLocaleHistorySearchText.trim();
    }
    this.setState({ extraLocaleHistoryLoading: true });
    this.state.extraLocaleHistoryLoading = true;
    this.props.root.refs.API.request("admin_extra_locale_selections", requestObj, this.extraLocaleHistoryOK, this.extraLocaleHistoryFailed);
  }

  extraLocaleHistoryOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    this.setState({ extraLocaleHistoryRows: rows, extraLocaleHistoryLoading: false, hasExtraLocaleHistoryLoaded: true });
    this.state.extraLocaleHistoryRows = rows;
    this.state.extraLocaleHistoryLoading = false;
    this.state.hasExtraLocaleHistoryLoaded = true;
  }

  extraLocaleHistoryFailed = (err) => {
    console.log("admin_extra_locale_selections failed", err);
    this.setState({ extraLocaleHistoryLoading: false, hasExtraLocaleHistoryLoaded: true });
    this.state.extraLocaleHistoryLoading = false;
    this.state.hasExtraLocaleHistoryLoaded = true;
    this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_LOAD_FAILED || '載入選擇紀錄失敗', false);
  }

  handleExtraLocaleViewChange = (value) => {
    const view = value === 'history' ? 'history' : 'catalog';
    this.setState({ extraLocaleView: view });
    this.state.extraLocaleView = view;
    if (view === 'history' && !this.state.hasExtraLocaleHistoryLoaded) {
      this.loadExtraLocaleHistory();
    }
  }

  extraLocaleHistorySearchTextChanged = (e) => {
    this.setState({ extraLocaleHistorySearchText: e.target.value });
    this.state.extraLocaleHistorySearchText = e.target.value;
  }

  handleExtraLocaleHistoryFromChange = (e) => {
    const value = e.target.value || "";
    this.setState({ extraLocaleHistoryFrom: value });
    this.state.extraLocaleHistoryFrom = value;
  }

  handleExtraLocaleHistoryToChange = (e) => {
    const value = e.target.value || "";
    this.setState({ extraLocaleHistoryTo: value });
    this.state.extraLocaleHistoryTo = value;
  }

  /**
   * Export the selection history the picker's day range covers:
   *   Selected At, School, User, Category, Extra LOCALE Name
   *
   * Re-FETCHES rather than exporting `extraLocaleHistoryRows`: the table holds
   * whatever the last search returned (500 rows), and the sheet must not
   * silently inherit that cap on top of its own. The current keyword IS carried
   * over, so the export is "what this view is showing, narrowed to the range" —
   * anything else would hand back records the admin filtered away.
   *
   * The range is bounded TWICE. `date_from` / `date_to` narrow the query, so
   * `limit` caps rows already inside the window rather than the newest 2000
   * overall — which matters more than it sounds, because one pick on a shared
   * plan is up to 29 rows here. `filterSelectionsByDayRange` then re-cuts the
   * response, which is not redundant: a server predating the parameters ignores
   * them silently, and the local pass is what stops such a server exporting
   * everything under a filename that names a range. On a current server it
   * removes nothing.
   *
   * Both ends are CALENDAR DAYS and both are inclusive, so the same date twice
   * exports that whole day: the server reads a bare `date_from` as 00:00:00 and
   * a bare `date_to` as 23:59:59. Either may be left empty for an open end.
   *
   * `isSelectionHistoryTruncated` still reports a page the cap cut short — now
   * only when the window genuinely holds more than `limit` records — because a
   * partial sheet passed off as complete is the failure worth naming.
   */
  handleExtraLocaleHistoryExport = () => {
    if (this.state.extraLocaleHistoryExporting) {
      return;
    }
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    const from = this.state.extraLocaleHistoryFrom || "";
    const to = this.state.extraLocaleHistoryTo || "";
    // Refuse an inverted range HERE rather than shipping it. The route 400s it
    // too, but the message belongs next to the two inputs that caused it, and
    // an empty sheet named for a range the admin never had would be worse than
    // either.
    const rangeError = selectionRangeError(from, to);
    if (rangeError !== "") {
      this.props.root.addNotification(
        Localization.strings[rangeError] || '匯出日期範圍不正確。', false);
      return;
    }
    const requestObj = { limit: SELECTION_HISTORY_EXPORT_LIMIT };
    const keyword = (this.state.extraLocaleHistorySearchText || "").trim();
    if (keyword !== "") {
      requestObj.keyword = keyword;
    }
    // Bare 'YYYY-MM-DD' straight through — q() drops empty values, so an open
    // end simply sends no bound. The server widens a bare date_to to that day's
    // 23:59:59, which is why no time is attached here.
    if (from !== "") {
      requestObj.date_from = from;
    }
    if (to !== "") {
      requestObj.date_to = to;
    }
    this.setState({ extraLocaleHistoryExporting: true });
    this.state.extraLocaleHistoryExporting = true;
    const finish = () => {
      this.setState({ extraLocaleHistoryExporting: false });
      this.state.extraLocaleHistoryExporting = false;
    };
    this.props.root.refs.API.request(
      "admin_extra_locale_selections",
      requestObj,
      (res) => {
        finish();
        const fetched = Array.isArray(res?.body?.result) ? res.body.result : [];
        const rows = filterSelectionsByDayRange(fetched, from, to);
        if (rows.length === 0) {
          // Nothing to save — an empty header-only CSV reads as a broken export.
          this.props.root.addNotification(
            Localization.strings.EXTRA_LOCALE_EXPORT_EMPTY || '所選日期範圍內沒有選擇紀錄。', false);
          return;
        }
        this.downloadCsvFile(buildSelectionHistoryCsv(rows), selectionHistoryFileName(from, to));
        if (isSelectionHistoryTruncated(fetched, from)) {
          this.props.root.addNotification(
            (Localization.strings.EXTRA_LOCALE_EXPORT_TRUNCATED
              || '伺服器最多回傳 {0} 筆紀錄，此日期範圍可能未完整匯出；請縮短範圍或加上關鍵字。')
              .replace('{0}', String(SELECTION_HISTORY_EXPORT_LIMIT)), false);
        }
      },
      (err) => {
        finish();
        console.log("admin_extra_locale_selections export failed", err);
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.EXTRA_LOCALE_EXPORT_FAILED || '匯出失敗', false);
      }
    );
  }

  handleExtraLocaleHistorySearchEnterPressed = (e) => {
    if (e.key === "Enter") {
      this.loadExtraLocaleHistory();
    }
  }

  // ------------------------------------------------------------------ //
  // Extra Locale > scheduled report settings
  //
  // The mail itself is sent by an ACA job at 09:00 Hong Kong — yesterday's
  // selections every day, and the previous Mon..Sun additionally on Mondays.
  // Nothing on this page schedules anything; it edits four `system_config`
  // rows the job reads, and offers a manual send for proving the pipeline.
  // ------------------------------------------------------------------ //

  /**
   * Load the settings, and MIRROR them into the four editable fields.
   *
   * The mirror is what makes the form authoritative: after every load and every
   * successful save the inputs hold exactly the server's values, so a recipient
   * list the server normalised (de-duplicated, re-ordered, invalid tokens
   * removed) is visible as stored rather than as typed. `reportDirty` resets
   * with it — there is nothing unsaved the moment the two agree.
   */
  loadReportSettings = () => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({ reportLoading: true });
    this.state.reportLoading = true;
    this.props.root.refs.API.request(
      "admin_extra_locale_report_settings", {},
      (res) => {
        // callbackOK can be handed a null/non-object result (see the API
        // OK-path guard note): treat anything else as "no settings yet" rather
        // than reading fields off it.
        const data = (res && res.body && res.body.result && typeof res.body.result === 'object')
          ? res.body.result : null;
        const next = {
          reportLoading: false,
          hasReportLoaded: true,
          reportSettings: data,
          reportDirty: false,
        };
        if (data) {
          next.reportRecipientsText = String(data.recipients_text || "");
          next.reportDailyEnabled = !!data.daily_enabled;
          next.reportWeeklyEnabled = !!data.weekly_enabled;
          next.reportSendWhenEmpty = !!data.send_when_empty;
        }
        this.setState(next);
        Object.assign(this.state, next);
      },
      (err) => {
        this.setState({ reportLoading: false, hasReportLoaded: true });
        this.state.reportLoading = false;
        this.state.hasReportLoaded = true;
        console.log("admin_extra_locale_report_settings failed", err);
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.EXTRA_LOCALE_REPORT_LOAD_FAILED
            || '無法載入通知電郵設定', false);
      }
    );
  }

  handleReportRecipientsChange = (e) => {
    const value = e.target.value || "";
    this.setState({ reportRecipientsText: value, reportDirty: true });
    this.state.reportRecipientsText = value;
    this.state.reportDirty = true;
  }

  handleReportSwitchChange = (key) => (e) => {
    const value = !!e.currentTarget.checked;
    this.setState({ [key]: value, reportDirty: true });
    this.state[key] = value;
    this.state.reportDirty = true;
  }

  /**
   * Save all four fields.
   *
   * Sends every field rather than only the changed ones: the route takes PATCH
   * semantics so either would work, but a whole-form save is what the single
   * Save button promises, and a partial PUT would leave the page claiming to
   * have saved a switch it never sent.
   *
   * A rejected address is a 400 whose `remarks` NAMES the offending tokens —
   * surfaced verbatim, because "not a valid email address" without saying which
   * one sends the admin re-reading a list of six.
   */
  handleReportSave = () => {
    if (this.state.reportSaving) {
      return;
    }
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({ reportSaving: true });
    this.state.reportSaving = true;
    this.props.root.refs.API.request(
      "admin_extra_locale_report_settings_save",
      {
        recipients: this.state.reportRecipientsText,
        daily_enabled: this.state.reportDailyEnabled,
        weekly_enabled: this.state.reportWeeklyEnabled,
        send_when_empty: this.state.reportSendWhenEmpty,
      },
      (res) => {
        const data = (res && res.body && res.body.result && typeof res.body.result === 'object')
          ? res.body.result : null;
        const next = { reportSaving: false, reportDirty: false };
        if (data) {
          // Re-mirror: the server answers with the STORED settings, so the
          // textarea now shows the normalised list rather than what was typed.
          next.reportSettings = data;
          next.reportRecipientsText = String(data.recipients_text || "");
          next.reportDailyEnabled = !!data.daily_enabled;
          next.reportWeeklyEnabled = !!data.weekly_enabled;
          next.reportSendWhenEmpty = !!data.send_when_empty;
        }
        this.setState(next);
        Object.assign(this.state, next);
        this.props.root.addNotification(
          Localization.strings.EXTRA_LOCALE_REPORT_SAVED || '已儲存通知電郵設定', true);
      },
      (err) => {
        this.setState({ reportSaving: false });
        this.state.reportSaving = false;
        console.log("admin_extra_locale_report_settings_save failed", err);
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.EXTRA_LOCALE_REPORT_SAVE_FAILED
            || '儲存通知電郵設定失敗', false);
      }
    );
  }

  /**
   * Send one report now, over the REAL window and to the REAL list.
   *
   * Not a fake "test" mail to the caller alone: the two things that actually
   * break are a typo in an address and a window that comes back empty, and only
   * a real send surfaces either. The server overrides `send_when_empty` for
   * this call — someone who pressed a button is owed a mail even on a quiet
   * day, since that is the case being checked.
   *
   * Refuses while UNSAVED edits are pending, because the job reads the STORED
   * settings: sending with a freshly-typed address still in the box would mail
   * the old list and look like the new one had failed.
   */
  handleReportSendNow = (kind) => () => {
    if (this.state.reportSendingKind || this.state.reportSaving) {
      return;
    }
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    if (this.state.reportDirty) {
      this.props.root.addNotification(
        Localization.strings.EXTRA_LOCALE_REPORT_SAVE_FIRST
          || '請先儲存設定，再發送測試郵件。', false);
      return;
    }
    this.setState({ reportSendingKind: kind });
    this.state.reportSendingKind = kind;
    const finish = () => {
      this.setState({ reportSendingKind: "" });
      this.state.reportSendingKind = "";
    };
    this.props.root.refs.API.request(
      "admin_extra_locale_report_send", { kind },
      (res) => {
        finish();
        const data = (res && res.body && res.body.result && typeof res.body.result === 'object')
          ? res.body.result : {};
        if (data.status === 'no_recipients') {
          this.props.root.addNotification(
            Localization.strings.EXTRA_LOCALE_REPORT_NO_RECIPIENTS
              || '尚未設定收件電郵地址。', false);
          return;
        }
        // Report the COUNT and the WINDOW, not just "sent": a mail that went
        // out over the wrong dates is the failure this button exists to catch,
        // and the recipient cannot see the window from the button.
        this.props.root.addNotification(
          (Localization.strings.EXTRA_LOCALE_REPORT_SENT
            || '已發送 {0} 至 {1}（{2} 筆紀錄）')
            .replace('{0}', data.date_from === data.date_to
              ? String(data.date_from || '')
              : `${data.date_from || ''} - ${data.date_to || ''}`)
            .replace('{1}', (data.recipients || []).join(', '))
            .replace('{2}', String(data.count == null ? 0 : data.count)), true);
      },
      (err) => {
        finish();
        console.log("admin_extra_locale_report_send failed", err);
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.EXTRA_LOCALE_REPORT_SEND_FAILED
            || '發送失敗', false);
      }
    );
  }

  openExtraLocaleCreateDialog = () => {
    const dialog = createExtraLocaleEditDialogState({ open: true });
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  openExtraLocaleEditDialog = (row) => {
    // row.name is epack.name from the list join; NULL means the epack is gone.
    const dialog = createExtraLocaleEditDialogState({
      open: true,
      row: row,
      category: row.category || "",
      remarks: row.remarks || "",
      epack_id: row.epack_id && parseInt(row.epack_id) > 0 ? parseInt(row.epack_id) : 0,
      resolvedEpackName: row.name || "",
      epackSearchText: row.name || "",
      category_index: row.category_index != null ? String(row.category_index) : "",
      display_index: row.display_index != null ? String(row.display_index) : "",
      is_enabled: parseInt(row.is_enabled) === 1,
      // manual_path is the JOINED `file` row, so it is empty both when nothing
      // was ever attached and when the linked file has since been deleted. Both
      // read as "no manual", which is what the server reports too.
      manual_file_id: row.manual_path && parseInt(row.manual_file_id) > 0
        ? parseInt(row.manual_file_id) : 0,
      manual_path: row.manual_path || "",
      manual_display_name: row.manual_display_name || "",
    });
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  closeExtraLocaleEditDialog = () => {
    if (this.state.extraLocaleEditDialog.loading) {
      return;
    }
    if (this.extraLocaleEpackResolveTimer) {
      clearTimeout(this.extraLocaleEpackResolveTimer);
      this.extraLocaleEpackResolveTimer = null;
    }
    const dialog = createExtraLocaleEditDialogState();
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  handleExtraLocaleEditFieldChange = (field) => (e) => {
    const dialog = { ...this.state.extraLocaleEditDialog, [field]: e.target.value };
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  patchExtraLocaleEditDialog = (fields) => {
    const dialog = { ...this.state.extraLocaleEditDialog, ...fields };
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  /**
   * The LOCALE picker's input. Typing INVALIDATES any pinned pick (unless the
   * text is exactly the picked name) and schedules a debounced name search.
   * `include_unready`/`include_hidden` are sent because the 26/27 catalogue
   * templates are unpublished drafts a default search would hide.
   */
  handleExtraLocaleEpackSearchChange = (e) => {
    const text = e.target.value;
    const dialog = this.state.extraLocaleEditDialog;
    const keepPick = dialog.epack_id > 0 && text === dialog.resolvedEpackName;
    this.patchExtraLocaleEditDialog({
      epackSearchText: text,
      epack_id: keepPick ? dialog.epack_id : 0,
      resolvedEpackName: keepPick ? dialog.resolvedEpackName : "",
      epackSearchDone: false,
    });
    if (this.extraLocaleEpackResolveTimer) {
      clearTimeout(this.extraLocaleEpackResolveTimer);
      this.extraLocaleEpackResolveTimer = null;
    }
    const keyword = String(text || "").trim();
    if (keepPick || keyword === "") {
      this.patchExtraLocaleEditDialog({ epackSearchOptions: [], epackSearchLoading: false });
      return;
    }
    this.patchExtraLocaleEditDialog({ epackSearchLoading: true });
    this.extraLocaleEpackResolveTimer = setTimeout(() => {
      this.extraLocaleEpackResolveTimer = null;
      const requestedFor = keyword;
      const finish = (options) => {
        // Stale replies (the admin kept typing) must not win.
        if (String(this.state.extraLocaleEditDialog.epackSearchText || "").trim() !== requestedFor) {
          return;
        }
        this.patchExtraLocaleEditDialog({
          epackSearchOptions: options,
          epackSearchLoading: false,
          epackSearchDone: true,
        });
      };
      this.props.root.refs.API.request(
        "search_epack_by_keyword",
        { keyword: keyword, search_mode: "keyword", limit: 20, include_unready: 1, include_hidden: 1 },
        (res) => {
          const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
          finish(rows.map((r) => ({
            id: parseInt(r.id),
            name: r.name || "",
            school_name: r.school_name || r.school_name_eng || "",
          })));
        },
        () => finish([])
      );
    }, 400);
  }

  handleExtraLocaleEpackPick = (option) => {
    this.patchExtraLocaleEditDialog({
      epack_id: option.id,
      resolvedEpackName: option.name,
      epackSearchText: option.name,
      epackSearchOptions: [],
      epackSearchLoading: false,
      epackSearchDone: false,
    });
  }

  handleExtraLocaleEditEnabledChange = (e) => {
    const dialog = { ...this.state.extraLocaleEditDialog, is_enabled: e.currentTarget.checked };
    this.setState({ extraLocaleEditDialog: dialog });
    this.state.extraLocaleEditDialog = dialog;
  }

  /**
   * The manual FilePicker has finished a two-phase blob upload: the `file` row
   * exists and is confirmed. Pin its id into the dialog; the PATCH on Save is
   * what LINKS it to the catalog row.
   *
   * An upload the admin then cancels out of leaves an orphan `file` row (and
   * its blob) behind — the same trade every other FilePicker in the composer
   * makes, and cheaper than deleting on close, which would also delete the file
   * of an admin who closed the dialog by mistake.
   */
  handleExtraLocaleManualUploaded = (res) => {
    this.patchExtraLocaleEditDialog({
      manual_file_id: res && res.file_id ? parseInt(res.file_id) : 0,
      manual_path: res && res.path ? res.path : "",
      manual_display_name: res && res.display_name ? res.display_name : "",
    });
  }

  /** The picker's Delete: unlink here, and let it delete the `file` row. */
  handleExtraLocaleManualDeleted = () => {
    this.patchExtraLocaleEditDialog({
      manual_file_id: 0, manual_path: "", manual_display_name: "",
    });
  }

  /** {id: true} while that row's manual is uploading or detaching. */
  setExtraLocaleManualBusy = (id, busy) => {
    const next = { ...this.state.extraLocaleManualBusyIds };
    if (busy) {
      next[id] = true;
    } else {
      delete next[id];
    }
    this.setState({ extraLocaleManualBusyIds: next });
    this.state.extraLocaleManualBusyIds = next;
  }

  /**
   * Attach (or replace) a row's 教師參考手冊 straight from the list.
   *
   * Upload FIRST, link SECOND, and only then delete whatever was there before:
   * that order means a failure at any step leaves the row pointing at a manual
   * that still works. Deleting the old file first would, on a failed upload,
   * leave the template with nothing.
   *
   * The stale `file` row is deleted after a successful relink so the blob does
   * not linger unreferenced — but its failure is swallowed, because by then the
   * admin's actual request has already succeeded and a stray blob is not worth
   * an error toast.
   */
  handleExtraLocaleManualUpload = async (row, file) => {
    const id = row.id;
    if (!file || this.state.extraLocaleManualBusyIds[id]) {
      return;
    }
    // Re-key the picker so choosing the same file again re-fires onChange.
    this.setState({ extraLocaleManualPickerKey: this.state.extraLocaleManualPickerKey + 1 });
    this.state.extraLocaleManualPickerKey += 1;

    const problem = manualFileError(file, Localization.strings);
    if (problem) {
      this.props.root.addNotification(problem, false);
      return;
    }

    const previousFileId = parseInt(row.manual_file_id) > 0 ? parseInt(row.manual_file_id) : 0;
    this.setExtraLocaleManualBusy(id, true);
    try {
      const uploaded = await uploadManualPdf(this.props.root.refs.API, file);
      await new Promise((resolve, reject) => {
        this.props.root.refs.API.request(
          "admin_extra_locale_update",
          { id: id, manual_file_id: uploaded.file_id },
          resolve,
          (err) => reject(new Error(err?.body?.remarks || "")),
        );
      });
      if (previousFileId > 0 && previousFileId !== parseInt(uploaded.file_id)) {
        this.props.root.refs.API.request("delete_file", { file_id: previousFileId },
          () => {}, () => {});
      }
      this.props.root.addNotification(
        Localization.strings.EXTRA_LOCALE_MANUAL_UPLOADED || '教師參考手冊已上載', true);
      this.loadExtraLocaleCatalog();
    } catch (error) {
      console.log("manual upload failed", error);
      this.props.root.addNotification(
        (error && error.message)
          || Localization.strings.EXTRA_LOCALE_MANUAL_UPLOAD_FAILED || '上載失敗', false);
    } finally {
      this.setExtraLocaleManualBusy(id, false);
    }
  }

  openExtraLocaleManualDeleteDialog = (row) => {
    this.setState({ extraLocaleManualDeleteRow: row });
    this.state.extraLocaleManualDeleteRow = row;
  }

  closeExtraLocaleManualDeleteDialog = () => {
    this.setState({ extraLocaleManualDeleteRow: null });
    this.state.extraLocaleManualDeleteRow = null;
  }

  /**
   * Detach a row's manual: PATCH manual_file_id to 0, then delete the `file`
   * row it pointed at.
   *
   * The TEMPLATE is untouched — this removes the PDF only, and the Extra LOCALE
   * tab stops drawing the icon for every school holding it (the tab keys on the
   * joined file row, so it goes quiet on its own).
   */
  handleExtraLocaleManualDeleteConfirm = async () => {
    const row = this.state.extraLocaleManualDeleteRow;
    if (!row || this.state.extraLocaleManualBusyIds[row.id]) {
      return;
    }
    const fileId = parseInt(row.manual_file_id) > 0 ? parseInt(row.manual_file_id) : 0;
    this.setExtraLocaleManualBusy(row.id, true);
    try {
      await new Promise((resolve, reject) => {
        this.props.root.refs.API.request(
          "admin_extra_locale_update",
          // 0 is the detach; apiRoutes keeps a numeric 0 in the body, and the
          // route's `v is not None` filter carries it through to the UPDATE.
          { id: row.id, manual_file_id: 0 },
          resolve,
          (err) => reject(new Error(err?.body?.remarks || "")),
        );
      });
      if (fileId > 0) {
        this.props.root.refs.API.request("delete_file", { file_id: fileId },
          () => {}, () => {});
      }
      this.closeExtraLocaleManualDeleteDialog();
      this.props.root.addNotification(
        Localization.strings.EXTRA_LOCALE_MANUAL_DELETED || '教師參考手冊已刪除', true);
      this.loadExtraLocaleCatalog();
    } catch (error) {
      console.log("manual detach failed", error);
      this.props.root.addNotification(
        (error && error.message)
          || Localization.strings.EXTRA_LOCALE_MANUAL_DELETE_FAILED || '刪除失敗', false);
    } finally {
      this.setExtraLocaleManualBusy(row.id, false);
    }
  }

  /**
   * Download the manual attached to a catalog row — the admin list's own copy
   * of what a school sees on its Extra LOCALE tab, so the attachment can be
   * verified without holding the template.
   */
  handleExtraLocaleManualDownload = async (row) => {
    const how = await downloadManual({
      path: row.manual_path,
      displayName: row.name || row.manual_display_name,
      suffix: Localization.strings.EXTRA_LOCALE_MANUAL_FILE_SUFFIX || '教師參考手冊',
    });
    if (how === 'none') {
      this.props.root.addNotification(
        Localization.strings.EXTRA_LOCALE_MANUAL_DOWNLOAD_FAILED
          || '無法下載教師參考手冊', false);
    }
  }

  handleExtraLocaleEditSave = () => {
    const dialog = this.state.extraLocaleEditDialog;
    if (dialog.loading) {
      return;
    }
    const epackId = parseInt(dialog.epack_id);
    if (isNaN(epackId) || epackId <= 0) {
      this.props.root.addNotification(Localization.strings.EXTRA_LOCALE_EPACK_REQUIRED || '請從清單選擇 LOCALE', false);
      return;
    }
    const payload = {
      category: dialog.category.trim(),
      remarks: dialog.remarks.trim(),
      epack_id: epackId,
      category_index: dialog.category_index !== "" && !isNaN(Number(dialog.category_index)) ? parseInt(dialog.category_index) : 0,
      display_index: dialog.display_index !== "" && !isNaN(Number(dialog.display_index)) ? parseInt(dialog.display_index) : 0,
      is_enabled: dialog.is_enabled ? 1 : 0,
      // ALWAYS sent, 0 included: omitting it would make PATCH's partial
      // semantics keep the old link, so removing a manual would silently not
      // save. 0 is the detach on both surfaces.
      manual_file_id: parseInt(dialog.manual_file_id) > 0 ? parseInt(dialog.manual_file_id) : 0,
    };
    const loadingDialog = { ...dialog, loading: true };
    this.setState({ extraLocaleEditDialog: loadingDialog });
    this.state.extraLocaleEditDialog = loadingDialog;

    const done = () => {
      const reset = createExtraLocaleEditDialogState();
      this.setState({ extraLocaleEditDialog: reset });
      this.state.extraLocaleEditDialog = reset;
      this.loadExtraLocaleCatalog();
    };
    const failed = (err) => {
      const back = { ...this.state.extraLocaleEditDialog, loading: false };
      this.setState({ extraLocaleEditDialog: back });
      this.state.extraLocaleEditDialog = back;
      this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_SAVE_FAILED || '儲存範本失敗', false);
    };

    if (dialog.row) {
      this.props.root.refs.API.request("admin_extra_locale_update", { id: dialog.row.id, ...payload }, done, failed);
    } else {
      this.props.root.refs.API.request("admin_extra_locale_create", payload, done, failed);
    }
  }

  // The list's 開放選擇 switch — one-field PATCH, optimistic, reverted on failure.
  handleExtraLocaleEnabledToggle = (row, checked) => {
    const id = row.id;
    if (this.state.extraLocaleTogglingIds[id]) {
      return;
    }
    const previous = parseInt(row.is_enabled) === 1;
    const apply = (value) => {
      const rows = this.state.extraLocaleRows.map((r) => (r.id === id ? { ...r, is_enabled: value ? 1 : 0 } : r));
      this.setState({ extraLocaleRows: rows });
      this.state.extraLocaleRows = rows;
    };
    const setToggling = (value) => {
      const toggling = { ...this.state.extraLocaleTogglingIds };
      if (value) { toggling[id] = true; } else { delete toggling[id]; }
      this.setState({ extraLocaleTogglingIds: toggling });
      this.state.extraLocaleTogglingIds = toggling;
    };
    apply(checked);
    setToggling(true);
    this.props.root.refs.API.request(
      "admin_extra_locale_update",
      { id: id, is_enabled: checked ? 1 : 0 },
      () => { setToggling(false); },
      (err) => {
        apply(previous);
        setToggling(false);
        this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_SAVE_FAILED || '儲存範本失敗', false);
      }
    );
  }

  openExtraLocaleDeleteDialog = (row) => {
    this.setState({ extraLocaleDeleteRow: row });
    this.state.extraLocaleDeleteRow = row;
  }

  closeExtraLocaleDeleteDialog = () => {
    if (this.state.extraLocaleDeleteLoading) {
      return;
    }
    this.setState({ extraLocaleDeleteRow: null });
    this.state.extraLocaleDeleteRow = null;
  }

  handleExtraLocaleDeleteConfirm = () => {
    const row = this.state.extraLocaleDeleteRow;
    if (!row || this.state.extraLocaleDeleteLoading) {
      return;
    }
    this.setState({ extraLocaleDeleteLoading: true });
    this.state.extraLocaleDeleteLoading = true;
    this.props.root.refs.API.request(
      "admin_extra_locale_delete",
      { id: row.id },
      () => {
        this.setState({ extraLocaleDeleteLoading: false, extraLocaleDeleteRow: null });
        this.state.extraLocaleDeleteLoading = false;
        this.state.extraLocaleDeleteRow = null;
        this.loadExtraLocaleCatalog();
      },
      (err) => {
        this.setState({ extraLocaleDeleteLoading: false });
        this.state.extraLocaleDeleteLoading = false;
        this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_DELETE_FAILED || '刪除範本失敗', false);
      }
    );
  }

  /**
   * Hand a built CSV string to the browser as a download.
   *
   * The "\uFEFF" BOM is what makes Excel decode the Chinese as UTF-8 — without
   * it Excel reads the file in its locale codepage and mangles every name.
   * Shared by both Extra Locale exports so they cannot drift apart on it.
   */
  downloadCsvFile(csv, fileName) {
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export the per-school selection sheet:
   *   School Name, Selected Extra LOCALEs / Maximum, Selected Extra LOCALE Names
   * Server aggregates (schoolSummary); this builds the CSV and hands it to the
   * browser.
   */
  handleExtraLocaleExport = () => {
    if (this.state.extraLocaleExporting) {
      return;
    }
    this.setState({ extraLocaleExporting: true });
    this.state.extraLocaleExporting = true;
    const done = (ok, rows, err) => {
      this.setState({ extraLocaleExporting: false });
      this.state.extraLocaleExporting = false;
      if (!ok) {
        this.props.root.addNotification(err?.body?.remarks || Localization.strings.EXTRA_LOCALE_EXPORT_FAILED || '匯出失敗', false);
        return;
      }
      const now = new Date();
      const stamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('');
      this.downloadCsvFile(buildExtraLocaleCsv(rows), `extra_locale_selections_${stamp}.csv`);
    };
    this.props.root.refs.API.request(
      "admin_extra_locale_school_summary",
      {},
      (res) => done(true, Array.isArray(res?.body?.result) ? res.body.result : []),
      (err) => done(false, [], err)
    );
  }

  loadDashboard = (period) => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    const next = period || this.state.dashboardPeriod || 'day';
    const requestId = (this.dashboardRequestId || 0) + 1;
    this.dashboardRequestId = requestId;
    this.setState({ dashboardLoading: true, dashboardPeriod: next, dashboardError: false });
    this.state.dashboardLoading = true;
    this.state.dashboardPeriod = next;
    this.state.dashboardError = false;
    this.props.root.refs.API.request(
      "admin_platform_dashboard",
      { period: next },
      (res) => {
        if (this.dashboardRequestId !== requestId) {
          return;
        }
        const data = res?.body?.result && typeof res.body.result === 'object' && !Array.isArray(res.body.result)
          ? res.body.result
          : null;
        this.setState({ dashboard: data, dashboardLoading: false, dashboardError: !data });
        this.state.dashboard = data;
        this.state.dashboardLoading = false;
        this.state.dashboardError = !data;
        if (!data) {
          this.props.root.addNotification(Localization.strings.DASHBOARD_LOAD_FAILED || '無法載入數據概覽', false);
        }
      },
      (err) => {
        if (this.dashboardRequestId !== requestId) {
          return;
        }
        console.log("admin_platform_dashboard failed", err);
        this.setState({ dashboardLoading: false, dashboardError: true });
        this.state.dashboardLoading = false;
        this.state.dashboardError = true;
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.DASHBOARD_LOAD_FAILED || '無法載入數據概覽',
          false,
        );
      },
    );
  }

  handleDashboardPeriodChange = (value) => {
    this.loadDashboard(value);
  }

  dashboardTab() {
    const strings = Localization.strings;
    const data = this.state.dashboard;
    const period = this.state.dashboardPeriod || 'day';
    // A period switch makes the payload stale immediately. A refresh of the
    // same period keeps the numbers up and only spins the button.
    const stale = !data || data.period !== period;
    const metric = (key) => (data && data[key]) ? data[key] : { total: 0, previous: 0, series: [] };
    const loginsApp = metric('logins_app');
    const loginsDevices = metric('logins_devices');
    const loginsComposer = metric('logins_composer');
    return (
      <Box
        key="platform-dashboard"
        className="admin-page-transition"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Stack gap={4}>
              <Text fw={600}>{strings.DASHBOARD_TITLE || '數據概覽'}</Text>
              <Text size="sm" c="dimmed">{strings.DASHBOARD_INTRO || '全平台統計，以香港時間計算。'}</Text>
            </Stack>
            <Group gap="sm">
              <SegmentedControl
                value={period}
                onChange={this.handleDashboardPeriodChange}
                data={[
                  { value: 'day', label: strings.DASHBOARD_DAY || '今天' },
                  { value: 'days_7', label: strings.DASHBOARD_DAYS_7 || '過去 7 天' },
                  { value: 'days_30', label: strings.DASHBOARD_DAYS_30 || '過去 30 天' },
                ]}
              />
              <Tooltip label={strings.DASHBOARD_REFRESH || '重新整理'}>
                <ActionIcon variant="light" size="lg" onClick={() => this.loadDashboard(period)} loading={this.state.dashboardLoading}>
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {stale ? (
            this.state.dashboardError && !this.state.dashboardLoading ? (
              <Alert color="red" variant="light">
                {strings.DASHBOARD_LOAD_FAILED || '無法載入數據概覽'}
              </Alert>
            ) : (
              <Group justify="center" py="xl">
                <Loader />
              </Group>
            )
          ) : (
            <>
              <Text size="sm" c="dimmed">
                {(strings.DASHBOARD_RANGE || '{0}')
                  .replace('{0}', dashboardRangeText(data && data.from, data && data.to))}
                {'  ·  '}
                {(strings.DASHBOARD_PREVIOUS || '對上一個同期 {0}')
                  .replace('{0}', dashboardRangeText(data && data.previous_from, data && data.previous_to))}
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                <DashboardCard
                  title={strings.DASHBOARD_LOGINS_APP || '用戶登入（App）'}
                  hint={strings.DASHBOARD_LOGINS_APP_HINT || '在 App 登入的不重複帳戶，以及已輸入學校金鑰的裝置。'}
                  total={loginsApp.total}
                  previous={loginsApp.previous}
                  series={loginsApp.series}
                  icon={<IconDeviceMobile size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_LOGINS_DEVICES || '匿名 App 使用'}
                  hint={strings.DASHBOARD_LOGINS_DEVICES_HINT || '裝置處於未登入狀態，以不重複裝置進行計算'}
                  total={loginsDevices.total}
                  previous={loginsDevices.previous}
                  series={loginsDevices.series}
                  icon={<IconDevices size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_LOGINS_COMPOSER || '用戶登入（Composer）'}
                  hint={strings.DASHBOARD_LOGINS_COMPOSER_HINT || '在 Composer 登入的不重複帳戶。'}
                  total={loginsComposer.total}
                  previous={loginsComposer.previous}
                  series={loginsComposer.series}
                  icon={<IconLogin size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_NEW_USERS || '新用戶'}
                  hint={strings.DASHBOARD_NEW_USERS_HINT || '新建立的帳戶。'}
                  {...metric('new_users')}
                  icon={<IconUserPlus size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_LOCALES_CREATED || '新建 LOCALE'}
                  hint={strings.DASHBOARD_LOCALES_CREATED_HINT || '新建的 LOCALE，複製不計在內。'}
                  {...metric('locales_created')}
                  icon={<IconMap size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_LOCALES_DELETED || '刪除 LOCALE'}
                  hint={strings.DASHBOARD_LOCALES_DELETED_HINT || '已刪除的 LOCALE。'}
                  {...metric('locales_deleted')}
                  icon={<IconTrash size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_LOCALES_DUPLICATED || '複製 LOCALE'}
                  hint={strings.DASHBOARD_LOCALES_DUPLICATED_HINT || '複製出來的 LOCALE。'}
                  {...metric('locales_duplicated')}
                  icon={<IconCopy size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_EXTRA_SELECTED || '已選預製教材'}
                  hint={strings.DASHBOARD_EXTRA_SELECTED_HINT || '學校選定的預製教材。'}
                  {...metric('extra_locales_selected')}
                  icon={<IconBooks size={18} />}
                />
                <DashboardCard
                  title={strings.DASHBOARD_MODELS || '已生成 3D 模型'}
                  hint={strings.DASHBOARD_MODELS_HINT || '已完成儲存的 3D 模型。'}
                  {...metric('models_generated')}
                  icon={<IconCube size={18} />}
                />
              </SimpleGrid>
              <DashboardNotes />
            </>
          )}
        </Stack>
      </Box>
    );
  }

  extraLocaleTab() {
    const view = this.state.extraLocaleView;
    return (
      <Box
        key="extra-locale"
        className="admin-page-transition"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <SegmentedControl
            value={view}
            onChange={this.handleExtraLocaleViewChange}
            data={[
              { value: 'catalog', label: Localization.strings.EXTRA_LOCALE_ADMIN_TAB || '範本管理' },
              { value: 'history', label: Localization.strings.EXTRA_LOCALE_ADMIN_HISTORY || '選擇紀錄' },
            ]}
            w={320}
          />
          {view === 'catalog' ? this.renderExtraLocaleCatalogView() : this.renderExtraLocaleHistoryView()}
        </Stack>
      </Box>
    );
  }

  renderExtraLocaleCatalogView() {
    const rows = this.state.extraLocaleRows;
    return (
      <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
        <Group gap="sm" wrap="wrap" align="center" justify="space-between">
          <Text size="sm" c="dimmed">
            {Localization.strings.EXTRA_LOCALE_ADMIN_HINT || '此列表是所有學校可選的預製教材範本。關閉「開放選擇」後，學校將不會在選單中看到該範本；已有的選擇紀錄不受影響。'}
          </Text>
          <Group gap="sm">
            <Tooltip label="重新整理">
              <ActionIcon variant="light" size="lg" onClick={this.loadExtraLocaleCatalog}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button onClick={this.openExtraLocaleCreateDialog} leftSection={<IconPlus size={16} />} variant="filled" color="teal">
              {Localization.strings.EXTRA_LOCALE_ADMIN_CREATE || '新增範本'}
            </Button>
            <Button
              onClick={this.handleExtraLocaleExport}
              leftSection={<IconDownload size={16} />}
              variant="light"
              loading={this.state.extraLocaleExporting}
            >
              {Localization.strings.EXTRA_LOCALE_EXPORT_CSV || '匯出 CSV'}
            </Button>
          </Group>
        </Group>

        {this.state.extraLocaleLoading ? (
          <TableLoadingCard icon={<IconBooks {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={1290}>
            <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 90 }}>{Localization.strings.EXTRA_LOCALE_CATEGORY_INDEX || '類別排序'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_CATEGORY || '類別'}</Table.Th>
                  <Table.Th style={{ width: 90 }}>{Localization.strings.EXTRA_LOCALE_DISPLAY_INDEX || '範本排序'}</Table.Th>
                  <Table.Th style={{ width: 110 }}>{Localization.strings.EXTRA_LOCALE_EPACK_ID || 'LOCALE 編號'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_EPACK_NAME || 'LOCALE 名稱'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_REMARKS || '備註'}</Table.Th>
                  <Table.Th style={{ width: 130 }}>{Localization.strings.EXTRA_LOCALE_MANUAL_COLUMN || '教師參考手冊'}</Table.Th>
                  <Table.Th style={{ width: 100 }}>{Localization.strings.EXTRA_LOCALE_SELECTION_COUNT || '已選次數'}</Table.Th>
                  <Table.Th style={{ width: 110 }}>{Localization.strings.EXTRA_LOCALE_ENABLED || '開放選擇'}</Table.Th>
                  <Table.Th style={{ width: 100 }}>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>{row.category_index}</Table.Td>
                    <Table.Td>{row.category || '-'}</Table.Td>
                    <Table.Td>{row.display_index}</Table.Td>
                    <Table.Td style={{ fontFamily: 'monospace' }}>{row.epack_id && parseInt(row.epack_id) > 0 ? row.epack_id : '-'}</Table.Td>
                    <Table.Td>
                      {row.name != null && row.name !== '' ? row.name : (
                        // LEFT JOIN came back empty: the linked epack no longer
                        // exists. The row cannot be offered — flag it for repair.
                        <Badge color="red" variant="light">
                          {Localization.strings.EXTRA_LOCALE_EPACK_DANGLING || 'LOCALE 已刪除'}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.remarks || '-'}</Table.Td>
                    <Table.Td>
                      {/* manual_path, not manual_file_id: the id can outlive the
                          `file` row it points at (a replace deletes the old
                          one), and the server LEFT JOINs, so an empty path IS
                          the "no manual" answer — for this column and for the
                          PDF icon on every school's Extra LOCALE tab. */}
                      <Group gap={2} wrap="nowrap">
                        {row.manual_path && (
                          <Tooltip label={Localization.strings.EXTRA_LOCALE_MANUAL_DOWNLOAD || '下載教師參考手冊'}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              disabled={!!this.state.extraLocaleManualBusyIds[row.id]}
                              onClick={() => this.handleExtraLocaleManualDownload(row)}
                            >
                              <IconFileTypePdf size={18} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <FileButton
                          key={`manual-${row.id}-${this.state.extraLocaleManualPickerKey}`}
                          accept="application/pdf,.pdf"
                          onChange={(file) => this.handleExtraLocaleManualUpload(row, file)}
                        >
                          {(props) => (
                            <Tooltip label={row.manual_path
                              ? (Localization.strings.EXTRA_LOCALE_MANUAL_REPLACE || '更換教師參考手冊')
                              : (Localization.strings.EXTRA_LOCALE_MANUAL_UPLOAD || '上載教師參考手冊')}>
                              <ActionIcon
                                {...props}
                                variant="subtle"
                                color="brand"
                                loading={!!this.state.extraLocaleManualBusyIds[row.id]}
                              >
                                <IconFileUpload size={18} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </FileButton>
                        {row.manual_path && (
                          <Tooltip label={Localization.strings.EXTRA_LOCALE_MANUAL_DELETE || '刪除教師參考手冊'}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              disabled={!!this.state.extraLocaleManualBusyIds[row.id]}
                              onClick={() => this.openExtraLocaleManualDeleteDialog(row)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {parseInt(row.selection_count) > 0 ? (
                        <Badge variant="light">{row.selection_count}</Badge>
                      ) : '-'}
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={parseInt(row.is_enabled) === 1}
                        disabled={!!this.state.extraLocaleTogglingIds[row.id]}
                        onChange={(e) => this.handleExtraLocaleEnabledToggle(row, e.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label={Localization.strings.EXTRA_LOCALE_ADMIN_EDIT || '編輯範本'}>
                          <ActionIcon variant="subtle" color="brand" onClick={() => this.openExtraLocaleEditDialog(row)}>
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={Localization.strings.DELETE || '刪除'}>
                          <ActionIcon variant="subtle" color="red" onClick={() => this.openExtraLocaleDeleteDialog(row)}>
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && this.state.hasExtraLocaleLoaded && (
                  <Table.Tr>
                    <Table.Td colSpan={10} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      {Localization.strings.EXTRA_LOCALE_ADMIN_EMPTY || '尚未新增任何範本'}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  renderExtraLocaleHistoryView() {
    const rows = this.state.extraLocaleHistoryRows;
    // Both inputs carry min/max so the native picker steers away from an
    // inverted range, but those are advisory (a typed value ignores them), so
    // the same check decides the inline message and gates the export button.
    const rangeError = selectionRangeError(this.state.extraLocaleHistoryFrom,
                                           this.state.extraLocaleHistoryTo);
    return (
      <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
        {/*
          Two clusters, each wrapping as a UNIT: searching the table on the
          left, the CSV export on the right. Nested Groups rather than one flat
          row because the flat version dropped the export BUTTON onto the next
          line by itself, away from the two date fields it acts on.
        */}
        <Group gap="sm" wrap="wrap" align="flex-end" justify="space-between">
          <Group gap="sm" align="flex-end" wrap="nowrap" style={{ flex: 1, minWidth: 280 }}>
            <TextInput
              style={{ flex: 1, minWidth: 180 }}
              value={this.state.extraLocaleHistorySearchText}
              onChange={this.extraLocaleHistorySearchTextChanged}
              onKeyDown={this.handleExtraLocaleHistorySearchEnterPressed}
              placeholder={Localization.strings.EXTRA_LOCALE_SEARCH_PLACEHOLDER || '搜尋範本、學校或用戶...'}
              leftSection={<IconSearch size={16} />}
            />
            <Button onClick={this.loadExtraLocaleHistory} leftSection={<IconSearch size={16} />}>
              搜尋
            </Button>
          </Group>
          <Group gap="sm" align="flex-end" wrap="nowrap">
          {/*
            Labelled as the EXPORT range, not a table filter: it narrows the CSV
            only, and the list above keeps showing the newest records. Pick the
            start and end dates first, then export — both ends are INCLUSIVE, so
            the same date twice exports that whole day, and the default is
            yesterday -> today ("the last day").

            type="date" rather than a Mantine DatePickerInput: @mantine/dates is
            not a dependency of this app, and the native control already emits
            the bare 'YYYY-MM-DD' that both the filter and the route want. The
            same choice the subscription date fields above make.
          */}
          <TextInput
            type="date"
            w={165}
            label={Localization.strings.EXTRA_LOCALE_EXPORT_FROM || '匯出開始日期'}
            value={this.state.extraLocaleHistoryFrom}
            onChange={this.handleExtraLocaleHistoryFromChange}
            max={this.state.extraLocaleHistoryTo || undefined}
            error={rangeError !== ""}
          />
          <TextInput
            type="date"
            w={165}
            label={Localization.strings.EXTRA_LOCALE_EXPORT_TO || '匯出結束日期'}
            value={this.state.extraLocaleHistoryTo}
            onChange={this.handleExtraLocaleHistoryToChange}
            min={this.state.extraLocaleHistoryFrom || undefined}
            error={rangeError !== ""}
          />
          <Button
            onClick={this.handleExtraLocaleHistoryExport}
            leftSection={<IconDownload size={16} />}
            variant="light"
            loading={this.state.extraLocaleHistoryExporting}
            disabled={rangeError !== ""}
          >
            {Localization.strings.EXTRA_LOCALE_EXPORT_CSV || '匯出 CSV'}
          </Button>
          </Group>
        </Group>

        {rangeError !== "" && (
          <Text size="sm" c="red">
            {Localization.strings[rangeError] || '匯出日期範圍不正確。'}
          </Text>
        )}

        {this.state.extraLocaleHistoryLoading ? (
          <TableLoadingCard icon={<IconHistory {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={1080}>
            <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 170 }}>{Localization.strings.EXTRA_LOCALE_SELECTED_AT || '選擇日期'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_SCHOOL || '學校'}</Table.Th>
                  <Table.Th style={{ width: 170 }}>{Localization.strings.EXTRA_LOCALE_USER || '用戶'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_CATEGORY || '類別'}</Table.Th>
                  <Table.Th>{Localization.strings.EXTRA_LOCALE_NAME || '名稱'}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {/*
                  KEY IS id + school_id, NOT id. One selection is ONE
                  `subscription_epack` row, and this page is school-shaped: the
                  server fans each pick out through `school_subscription` to one
                  row per school on the plan, so `row.id` (the subscription_epack
                  id) REPEATS — 29 rows sharing a key for a single pick on QEF
                  2024 Sec. extra_locale_service.list_selection_history documents
                  exactly that ("`id` ... REPEATS across those rows (the composer
                  uses it as a React key)"); this client had never been updated to
                  match. Duplicate keys make React reconcile the wrong rows on the
                  next re-render of the same list — which the keyword search box
                  triggers — dropping or mis-updating them. The pair is unique:
                  the query is DISTINCT and keeps one row per (school, template).
                */}
                {rows.map((row) => (
                  <Table.Tr key={`${row.id}-${row.school_id}`}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{this.formatDateTime(row.time_create)}</Table.Td>
                    <Table.Td>{row.school_name || row.school_name_eng || ('#' + row.school_id)}</Table.Td>
                    <Table.Td>{row.user_display_name || row.username || ('#' + row.user_id)}</Table.Td>
                    <Table.Td>{row.category || '-'}</Table.Td>
                    <Table.Td>{row.code ? (row.code + '：' + row.name) : row.name}</Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && this.state.hasExtraLocaleHistoryLoaded && (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      {Localization.strings.EXTRA_LOCALE_HISTORY_EMPTY || '沒有選擇紀錄'}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  /**
   * Extra Locale > 通知電郵設定 — who receives the scheduled Selection History
   * mail, and which of the two schedules run.
   *
   * The page states the SCHEDULE it is configuring in plain words at the top,
   * because nothing else on screen says it: the times live in an ACA cron
   * expression, and an admin editing a recipient list has no other way to know
   * whether the mail lands at 09:00 or at midnight, or that Monday brings two.
   *
   * `smtp_configured` gets its own warning. A perfectly-filled page delivers
   * nothing when the container has no SMTP secret, and that failure is
   * invisible from here otherwise — the settings save fine, the job runs, and
   * the mail never arrives.
   */
  extraLocaleReportTab() {
    const settings = this.state.reportSettings;
    const loading = this.state.reportLoading;
    // The SERVER's parse of the stored list, not the textarea's contents: what
    // the job will actually mail, which is the number worth showing.
    const recipients = (settings && Array.isArray(settings.recipients))
      ? settings.recipients : [];
    const smtpMissing = !!settings && settings.smtp_configured === false;
    const busy = this.state.reportSaving || !!this.state.reportSendingKind;
    return (
      <Box
        key="extra-locale-report"
        className="admin-page-transition"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0, maxWidth: 760 }}>
          <Group justify="space-between" align="center">
            <Text fw={600} fz="lg">
              {Localization.strings.EXTRA_LOCALE_REPORT_TAB || '通知電郵設定'}
            </Text>
            <Tooltip label={Localization.strings.REFRESH || '重新整理'}>
              <ActionIcon variant="light" size="lg" onClick={this.loadReportSettings}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Alert icon={<IconClock size={18} />} color="blue" variant="light">
            {Localization.strings.EXTRA_LOCALE_REPORT_SCHEDULE_HINT
              || '系統會於每日香港時間上午 9:00 自動將「前一日」的選擇紀錄以電郵寄出；'
                 + '逢星期一上午 9:00 另外加寄「上一週（星期一至星期日）」的選擇紀錄。'
                 + '每封電郵附有與「選擇紀錄」頁面相同的 CSV 檔。'}
          </Alert>

          {smtpMissing && (
            <Alert icon={<IconAlertTriangle size={18} />} color="red" variant="light">
              {Localization.strings.EXTRA_LOCALE_REPORT_SMTP_MISSING
                || '伺服器尚未設定 SMTP 帳戶，即使填寫收件地址亦不會發出電郵。請聯絡系統管理員。'}
            </Alert>
          )}

          {loading ? (
            <TableLoadingCard icon={<IconMail {...LOADING_CARD_ICON_PROPS} />} />
          ) : (
            <>
              <Textarea
                label={Localization.strings.EXTRA_LOCALE_REPORT_RECIPIENTS || '收件電郵地址'}
                description={Localization.strings.EXTRA_LOCALE_REPORT_RECIPIENTS_HELP
                  || '每行一個地址，亦可用逗號或分號分隔。留空即停止發送。'}
                placeholder={"someone@example.edu.hk\nanother@example.edu.hk"}
                autosize
                minRows={4}
                maxRows={10}
                value={this.state.reportRecipientsText}
                onChange={this.handleReportRecipientsChange}
              />
              {/*
                The SERVER's count, so an admin can see at a glance that the six
                lines they pasted resolved to six addresses (and not to five
                plus a display name that was refused).
              */}
              <Text size="sm" c="dimmed">
                {(Localization.strings.EXTRA_LOCALE_REPORT_RECIPIENT_COUNT
                  || '目前已儲存 {0} 個收件地址')
                  .replace('{0}', String(recipients.length))}
              </Text>

              <Divider />

              <Switch
                checked={this.state.reportDailyEnabled}
                onChange={this.handleReportSwitchChange('reportDailyEnabled')}
                label={Localization.strings.EXTRA_LOCALE_REPORT_DAILY
                  || '每日發送（前一日的選擇紀錄）'}
              />
              <Switch
                checked={this.state.reportWeeklyEnabled}
                onChange={this.handleReportSwitchChange('reportWeeklyEnabled')}
                label={Localization.strings.EXTRA_LOCALE_REPORT_WEEKLY
                  || '每星期一發送（上一週的選擇紀錄）'}
              />
              <Switch
                checked={this.state.reportSendWhenEmpty}
                onChange={this.handleReportSwitchChange('reportSendWhenEmpty')}
                label={Localization.strings.EXTRA_LOCALE_REPORT_SEND_WHEN_EMPTY
                  || '沒有紀錄時仍然發送'}
                description={Localization.strings.EXTRA_LOCALE_REPORT_SEND_WHEN_EMPTY_HELP
                  || '關閉後，該日若沒有任何選擇紀錄便不會發出電郵——收不到郵件時將無法分辨是「沒有紀錄」還是「發送失敗」。'}
              />

              <Divider />

              <Group gap="sm" wrap="wrap">
                <Button
                  onClick={this.handleReportSave}
                  loading={this.state.reportSaving}
                  disabled={!this.state.reportDirty || !!this.state.reportSendingKind}
                  leftSection={<IconCheck size={16} />}
                >
                  {Localization.strings.SAVE || '儲存'}
                </Button>
                {REPORT_SEND_KINDS.map((kind) => (
                  <Button
                    key={kind}
                    variant="light"
                    leftSection={<IconMail size={16} />}
                    loading={this.state.reportSendingKind === kind}
                    disabled={busy && this.state.reportSendingKind !== kind}
                    onClick={this.handleReportSendNow(kind)}
                  >
                    {kind === 'daily'
                      ? (Localization.strings.EXTRA_LOCALE_REPORT_SEND_DAILY_NOW || '立即發送每日報告')
                      : (Localization.strings.EXTRA_LOCALE_REPORT_SEND_WEEKLY_NOW || '立即發送每週報告')}
                  </Button>
                ))}
              </Group>
              <Text size="sm" c="dimmed">
                {Localization.strings.EXTRA_LOCALE_REPORT_SEND_NOW_HELP
                  || '「立即發送」會依照上述已儲存的收件名單，寄出與排程相同的日期範圍，用作測試。'}
              </Text>
            </>
          )}
        </Stack>
      </Box>
    );
  }

  renderExtraLocaleEditDialog() {
    const dialog = this.state.extraLocaleEditDialog;
    return (
      <Modal
        opened={dialog.open}
        onClose={this.closeExtraLocaleEditDialog}
        closeOnClickOutside={!dialog.loading}
        closeOnEscape={!dialog.loading}
        withCloseButton={!dialog.loading}
        title={
          <Text fw={600}>
            {dialog.row
              ? (Localization.strings.EXTRA_LOCALE_ADMIN_EDIT || '編輯範本')
              : (Localization.strings.EXTRA_LOCALE_ADMIN_CREATE || '新增範本')}
          </Text>
        }
        size="md"
        centered
        zIndex={2000}
      >
        <Stack gap="md">
          <Box>
            <TextInput
              label={Localization.strings.EXTRA_LOCALE_EPACK_PICKER || 'LOCALE'}
              required
              description={Localization.strings.EXTRA_LOCALE_EPACK_PICKER_HINT || '輸入 LOCALE 名稱關鍵字，然後從清單中選擇'}
              placeholder={Localization.strings.EXTRA_LOCALE_EPACK_PICKER_PLACEHOLDER || '搜尋 LOCALE 名稱...'}
              value={dialog.epackSearchText}
              onChange={this.handleExtraLocaleEpackSearchChange}
              disabled={dialog.loading}
              leftSection={<IconSearch size={16} />}
              rightSection={dialog.epackSearchLoading ? <Loader size="xs" /> : undefined}
            />
            {dialog.epackSearchOptions.length > 0 && (
              <Paper withBorder mt={4} p={4} style={{ maxHeight: 220, overflowY: 'auto' }}>
                <Stack gap={0}>
                  {dialog.epackSearchOptions.map((option) => (
                    <UnstyledButton
                      key={option.id}
                      onClick={() => this.handleExtraLocaleEpackPick(option)}
                      px={8}
                      py={6}
                      style={{ borderRadius: 4 }}
                      className="mantine-focus-auto"
                    >
                      <Text size="sm">{option.name}</Text>
                      <Text size="xs" c="dimmed">
                        #{option.id}{option.school_name ? ` · ${option.school_name}` : ''}
                      </Text>
                    </UnstyledButton>
                  ))}
                </Stack>
              </Paper>
            )}
            {dialog.epackSearchDone && dialog.epackSearchOptions.length === 0
              && dialog.epack_id <= 0 && String(dialog.epackSearchText || '').trim() !== '' && (
              <Text size="sm" c="dimmed" mt={4}>
                {Localization.strings.EXTRA_LOCALE_EPACK_NO_RESULTS || '沒有符合的 LOCALE'}
              </Text>
            )}
            {dialog.epack_id > 0 && (
              <Text size="sm" c="teal" mt={4}>
                {(Localization.strings.EXTRA_LOCALE_EPACK_SELECTED || '已選擇')}: {dialog.resolvedEpackName} (#{dialog.epack_id})
              </Text>
            )}
          </Box>
          <TextInput
            label={Localization.strings.EXTRA_LOCALE_CATEGORY || '類別'}
            description={Localization.strings.EXTRA_LOCALE_CATEGORY_HINT || '例：小學 - 常識科（人文科及科學科）'}
            value={dialog.category}
            onChange={this.handleExtraLocaleEditFieldChange('category')}
            disabled={dialog.loading}
          />
          <Group grow>
            <TextInput
              label={Localization.strings.EXTRA_LOCALE_CATEGORY_INDEX || '類別排序'}
              description={Localization.strings.EXTRA_LOCALE_CATEGORY_INDEX_HINT || '同一類別的範本請使用相同數值'}
              value={dialog.category_index}
              onChange={this.handleExtraLocaleEditFieldChange('category_index')}
              disabled={dialog.loading}
            />
            <TextInput
              label={Localization.strings.EXTRA_LOCALE_DISPLAY_INDEX || '範本排序'}
              description={Localization.strings.EXTRA_LOCALE_DISPLAY_INDEX_HINT || '類別內的顯示次序'}
              value={dialog.display_index}
              onChange={this.handleExtraLocaleEditFieldChange('display_index')}
              disabled={dialog.loading}
            />
          </Group>
          <Textarea
            label={Localization.strings.EXTRA_LOCALE_REMARKS || '備註'}
            value={dialog.remarks}
            onChange={this.handleExtraLocaleEditFieldChange('remarks')}
            disabled={dialog.loading}
            autosize
            minRows={2}
          />
          {/*
            教師參考手冊 (PDF). The SAME two-phase blob upload every other file in
            the composer uses — request_upload -> PUT to the SAS -> confirm_upload
            — so there is one uploader, one storage account and one delete path.

            NO `epackID`: the upload is deliberately filed under epack_id 0, and
            that single choice is what keeps the manual out of ex_flutterv1's
            download manifest (get_files_by_epack_id filters on the locale's id),
            out of every school's storage quota (those sums JOIN `epack`), and
            out of a duplicated locale. See DB/20260910_extra_locale_manual.sql.

            `key` remounts the picker per catalog row: its constructor seeds
            fileID/filePath from props, so reopening the dialog on a different
            template would otherwise show the previous one's manual until a
            prop-diff caught up.
          */}
          <FilePicker
            key={`extra-locale-manual-${dialog.row ? dialog.row.id : 'new'}`}
            root={this.props.root}
            parent={this}
            onUploadSuccess={this.handleExtraLocaleManualUploaded}
            onDeleteFile={this.handleExtraLocaleManualDeleted}
            allowedExtension="pdf"
            fileType="document"
            pickerTitle={Localization.strings.EXTRA_LOCALE_MANUAL || '教師參考手冊 (PDF)'}
            fileInstruction={Localization.strings.EXTRA_LOCALE_MANUAL_ADMIN_HELP
              || '學校選擇此範本後，可於「Extra LOCALE」分頁下載此 PDF。'}
            fileID={dialog.manual_file_id > 0 ? dialog.manual_file_id : ""}
            filePath={manualUrl(dialog.manual_path)}
            fileDisplayName={dialog.manual_display_name}
            // The manuals run ~5MB and peak near 7MB; 20 leaves headroom without
            // inviting an un-flattened scan. The server's own ceiling for a
            // non-video upload is 50MB.
            sizeLimit={20 * 1024 * 1024}
          />
          <Switch
            label={Localization.strings.EXTRA_LOCALE_ENABLED || '開放選擇'}
            checked={dialog.is_enabled}
            onChange={this.handleExtraLocaleEditEnabledChange}
            disabled={dialog.loading}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={this.closeExtraLocaleEditDialog} disabled={dialog.loading}>
              {Localization.strings.CANCEL || '取消'}
            </Button>
            <Button onClick={this.handleExtraLocaleEditSave} loading={dialog.loading}>
              {Localization.strings.SAVE || '儲存'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  renderExtraLocaleManualDeleteDialog() {
    const row = this.state.extraLocaleManualDeleteRow;
    const label = row ? (row.name || ('LOCALE #' + row.epack_id)) : '';
    const busy = !!(row && this.state.extraLocaleManualBusyIds[row.id]);
    return (
      <Modal
        opened={!!row}
        onClose={this.closeExtraLocaleManualDeleteDialog}
        closeOnClickOutside={!busy}
        closeOnEscape={!busy}
        withCloseButton={!busy}
        title={<Text fw={600}>{Localization.strings.EXTRA_LOCALE_MANUAL_DELETE || '刪除教師參考手冊'}</Text>}
        size="sm"
        centered
        zIndex={2000}
      >
        <Stack gap="md">
          <Text>
            {(Localization.strings.EXTRA_LOCALE_MANUAL_DELETE_CONFIRM
              || '確定刪除「{0}」的教師參考手冊？範本本身不受影響。').replace('{0}', label)}
          </Text>
          {/* The consequence worth spelling out: this is not a draft the admin
              is discarding, it is a download that disappears from every school
              currently holding the template. */}
          {row && parseInt(row.selection_count) > 0 && (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              {(Localization.strings.EXTRA_LOCALE_MANUAL_DELETE_SELECTED_WARNING
                || '已選擇此範本的 {0} 間學校將無法再下載此 PDF。')
                .replace('{0}', String(row.selection_count))}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={this.closeExtraLocaleManualDeleteDialog} disabled={busy}>
              {Localization.strings.CANCEL || '取消'}
            </Button>
            <Button color="red" onClick={this.handleExtraLocaleManualDeleteConfirm}
                    loading={busy} leftSection={<IconTrash size={16} />}>
              {Localization.strings.DELETE || '刪除'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  renderExtraLocaleDeleteDialog() {
    const row = this.state.extraLocaleDeleteRow;
    const label = row ? (row.name || ('LOCALE #' + row.epack_id)) : '';
    return (
      <Modal
        opened={!!row}
        onClose={this.closeExtraLocaleDeleteDialog}
        closeOnClickOutside={!this.state.extraLocaleDeleteLoading}
        closeOnEscape={!this.state.extraLocaleDeleteLoading}
        withCloseButton={!this.state.extraLocaleDeleteLoading}
        title={<Text fw={600}>{Localization.strings.EXTRA_LOCALE_ADMIN_DELETE || '刪除範本'}</Text>}
        size="sm"
        centered
        zIndex={2000}
      >
        <Stack gap="md">
          <Text>
            {(Localization.strings.EXTRA_LOCALE_ADMIN_DELETE_CONFIRM || '確定刪除範本「{0}」？學校已有的選擇紀錄會保留。').replace('{0}', label)}
          </Text>
          {row && parseInt(row.selection_count) > 0 && (
            <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
              {(Localization.strings.EXTRA_LOCALE_ADMIN_DELETE_SELECTED_WARNING || '此範本已被 {0} 間學校選擇。').replace('{0}', String(row.selection_count))}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={this.closeExtraLocaleDeleteDialog} disabled={this.state.extraLocaleDeleteLoading}>
              {Localization.strings.CANCEL || '取消'}
            </Button>
            <Button color="red" onClick={this.handleExtraLocaleDeleteConfirm} loading={this.state.extraLocaleDeleteLoading} leftSection={<IconTrash size={16} />}>
              {Localization.strings.DELETE || '刪除'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  renderAdminDetailPageHeader(title, onBack, disabled = false) {
    return (
      <>
        <Group gap="md" align="center">
          <Button
            variant="subtle"
            color="gray"
            onClick={onBack}
            disabled={disabled}
            leftSection={<IconArrowLeft size={18} />}
          >
            {Localization.strings.BACK || 'Back'}
          </Button>
          <Text fw={600} size="lg">{title}</Text>
        </Group>
        <Divider />
      </>
    );
  }

  // How many of an Assigned-LOCALEs list's rows count against PLAN_EXTRA_LOCALE.
  //
  // `subscription_epack` holds TWO kinds of row and only ONE of them is capped:
  //   * Extra Locale (預製教材範本) CATALOG TEMPLATES — a teacher's pick, or an
  //     admin assigning a template. PLAN_EXTRA_LOCALE is the allowance for
  //     exactly these.
  //   * ordinary purchased locales an admin attaches through this page as part
  //     of fulfilment. They live in the same table and are NOT capped by that
  //     privilege.
  // The server marks the first kind with `is_extra_locale_template` = 1
  // (composer_batch_service.subscription_epack_list), and it is the same set
  // the teacher surface calls `used` and subscription_get returns as
  // assigned_extra_locale_count. Every allowance number this page DISPLAYS
  // comes through here so those counters cannot drift apart again.
  //
  // IT IS A READOUT, NOT A GATE (2026-08-28). Nothing on this page refuses a
  // click because of it — see the allowance note in
  // renderSubscriptionLocalesPage for the three reasons the client cannot own
  // this rule, and subscriptionLocaleAssignFailureMessage for the server's.
  //
  // WHY it exists at all: the number used to be `dialog.rows.length`, so a plan
  // with 17 ordinary locales attached and PLAN_EXTRA_LOCALE = 3 read a red 17/3
  // — an overrun that was never real (subscription 1, QEF 2024 Pri, is exactly
  // that shape today), and, while the count was still wired to the Assign
  // button, a permanent lock-out. Post-merge a teacher's pick is a row in this
  // very table, so 15 picks on a 15-slot plan produced the same false overrun.
  //
  // OLD-SERVER FALLBACK, and why it degrades in this direction: a server that
  // predates the column sends no `is_extra_locale_template` at all. A bare
  // filter would then find 0 templates and paint a confident 0/3 over a list
  // that may be nothing but templates — understating what the plan has spent,
  // which is the misleading direction for a readout. So when NO row in a
  // non-empty list carries the key we fall back to the raw row count: coarse
  // (ordinary locales inflate it) but never too low. A current server always
  // sends the key, 0 included, so an all-ordinary list is still distinguishable
  // from an old server's silence.
  extraLocaleTemplateRowCount = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const serverMarksTemplates = list.some((row) => (
      row && row.is_extra_locale_template !== undefined && row.is_extra_locale_template !== null
    ));
    if (!serverMarksTemplates) {
      return list.length;
    }
    // Values arrive as ints on /api and as PHP-style strings ("1"/"0") on the
    // legacy surface, hence Number() rather than ===.
    return list.filter((row) => Number(row.is_extra_locale_template) === 1).length;
  }

  renderSubscriptionLocalesPage() {
    const dialog = this.state.subscriptionLocalesDialog;
    const title = `${Localization.strings.SUBSCRIPTION_LOCALE_TITLE}${dialog.subscription ? ` - ${dialog.subscription.name}` : ''}`;
    const assignedRows = dialog.rows || [];
    const maxLocaleCount = Math.max(0, Number(dialog.subscription?.max_extra_locale_number) || 0);
    // The allowance is TEMPLATES vs PLAN_EXTRA_LOCALE, never the raw row count —
    // see extraLocaleTemplateRowCount above. Ordinary attached locales stay
    // listed and stay removable; they just do not consume Extra Locale slots.
    //
    // AND IT IS INFORMATION ONLY: no control on this page is disabled by it
    // (2026-08-28). Three reasons, in order of force.
    //   1. The server caps CATALOG TEMPLATES ONLY. subscription_write.py's
    //      add_epacks_to_subscription counts a request's catalog ids against the
    //      quota and says of the rest, in as many words, "Ordinary (non-catalog)
    //      attachments stay UNCAPPED: they are not charged to the allowance and
    //      never were." A client that also blocks ordinary attachments is
    //      stricter than the server in the one direction that hurts — it stops
    //      an admin attaching a locale the school actually paid for. Teachers
    //      filling a 9-slot plan with 9 picks would have locked the admin out of
    //      this page permanently, with no admin-side way to clear the block.
    //   2. A plan with NO PLAN_EXTRA_LOCALE privilege row reads
    //      max_extra_locale_number = 0, and today essentially every plan is one:
    //      in the 2026-07-21 production dump, 0 of 599 subscription_privilege
    //      rows across all 26 subscriptions carry privilege 24 (it postdates the
    //      dump). So the old gate was not an edge case — it disabled Assign on
    //      every plan on the page.
    //   3. The client could not apply the correct gate even if it wanted to. The
    //      picker's rows come from search_epack_by_keyword, whose SELECT carries
    //      no catalog flag (composer_batch_service.search_epack_by_keyword), so
    //      nothing here can tell a template from an ordinary locale BEFORE the
    //      write. It cannot surface one either: this dialog sends no
    //      include_unready, the service therefore appends `e.is_ready = 1`, and
    //      every catalog epack is an unpublished draft (all 40 of the 49 catalog
    //      ids that exist in that dump are is_ready = 0).
    // The server's typed 409 is the real gate, and the failure branch of
    // handleAssignSelectedLocales renders it.
    // Short label for the per-row marker and the panel's cap readout.
    // EXTRA_LOCALE_ADMIN_SECTION is preferred over EXTRA_LOCALE_TITLE because
    // both of its translations are two words ('預製教材範本' / 'Extra Locales'),
    // while EXTRA_LOCALE_TITLE's English string is a full 50-character sentence
    // that would blow a badge across the row.
    const templateBadgeLabel = Localization.strings.EXTRA_LOCALE_ADMIN_SECTION
      || Localization.strings.EXTRA_LOCALE_TITLE
      || '預製教材範本';
    const assignedLocaleIds = new Set(assignedRows.map((row) => String(row.epack_id)));
    const availableRows = (dialog.searchResults || []).filter((row) => !assignedLocaleIds.has(String(row.id)));
    const availableLocaleIds = new Set(availableRows.map((row) => String(row.id)));
    const selectedLocaleIds = (dialog.selectedLocaleIds || []).filter((id) => availableLocaleIds.has(String(id)));
    const selectedLocaleIdSet = new Set(selectedLocaleIds.map(String));
    const assignedJoinIds = new Set(
      assignedRows
        .map((row) => row.subscription_epack_id || row.id)
        .filter((id) => id !== null && id !== undefined)
        .map(String)
    );
    const selectedAssignedLocaleIds = (dialog.selectedAssignedLocaleIds || [])
      .map(String)
      .filter((id) => assignedJoinIds.has(id));
    const selectedAssignedLocaleIdSet = new Set(selectedAssignedLocaleIds);
    // Over-limit counts EVERY assigned row, matching the server's
    // assigned_extra_locale_count (a plain COUNT(*) again since 2026-08-30 —
    // see subscription_service.subscription_get for why the catalogue join was
    // the wrong definition). The badge answers "what does this plan carry
    // against its allowance", so a plan holding 17 rows against 3 IS over, and
    // saying so is honest rather than alarming: nothing gates on this number,
    // the Assign button is no longer bound to it, and the server caps catalogue
    // attachments itself.
    const isOverLimit = assignedRows.length > maxLocaleCount;
    // Nothing picked, or a request in flight — that is the whole rule. The
    // allowance is deliberately absent: see the note above it.
    const canAssign = selectedLocaleIds.length > 0
      && !dialog.loading
      && !dialog.assigning
      && !dialog.removing;
    const panelHeight = 'clamp(22rem, calc(85vh - 14rem), 36rem)';
    // One label, always. It used to flip to "LOCALE limit reached" whenever the
    // template readout was full, announcing a refusal this page no longer makes
    // — and announcing it loudest on the plans with no allowance at all, where
    // the readout is 0/0 from the moment the page opens.
    const assignLabel = Localization.strings.SUBSCRIPTION_LOCALE_ASSIGN_SELECTED
      || 'Assign selected LOCALEs';
    const searchMode = dialog.searchMode || 'keyword';
    const isSourceSearchMode = searchMode === 'user' || searchMode === 'subscription';
    const selectedSearchSource = dialog.selectedSearchSource || null;
    const searchSourceResults = dialog.searchSourceResults || [];
    const searchModeOptions = [
      {
        value: 'user',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_USER || 'User',
      },
      {
        value: 'subscription',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_SUBSCRIPTION || 'Other subscription',
      },
      {
        value: 'keyword',
        label: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_BY_KEYWORD || 'Keyword',
      },
    ];
    const searchPlaceholder = {
      user: Localization.strings.SUBSCRIPTION_LOCALE_USER_SEARCH_PLACEHOLDER
        || 'Search username or display name',
      subscription: Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_SEARCH_PLACEHOLDER
        || 'Search subscription name',
      keyword: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_PLACEHOLDER
        || 'Search LOCALE name',
    }[searchMode];
    const searchPrompt = {
      user: Localization.strings.SUBSCRIPTION_LOCALE_USER_SEARCH_PROMPT
        || 'Search by username or display name',
      subscription: Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_SEARCH_PROMPT
        || 'Search another subscription by its name',
      keyword: Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_PROMPT
        || 'Search for LOCALEs to assign',
    }[searchMode];
    const noSourceResultText = searchMode === 'user'
      ? (Localization.strings.SUBSCRIPTION_LOCALE_NO_USER_RESULT || 'No matching users found')
      : (Localization.strings.SUBSCRIPTION_LOCALE_NO_SUBSCRIPTION_RESULT || 'No matching subscriptions found');
    const filterTemplate = searchMode === 'user'
      ? (Localization.strings.SUBSCRIPTION_LOCALE_USER_FILTER || 'User: {0}')
      : (Localization.strings.SUBSCRIPTION_LOCALE_SUBSCRIPTION_FILTER || 'Subscription: {0}');
    const selectedFilterLabel = selectedSearchSource
      ? filterTemplate.replace('{0}', selectedSearchSource.label)
      : '';

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(
          title,
          this.closeSubscriptionLocalesDialog,
          dialog.assigning || dialog.removing
        )}
        <Flex gap="md" align="stretch" style={{ minWidth: 0 }}>
          <Paper
            withBorder
            radius="md"
            style={{
              flex: 1,
              minWidth: 0,
              height: panelHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              {/*
                ONE number against the allowance, and it counts every attached
                row — the same COUNT(*) the server puts in the Subscription
                List badge, so the two screens cannot disagree. This briefly
                showed a templates-only count instead; subscription 76 then read
                0/15 with ten locales assigned, because its rows are the 25/26
                editions of the pre-made templates and the 26/27 catalogue does
                not list them. Nothing gates on this number — it is a readout.
                The per-row 預製教材範本 marker still distinguishes catalogue
                templates in the list below.
              */}
              <Group gap="xs" wrap="nowrap" justify="space-between">
                <Text fw={600}>
                  {Localization.strings.SUBSCRIPTION_ASSIGNED_LOCALES || 'Assigned LOCALES'} ({assignedRows.length})
                </Text>
                <Text size="xs" fw={600} c={isOverLimit ? 'red' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                  {templateBadgeLabel} {assignedRows.length}/{maxLocaleCount}
                </Text>
              </Group>
            </Box>
            <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
              {dialog.loading ? (
                <Group justify="center" p="xl">
                  <Loader size="sm" />
                  <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
                </Group>
              ) : assignedRows.length > 0 ? (
                assignedRows.map((row, idx) => {
                  const rowId = String(row.subscription_epack_id || row.id);
                  const authorName = row.user_display_name || row.user_username || '-';
                  const localeMetadata = [
                    authorName,
                    this.formatDateTime(row.date_created),
                    this.formatDateTime(row.date_update),
                  ].join(' | ');
                  // The only visible difference between a teacher's permanent
                  // Extra Locale pick and an ordinary locale an admin attached:
                  // same table, same row shape, but only the badged ones spend
                  // the allowance shown in this panel's header. An old server
                  // sends no flag, so nothing is badged and the header falls
                  // back to counting everything — consistent, if coarse.
                  const isTemplateRow = Number(row.is_extra_locale_template) === 1;
                  return (
                    <Box
                      key={rowId || idx}
                      px="md"
                      py="sm"
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                    >
                      <Checkbox
                        checked={selectedAssignedLocaleIdSet.has(rowId)}
                        disabled={dialog.assigning || dialog.removing}
                        label={
                          <Box style={{ minWidth: 0 }}>
                            <Group gap={6} wrap="nowrap" align="center">
                              <Text size="sm" lineClamp={2}>{row.epack_name || '-'}</Text>
                              {isTemplateRow ? (
                                <Badge
                                  color="blue"
                                  variant="light"
                                  size="xs"
                                  radius="sm"
                                  style={{ flexShrink: 0 }}
                                >
                                  {templateBadgeLabel}
                                </Badge>
                              ) : null}
                            </Group>
                            <Text size="xs" c="dimmed" truncate>{localeMetadata}</Text>
                          </Box>
                        }
                        aria-label={row.epack_name || '-'}
                        onChange={(event) => this.handleAssignedLocaleSelectionChange(rowId, event.currentTarget.checked)}
                      />
                    </Box>
                  );
                })
              ) : (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {Localization.strings.SUBSCRIPTION_NO_LOCALE}
                </Text>
              )}
            </ScrollArea>
            <Box px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Group justify="flex-end">
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={16} />}
                  loading={dialog.removing}
                  disabled={
                    selectedAssignedLocaleIds.length === 0
                    || dialog.loading
                    || dialog.assigning
                  }
                  onClick={this.handleRemoveSelectedLocales}
                >
                  {Localization.strings.SUBSCRIPTION_REMOVE_SELECTED || 'Remove selected'}
                </Button>
              </Group>
            </Box>
          </Paper>

          <Flex align="center" justify="center" style={{ width: 56, flexShrink: 0 }}>
            <Tooltip label={assignLabel}>
              <ActionIcon
                size="xl"
                radius="xl"
                variant="filled"
                loading={dialog.assigning}
                disabled={!canAssign}
                aria-label={assignLabel}
                onClick={this.handleAssignSelectedLocales}
              >
                <IconChevronLeft size={24} stroke={2} />
              </ActionIcon>
            </Tooltip>
          </Flex>

          <Paper
            withBorder
            radius="md"
            style={{
              flex: 1,
              minWidth: 0,
              height: panelHeight,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Stack gap="sm" px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <SegmentedControl
                fullWidth
                value={searchMode}
                data={searchModeOptions}
                disabled={dialog.loading || dialog.assigning || dialog.removing}
                onChange={this.handleSubscriptionLocaleSearchModeChange}
              />
              <TextInput
                value={dialog.searchText || ''}
                placeholder={searchPlaceholder}
                leftSection={<IconSearch size={16} />}
                rightSection={dialog.searchLoading ? <Loader size={16} /> : null}
                disabled={dialog.loading || dialog.assigning || dialog.removing}
                aria-label={searchPlaceholder}
                onChange={this.handleSubscriptionLocaleSearchChange}
              />
              {selectedSearchSource ? (
                <Group gap="xs">
                  <Pill
                    withRemoveButton
                    onRemove={this.handleClearSubscriptionLocaleSearchSource}
                  >
                    {selectedFilterLabel}
                  </Pill>
                </Group>
              ) : null}
            </Stack>
            <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
              {dialog.searchLoading ? (
                <Group justify="center" p="xl">
                  <Loader size="sm" />
                  <Text c="dimmed">{Localization.strings.SUBSCRIPTION_SEARCHING}</Text>
                </Group>
              ) : !(dialog.searchText || '').trim() ? (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {searchPrompt}
                </Text>
              ) : isSourceSearchMode && !selectedSearchSource ? (
                searchSourceResults.length === 0 ? (
                  <Text ta="center" c="dimmed" size="sm" p="xl">
                    {noSourceResultText}
                  </Text>
                ) : (
                  searchSourceResults.map((row) => {
                    const isUser = searchMode === 'user';
                    const primaryText = isUser
                      ? (row.display_name || row.username || '-')
                      : (row.name || row.name_en || '-');
                    const secondaryParts = isUser
                      ? [
                        row.username !== primaryText ? row.username : null,
                        row.school_name || row.school_name_eng,
                      ]
                      : [row.name_en !== primaryText ? row.name_en : null];
                    const secondaryText = secondaryParts.filter(Boolean).join(' | ');
                    return (
                      <UnstyledButton
                        key={row.id}
                        w="100%"
                        px="md"
                        py="sm"
                        disabled={dialog.assigning || dialog.removing}
                        aria-label={primaryText}
                        onClick={() => this.handleSubscriptionLocaleSearchSourceSelect(row)}
                        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                      >
                        <Group justify="space-between" gap="sm" wrap="nowrap">
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>{primaryText}</Text>
                            {secondaryText ? (
                              <Text size="xs" c="dimmed" truncate>{secondaryText}</Text>
                            ) : null}
                          </Box>
                          <Badge variant="light" size="sm">
                            {row.locale_count || 0} LOCALE
                          </Badge>
                        </Group>
                      </UnstyledButton>
                    );
                  })
                )
              ) : availableRows.length === 0 ? (
                <Text ta="center" c="dimmed" size="sm" p="xl">
                  {Localization.strings.SUBSCRIPTION_LOCALE_NO_SEARCH_RESULT || 'No available LOCALES found'}
                </Text>
              ) : (
                availableRows.map((row) => {
                  const rowId = String(row.id);
                  const checked = selectedLocaleIdSet.has(rowId);
                  // No allowance test on the tick box either — it used to grey
                  // out every unticked row once the template readout was full,
                  // i.e. from the start on any plan without the privilege.
                  const authorName = row.user_display_name || row.user_username || '-';
                  const localeMetadata = [
                    authorName,
                    this.formatDateTime(row.date_created),
                    this.formatDateTime(row.date_update),
                  ].join(' | ');
                  return (
                    <Box
                      key={rowId}
                      px="md"
                      py="sm"
                      style={{
                        display: 'block',
                        borderBottom: '1px solid var(--mantine-color-gray-2)',
                        cursor: dialog.assigning || dialog.removing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={dialog.assigning || dialog.removing}
                        label={
                          <Box>
                            <Text size="sm">{row.name || '-'}</Text>
                            <Text size="xs" c="dimmed" truncate>
                              {localeMetadata}
                            </Text>
                          </Box>
                        }
                        aria-label={row.name || '-'}
                        onChange={(event) => this.handleSubscriptionLocaleSelectionChange(row.id, event.currentTarget.checked)}
                      />
                    </Box>
                  );
                })
              )}
            </ScrollArea>
          </Paper>
        </Flex>
      </Stack>
    );
  }

  renderSubscriptionPrivilegePage() {
    const dialog = this.state.subscriptionPrivilegeDialog;
    const title = `${Localization.strings.SUBSCRIPTION_PRIVILEGE_TITLE}${dialog.subscription ? ` - ${dialog.subscription.name}` : ''}`;

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(title, this.closeSubscriptionPrivilegeDialog)}
        {dialog.loading ? (
          <Group justify="center" p="md">
            <Loader size="sm" />
            <Text c="dimmed">{Localization.strings.LOADING_DATA}</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table striped verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>{Localization.strings.SUBSCRIPTION_PRIVILEGE_NAME}</Table.Th>
                  <Table.Th>{Localization.strings.VALUE}</Table.Th>
                  <Table.Th>{Localization.strings.VALUE} 2</Table.Th>
                  <Table.Th w={80}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dialog.rows.map((row, idx) => (
                  <Table.Tr key={row.subscription_privilege_id || row.id || idx}>
                    <Table.Td>{row.privilege_id}</Table.Td>
                    <Table.Td>
                      <Text>{row.name_long_zh || row.name_long_en || row.name || '-'}</Text>
                      {this.privilegeName(row) === PLAN_EXTRA_LOCALE && (
                        <Text size="xs" c="dimmed">
                          {Localization.strings.PLAN_EXTRA_LOCALE_ADMIN_HELP}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {this.privilegeIsBoolean(row) ? (
                        <Switch
                          checked={this.privilegeTruthy(row.value)}
                          onChange={(e) => this.commitPrivilegeField(row, 'value', e.currentTarget.checked ? 1 : 0)}
                        />
                      ) : row.name === 'STORAGE_LIMIT' ? (
                        (() => {
                          const gbDisplay = this.mbToGbDisplay(row.value);
                          return (
                            <TextInput
                              key={`val-${row.subscription_privilege_id || row.id}-${row.value}`}
                              type="number"
                              defaultValue={gbDisplay}
                              onBlur={(e) => {
                                this.commitStoragePrivilegeGb(row, gbDisplay, e.target.value);
                                const gb = parseFloat(e.target.value);
                                const normalized = (isNaN(gb) || gb < 0) ? 0 : gb;
                                e.target.value = this.mbToGbDisplay(Math.round(normalized * 1024));
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                              min={0}
                              step={0.1}
                              rightSection={<Text size="sm" c="dimmed">GB</Text>}
                              rightSectionWidth={40}
                              w={140}
                            />
                          );
                        })()
                      ) : (
                        <TextInput
                          key={`val-${row.subscription_privilege_id || row.id}-${row.value}`}
                          type="number"
                          defaultValue={row.value ?? ''}
                          onBlur={(e) => this.commitPrivilegeField(row, 'value', e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                          min={0}
                          rightSection={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? <Text size="xs" c="dimmed">LOCALE</Text> : undefined}
                          rightSectionWidth={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? 62 : undefined}
                          w={this.privilegeName(row) === PLAN_EXTRA_LOCALE ? 170 : 110}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      {row.name === 'AI_3D_MODEL' ? (
                        <TextInput
                          key={`val2-${row.subscription_privilege_id || row.id}-${row.value_2}`}
                          type="number"
                          defaultValue={row.value_2 ?? ''}
                          disabled={!this.privilegeTruthy(row.value)}
                          onBlur={(e) => this.commitPrivilegeField(row, 'value_2', e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                          min={0}
                          w={110}
                        />
                      ) : (
                        <TextInput type="number" value={row.value_2 ?? ''} disabled readOnly w={110} />
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Tooltip label={Localization.strings.RESET || 'Reset'}>
                        <ActionIcon variant="subtle" onClick={() => this.handlePrivilegeReset(row)}>
                          <IconRestore size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {dialog.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5} style={{ textAlign: 'center', padding: 12 }}>
                      {Localization.strings.SUBSCRIPTION_NO_PRIVILEGE}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  renderSubscriptionCreatePage() {
    const dialog = this.state.subscriptionCreateDialog;
    const sourceSubscription = this.state.subscriptionCopyPrivilegeFrom;
    const sourceName = sourceSubscription
      ? (sourceSubscription.name || sourceSubscription.name_en || `#${sourceSubscription.id}`)
      : '';
    const sourceNameEn = sourceSubscription
      && sourceSubscription.name_en
      && sourceSubscription.name_en !== sourceName
      ? sourceSubscription.name_en
      : '';

    return (
      <Stack gap="md">
        {this.renderAdminDetailPageHeader(
          Localization.strings.SUBSCRIPTION_CREATE,
          this.closeSubscriptionCreateDialog,
          dialog.loading
        )}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="lg">
          <Stack gap="md">
            <TextInput
              label={Localization.strings.SUBSCRIPTION_NAME_ZH}
              required
              placeholder={Localization.strings.SUBSCRIPTION_NAME_ZH}
              value={dialog.name}
              onChange={this.handleSubscriptionCreateFieldChange('name')}
              disabled={dialog.loading}
              autoFocus={!!dialog.returnToSchoolSubscription}
            />
            <TextInput
              label={Localization.strings.SUBSCRIPTION_NAME_EN}
              required
              placeholder={Localization.strings.SUBSCRIPTION_NAME_EN}
              value={dialog.name_en}
              onChange={this.handleSubscriptionCreateFieldChange('name_en')}
              disabled={dialog.loading}
            />
            <Textarea
              label={Localization.strings.REMARK}
              placeholder={Localization.strings.REMARK}
              minRows={2}
              autosize
              value={dialog.remarks}
              onChange={this.handleSubscriptionCreateFieldChange('remarks')}
              disabled={dialog.loading}
            />
            <Switch
              label={Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template'}
              checked={!!dialog.is_template}
              disabled={dialog.loading}
              onChange={(event) => {
                const updated = {
                  ...this.state.subscriptionCreateDialog,
                  is_template: event.currentTarget.checked,
                };
                this.setState({ subscriptionCreateDialog: updated });
                this.state.subscriptionCreateDialog = updated;
              }}
            />
          </Stack>

          <Stack gap="md">
            <Group align="center" wrap="nowrap">
              <Text size="sm" fw={500} w={190} style={{ flexShrink: 0 }}>
                {Localization.strings.SUBSCRIPTION_COPY_PRIV_LABEL}
              </Text>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <SubscriptionPicker
                  root={this.props.root}
                  placeholder={Localization.strings.SUBSCRIPTION_PICKER_PLACEHOLDER}
                  disabled={dialog.loading}
                  searchOnly
                  onSelect={this.handleSubscriptionCopySourceSelect}
                />
              </Box>
            </Group>

            <Group align="center" gap="xs" wrap="nowrap" mih={40}>
              <Text size="sm" fw={500}>
                {Localization.strings.SUBSCRIPTION_SOURCE_LABEL || 'Source'}:
              </Text>
              {sourceSubscription ? (
                <>
                  <Popover key={sourceSubscription.id} width={380} position="bottom-start" withArrow shadow="md">
                    <Popover.Target>
                      <ActionIcon
                        variant="light"
                        color="grape"
                        aria-label={Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                        title={Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                      >
                        <IconShieldCheck size={18} />
                      </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Text size="sm" fw={600} mb="xs">
                        {Localization.strings.SUBSCRIPTION_SOURCE_PRIVILEGES || 'Source privileges'}
                      </Text>
                      {dialog.sourcePrivilegesLoading ? (
                        <Group justify="center" py="lg">
                          <Loader size="sm" />
                        </Group>
                      ) : dialog.sourcePrivileges.length === 0 ? (
                        <Text size="sm" c="dimmed">
                          {Localization.strings.SUBSCRIPTION_NO_PRIVILEGE}
                        </Text>
                      ) : (
                        <ScrollArea h={280} type="auto">
                          <Stack gap={0} pr="xs">
                            {dialog.sourcePrivileges.map((privilege, index) => {
                              const privilegeLabel = Localization.strings.getLanguage() === 'en'
                                ? (privilege.name_long_en || privilege.name_long_zh || privilege.name || '-')
                                : (privilege.name_long_zh || privilege.name_long_en || privilege.name || '-');
                              const isBoolean = this.privilegeIsBoolean(privilege);
                              const valueText = isBoolean
                                ? (this.privilegeTruthy(privilege.value)
                                  ? Localization.strings.SUPPORTED
                                  : Localization.strings.NOT_SUPPORTED)
                                : [privilege.value, privilege.value_2]
                                  .filter((value) => value !== undefined && value !== null && value !== '')
                                  .join(' / ');
                              return (
                                <Group
                                  key={privilege.subscription_privilege_id || privilege.id || index}
                                  justify="space-between"
                                  align="flex-start"
                                  gap="sm"
                                  wrap="nowrap"
                                  py="xs"
                                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                                >
                                  <Text size="sm" style={{ flex: 1 }}>{privilegeLabel}</Text>
                                  <Badge
                                    variant="light"
                                    color={isBoolean ? (this.privilegeTruthy(privilege.value) ? 'green' : 'gray') : 'blue'}
                                    style={{ flexShrink: 0 }}
                                  >
                                    {valueText || '-'}
                                  </Badge>
                                </Group>
                              );
                            })}
                          </Stack>
                        </ScrollArea>
                      )}
                    </Popover.Dropdown>
                  </Popover>
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{sourceName}</Text>
                    {sourceNameEn ? <Text size="xs" c="dimmed" truncate>{sourceNameEn}</Text> : null}
                  </Box>
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  {Localization.strings.SUBSCRIPTION_SOURCE_NOT_SELECTED || 'Not selected'}
                </Text>
              )}
            </Group>

            <Box>
              <Text size="sm" fw={500} mb={6}>
                {Localization.strings.SUBSCRIPTION_SELECT_FROM_TEMPLATES || 'Select from templates'}
              </Text>
              <Paper withBorder radius="md" p="sm">
                <ScrollArea h={150} type="auto" offsetScrollbars>
                  {dialog.templatesLoading ? (
                    <Group justify="center" h={130}>
                      <Loader size="sm" />
                    </Group>
                  ) : dialog.templates.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      {Localization.strings.SUBSCRIPTION_NO_TEMPLATES || 'No templates'}
                    </Text>
                  ) : (
                    <Group gap="xs" align="flex-start">
                      {dialog.templates.map((template) => {
                        const selected = sourceSubscription && sourceSubscription.id === template.id;
                        const templateName = template.name || template.name_en || `#${template.id}`;
                        return (
                          <Tooltip
                            key={template.id}
                            label={[template.name, template.name_en].filter(Boolean).join(' / ')}
                          >
                            <UnstyledButton
                              type="button"
                              disabled={dialog.loading}
                              aria-pressed={!!selected}
                              onClick={() => this.handleSubscriptionCopySourceSelect(template)}
                              style={{
                                border: `1px solid ${selected ? BRAND_PRIMARY : 'var(--mantine-color-gray-3)'}`,
                                borderRadius: 999,
                                padding: '5px 12px',
                                backgroundColor: selected ? BRAND_PRIMARY : 'var(--mantine-color-gray-1)',
                                color: selected ? 'white' : 'var(--mantine-color-gray-7)',
                                fontSize: 'var(--mantine-font-size-sm)',
                                lineHeight: 1.35,
                                cursor: dialog.loading ? 'default' : 'pointer',
                              }}
                            >
                              {templateName}
                            </UnstyledButton>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  )}
                </ScrollArea>
              </Paper>
            </Box>

            <Switch
              label={Localization.strings.SUBSCRIPTION_COPY_LOCALE_LABEL}
              checked={!!dialog.copyExtraLocales}
              disabled={dialog.loading || !sourceSubscription}
              onChange={this.handleSubscriptionCopyExtraLocalesChange}
            />
          </Stack>
        </SimpleGrid>

        <Group justify="flex-end" mt="sm">
          <Button
            onClick={this.handleSubscriptionCreateSubmit}
            loading={dialog.loading}
            leftSection={<IconPlus size={16} />}
          >
            {Localization.strings.CREATE}
          </Button>
        </Group>
      </Stack>
    );
  }

  renderEditableSubscriptionName(row, field) {
    const editingCell = this.state.subscriptionEditingCell;
    const isEditing = editingCell?.id === row.id && editingCell.field === field;
    const updateKey = `${row.id}:${field}`;
    const isUpdating = !!this.state.subscriptionUpdatingFields[updateKey];
    const label = field === 'name'
      ? Localization.strings.SUBSCRIPTION_NAME_ZH
      : Localization.strings.SUBSCRIPTION_NAME_EN;

    if (isEditing) {
      return (
        <TextInput
          size="xs"
          variant="unstyled"
          value={editingCell.value}
          autoFocus
          maxLength={512}
          aria-label={label}
          onFocus={(event) => event.currentTarget.select()}
          onChange={this.handleSubscriptionNameEditChange}
          onBlur={this.handleSubscriptionNameEditCommit}
          onKeyDown={this.handleSubscriptionNameEditKeyDown}
        />
      );
    }

    return (
      <Box
        component="button"
        type="button"
        disabled={isUpdating}
        aria-label={`${Localization.strings.EDIT || 'Edit'} ${label}`}
        title={Localization.strings.SUBSCRIPTION_DOUBLE_CLICK_EDIT || 'Double-click to edit'}
        onDoubleClick={() => this.handleSubscriptionNameEditStart(row, field)}
        style={{
          width: '100%',
          minHeight: 30,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          textAlign: 'left',
          cursor: isUpdating ? 'wait' : 'text',
        }}
      >
        {row[field] || '-'}
      </Box>
    );
  }

  subscriptionTab() {
    const direction = this.state.subscriptionNavigationDirection;
    const animationClassName = direction
      ? `admin-page-transition admin-page-transition--${direction}`
      : 'admin-page-transition';

    if (this.state.subscriptionCreateDialog.open) {
      return (
        <Box key="subscription-create" className={animationClassName}>
          {this.renderSubscriptionCreatePage()}
        </Box>
      );
    }
    if (this.state.subscriptionLocalesDialog.open) {
      return (
        <Box key="subscription-locales" className={animationClassName}>
          {this.renderSubscriptionLocalesPage()}
        </Box>
      );
    }
    if (this.state.subscriptionPrivilegeDialog.open) {
      return (
        <Box key="subscription-privileges" className={animationClassName}>
          {this.renderSubscriptionPrivilegePage()}
        </Box>
      );
    }

    const rows = sortSubscriptionRows(
      this.state.subscriptionList,
      this.state.subscriptionSortBy,
      this.state.subscriptionSortDir,
    );
    return (
      <Box
        key="subscription-list"
        className={animationClassName}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'calc(85vh - 140px)' }}
      >
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            style={{ flex: 1, minWidth: 240 }}
            label={Localization.strings.SUBSCRIPTION_KEYWORD_SEARCH}
            placeholder={Localization.strings.SUBSCRIPTION_KEYWORD_HINT}
            value={this.state.subscriptionSearchText}
            onChange={this.handleSubscriptionKeywordChange}
            onKeyDown={this.handleSubscriptionSearchEnter}
            leftSection={<IconSearch size={16} />}
          />
          <Select
            w={120}
            allowDeselect={false}
            value={String(this.state.subscriptionLimit)}
            onChange={(v) => this.handleSubscriptionLimitChange({ target: { value: Number(v) } })}
            data={[
              { value: '50', label: '50 個結果' },
              { value: '100', label: '100 個結果' },
              { value: '200', label: '200 個結果' },
              { value: '500', label: '500 個結果' },
            ]}
          />
          <Button onClick={this.handleSubscriptionSearch} loading={this.state.subscriptionLoading} leftSection={<IconSearch size={16} />}>
            {this.state.subscriptionLoading ? Localization.strings.SUBSCRIPTION_SEARCHING : Localization.strings.SEARCH}
          </Button>
          <Button variant="light" onClick={this.openSubscriptionCreateDialog} leftSection={<IconPlus size={16} />}>
            {Localization.strings.SUBSCRIPTION_CREATE}
          </Button>
        </Group>

        {this.state.subscriptionLoading ? (
          <TableLoadingCard icon={<IconLicense {...LOADING_CARD_ICON_PROPS} />} />
        ) : (
          <Table.ScrollContainer minWidth={1416}>
            <Table
              striped
              highlightOnHover
              verticalSpacing="sm"
              stickyHeader
              style={{ tableLayout: 'fixed', width: 1416, minWidth: 1416 }}
            >
              <Table.Thead>
                <Table.Tr>
                  {this.renderSubscriptionSortableTh('id', 'ID', { width: 80 })}
                  <Table.Th style={{ width: 180 }}>{Localization.strings.ACTION}</Table.Th>
                  {this.renderSubscriptionSortableTh('name', Localization.strings.SUBSCRIPTION_NAME_ZH, { width: 300 })}
                  {this.renderSubscriptionSortableTh('name_en', Localization.strings.SUBSCRIPTION_NAME_EN, { width: 280 })}
                  {this.renderSubscriptionSortableTh('is_template', Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template', { width: 130 })}
                  {this.renderSubscriptionSortableTh('date_create', Localization.strings.SUBSCRIPTION_SORT_CREATED, { width: 170 })}
                  <Table.Th style={{ width: 220 }}>{Localization.strings.REMARK}</Table.Th>
                  <Table.Th style={{ width: 56 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const templateUpdateKey = `${row.id}:is_template`;
                  const isTemplateUpdating = !!this.state.subscriptionUpdatingFields[templateUpdateKey];
                  const assignedExtraLocaleCount = Number(row.assigned_extra_locale_count) || 0;
                  const maxExtraLocaleNumber = Number(row.max_extra_locale_number) || 0;
                  const isExtraLocaleOverLimit = assignedExtraLocaleCount > maxExtraLocaleNumber;
                  return (
                  <Table.Tr key={row.id}>
                    <Table.Td>{row.id}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label={Localization.strings.SUBSCRIPTION_EXTRA_LOCALE_USAGE || 'Assigned extra LOCALEs / maximum'}>
                          <Badge
                            color={isExtraLocaleOverLimit ? 'red' : 'blue'}
                            variant="light"
                            radius="sm"
                            style={{ width: 64, minWidth: 64, flexShrink: 0 }}
                          >
                            {`${assignedExtraLocaleCount}/${maxExtraLocaleNumber}`}
                          </Badge>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SUBSCRIPTION_LOCALE_BUTTON}>
                          <ActionIcon variant="subtle" color="blue" onClick={() => this.openSubscriptionLocalesDialog(row)}>
                            <IconMap size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={Localization.strings.SUBSCRIPTION_PRIVILEGE_BUTTON}>
                          <ActionIcon variant="subtle" color="grape" onClick={() => this.openSubscriptionPrivilegeDialog(row)}>
                            <IconShieldCheck size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-word' }}>
                      {this.renderEditableSubscriptionName(row, 'name')}
                    </Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-word' }}>
                      {this.renderEditableSubscriptionName(row, 'name_en')}
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={this.isSubscriptionTemplate(row.is_template)}
                        disabled={isTemplateUpdating}
                        aria-label={Localization.strings.SUBSCRIPTION_IS_TEMPLATE || 'Is template'}
                        onChange={(event) => this.handleSubscriptionTemplateChange(row, event.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{this.formatDateTime(row.date_create)}</Table.Td>
                    <Table.Td style={{ overflow: 'hidden', wordBreak: 'break-all' }}>
                      {row.remarks || '-'}
                    </Table.Td>
                    <Table.Td style={{ width: 56, whiteSpace: 'nowrap', textAlign: 'center' }}>
                      <Tooltip label={Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete'}>
                        <ActionIcon variant="subtle" color="red" onClick={() => this.openSubscriptionDeleteDialog(row)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
                {rows.length === 0 && this.state.hasSubscriptionSearched && (
                  <Table.Tr>
                    <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#666' }}>
                      {Localization.strings.SUBSCRIPTION_NO_RESULT}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        </Stack>
      </Box>
    );
  }

  searchTextChanged(e) {
    this.setState({ searchText: e.target.value });
    this.state.searchText = e.target.value;
  }

  handleSearchClicked() {
    const S = Localization.strings;
    const keyword = (this.state.searchText || "").trim();
    // A platform admin's search is cross-school and needs a keyword (the
    // server 400s an empty one). A school admin's empty keyword means "my
    // whole roster" — the server pins them to it — so it goes through.
    if (!keyword && !this.isSchoolAdminOnly()) {
      this.props.root.addNotification(S.ADMIN_ERR_SEARCH_REQUIRED || '請輸入搜尋條件', false);
      return;
    }

    let requestObj = {
      search_term: keyword
    };

    this.props.root.refs.API.request("search_user", requestObj, this.searchUserOK, this.searchUserFailed);
  }

  searchUserOK(res) {
    console.log("search user ok", res);
    let userList = res.body['result'];
    
    if (Array.isArray(userList)) {
      this.setState({
        tableRows: userList,
      });
      this.state.tableRows = userList;
      
      // The roster auto-lists on entering the tab for school admins; a toast
      // for every entry would be noise, so only a typed search reports a count.
      if ((this.state.searchText || "").trim()) {
        this.props.root.addNotification(
          String(Localization.strings.ADMIN_SEARCH_FOUND || '找到 {0} 名用戶').replace('{0}', String(userList.length)),
          true
        );
      }
    } else {
      this.setState({
        tableRows: [],
      });
      this.state.tableRows = [];
      this.props.root.addNotification(Localization.strings.ADMIN_SEARCH_EMPTY || '搜尋結果為空', false);
    }
  }

  searchUserFailed(err) {
    console.log("search user failed", err);
    this.setState({
      tableRows: [],
    });
    this.state.tableRows = [];
    this.props.root.addNotification(Localization.strings.ADMIN_SEARCH_FAILED || '搜尋失敗', false);
  }

  handleSearchEnterPressed(e) {
    if (e.key === "Enter") {
      this.handleSearchClicked();
    }
  }

  // School search methods
  schoolSearchTextChanged = (e) => {
    this.setState({ schoolSearchText: e.target.value });
    this.state.schoolSearchText = e.target.value;
  }

  handleSchoolFilterChange = (field) => (e) => {
    let filters = { ...this.state.schoolFilters };
    filters[field] = e.target.value;
    this.setState({ schoolFilters: filters });
    this.state.schoolFilters = filters;
    this.handleSchoolSearchClicked();
  }

  /** True while the school list is filtered by a subscription KEYWORD. */
  isSchoolSubscriptionKeywordActive = () => {
    return (this.state.schoolSubscriptionKeyword || "").trim() !== "";
  }

  /** True while the eLAFP quick filter is the active subscription keyword. */
  isElafpFilterActive = () => {
    return (this.state.schoolSubscriptionKeyword || "").trim().toLowerCase()
      === ELAFP_FILTER_KEYWORD.toLowerCase();
  }

  /**
   * Switch the school list into (or out of) subscription keyword mode.
   *
   * Keyword mode and a specific picked subscription answer the same question two
   * different ways, so they are mutually exclusive: turning a keyword on drops the
   * picked subscription and remounts SubscriptionPicker (via pickerKey) so its own
   * input text goes with it.
   */
  setSchoolSubscriptionKeyword = (keyword) => {
    const next = (keyword || "").trim();
    const pickerKey = next !== ""
      ? this.state.schoolSubscriptionPickerKey + 1
      : this.state.schoolSubscriptionPickerKey;
    const subscriptionFilter = next !== "" ? null : this.state.schoolSubscriptionFilter;

    // The "matched subscription" column only exists while the keyword filter is
    // on, and the server cannot order by it — leaving it selected would make the
    // next request silently fall back to id while the header still claimed it.
    const filters = (next === "" && isKeywordOnlySortColumn(this.state.schoolFilters.order_by))
      ? { ...this.state.schoolFilters, order_by: DEFAULT_SCHOOL_SORT_BY, order: DEFAULT_SCHOOL_SORT_DIR }
      : this.state.schoolFilters;

    this.setState({
      schoolSubscriptionKeyword: next,
      schoolSubscriptionFilter: subscriptionFilter,
      schoolSubscriptionPickerKey: pickerKey,
      schoolFilters: filters,
    });
    this.state.schoolSubscriptionKeyword = next;
    this.state.schoolSubscriptionFilter = subscriptionFilter;
    this.state.schoolSubscriptionPickerKey = pickerKey;
    this.state.schoolFilters = filters;

    this.handleSchoolSearchClicked();
  }

  /** eLAFP quick filter — a preset for the generic subscription keyword mode. */
  handleElafpFilterToggle = () => {
    this.setSchoolSubscriptionKeyword(this.isElafpFilterActive() ? "" : ELAFP_FILTER_KEYWORD);
  }

  /**
   * Header-click sort, following the Subscriptions tab: re-sort the rows already
   * on screen immediately, then re-request so the server's window matches the
   * column that was clicked (the list is paged at 50–500). The re-request is
   * quiet, or the instant re-sort would be replaced by a loading card.
   */
  handleSchoolSort = (column) => {
    const next = nextSchoolSort(
      this.state.schoolFilters.order_by,
      this.state.schoolFilters.order,
      column,
    );
    const filters = { ...this.state.schoolFilters, order_by: next.sortBy, order: next.sortDir };
    this.setState({ schoolFilters: filters });
    this.state.schoolFilters = filters;

    if (this.isSchoolSubscriptionKeywordActive()) {
      // Keyword mode never had a server sort to re-window, and its rows were
      // already trimmed to the result limit — so re-sort the FULL merged list
      // and re-slice. Sorting the trimmed page alone would show the wrong top-N.
      this.applySchoolKeywordResults(
        this.state.schoolKeywordAllResults,
        this.schoolSearchRequestId,
        { quiet: true },
      );
      return;
    }

    const sorted = sortSchoolRows(this.state.schoolSearchResults, next.sortBy, next.sortDir);
    this.setState({ schoolSearchResults: sorted });
    this.state.schoolSearchResults = sorted;

    // With one subscription picked the rows come from get_schools_by_subscription,
    // which hardcodes ORDER BY s.id DESC and applies no limit. Re-requesting would
    // hand back that fixed order and undo the sort just applied — and there is
    // nothing to re-window, since these rows are already the whole result set.
    if (!this.state.schoolSubscriptionFilter) {
      this.handleSchoolSearchClicked({ quiet: true });
    }
  }

  /**
   * The school-side filters as both list commands take them: search text and
   * school_type, plus the sort and the window. Shared with the CSV export, which
   * re-runs the plain list at its own row ceiling — the two must not drift, or
   * the file would answer a different question from the table above it.
   *
   * `exclude_expired` is NOT here: only the two subscription-filtered searches
   * may send it, so it stays at the call site that knows one is on.
   */
  buildSchoolListRequest(overrides = {}) {
    const requestObj = {
      order_by: this.state.schoolFilters.order_by,
      order: this.state.schoolFilters.order || DEFAULT_SORT_ORDER,
      limit: parseInt(this.state.schoolFilters.limit) || 100,
      // Asks for last_subscription_update and the wider sort allow-set. Every
      // other caller of these two commands keeps the narrower PHP-shaped rows.
      admin_view: true,
    };

    if (this.state.schoolSearchText && this.state.schoolSearchText.trim() !== "") {
      requestObj.keyword = this.state.schoolSearchText.trim();
    }

    if (this.state.schoolFilters.school_type !== "" && this.state.schoolFilters.school_type != null) {
      requestObj.school_type = parseInt(this.state.schoolFilters.school_type);
    }

    return { ...requestObj, ...overrides };
  }

  handleSchoolSearchClicked = (options) => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }

    // Button onClick passes a synthetic event; only { quiet: true } from
    // handleSchoolSort should skip the loading flash and keep rows on screen.
    const quiet = !!(options && options.quiet === true && !options.nativeEvent);

    const subscriptionKeyword = (this.state.schoolSubscriptionKeyword || "").trim();
    const hasSubscriptionFilter = this.state.schoolSubscriptionFilter && this.state.schoolSubscriptionFilter.id;

    let requestObj = this.buildSchoolListRequest();

    // Keyword mode issues N+1 requests, so every callback has to prove it belongs
    // to the newest search before it may touch state — otherwise a slow earlier
    // search lands on top of a faster later one.
    const requestId = this.schoolSearchRequestId + 1;
    this.schoolSearchRequestId = requestId;

    if (!quiet) {
      this.setState({
        schoolSearchResults: [],
        schoolKeywordAllResults: [],
        schoolLoading: true,
      });
      this.state.schoolSearchResults = [];
      this.state.schoolKeywordAllResults = [];
      this.state.schoolLoading = true;
    }

    if (subscriptionKeyword !== "" || hasSubscriptionFilter) {
      // Only get_schools_by_subscription understands this, and only it should:
      // the unfiltered list answers "every school", where a lapsed grant is no
      // reason to hide one. "Holds subscription X" is the question that has a
      // tense, and it was being answered as "was ever granted X".
      requestObj.exclude_expired = !this.state.schoolFilters.include_expired;
    }

    if (subscriptionKeyword !== "") {
      this.runSubscriptionKeywordSearch(subscriptionKeyword, requestObj, requestId);
    } else if (hasSubscriptionFilter) {
      requestObj.subscription_id = this.state.schoolSubscriptionFilter.id;
      this.props.root.refs.API.request(
        "get_schools_by_subscription",
        requestObj,
        // That command ignores order_by / order, so the chosen sort has to be
        // applied here or the table would always come back in id-DESC order.
        (res) => this.schoolSearchOK(res, requestId, { clientSort: true }),
        (err) => this.schoolSearchFailed(err, requestId),
      );
    } else {
      this.props.root.refs.API.request(
        "get_filtered_school_list",
        requestObj,
        (res) => this.schoolSearchOK(res, requestId),
        (err) => this.schoolSearchFailed(err, requestId),
      );
    }
  }

  /**
   * Subscription KEYWORD mode, resolved entirely on the client so it needs no new
   * backend route: subscription_get for the matching subscription ids, then one
   * get_schools_by_subscription per id, merged and de-duplicated by school id.
   *
   * A school may hold more than one grant in the same family (the 2024 Pri/Sec
   * pair, or a 2024 grant plus a 26/27 one) — hence the merge rather than a
   * concatenation.
   *
   * A PRESET keyword (see ELAFP_FILTER_PRESET) resolves to several keywords and
   * is then narrowed to NAME matches on the client, because subscription_get's
   * single LIKE spans remarks and cannot express "named for this programme".
   * A plain keyword keeps the server's own three-field match, unchanged.
   *
   * `requestObj` carries the school-side filters (search text, school_type),
   * which get_schools_by_subscription applies per call. It does NOT apply
   * order_by / order / limit — that command hardcodes ORDER BY s.id DESC — so
   * those are applied here, on the merged list, in applySchoolKeywordResults.
   */
  runSubscriptionKeywordSearch = (subscriptionKeyword, requestObj, requestId) => {
    const preset = subscriptionKeywordPreset(subscriptionKeyword);
    const keywords = preset ? preset.keywords : [subscriptionKeyword];

    const byId = new Map();
    let pendingLookups = keywords.length;
    let capped = false;
    let lookupError = null;

    const lookupsSettled = () => {
      pendingLookups -= 1;
      if (pendingLookups > 0 || requestId !== this.schoolSearchRequestId) {
        return;
      }
      // One failed lookup means an unknowable hole in the id set, and half an
      // eLAFP list looks exactly like a complete one. Fail the search instead.
      if (lookupError) {
        this.schoolSearchFailed(lookupError, requestId);
        return;
      }
      if (capped) {
        const template = Localization.strings.SCHOOL_SUBSCRIPTION_KEYWORD_CAPPED
          || '訂閱查詢已達 {0} 個上限，較新的訂閱可能未列入';
        this.props.root.addNotification(
          template.replace('{0}', SUBSCRIPTION_KEYWORD_MAX_SUBSCRIPTIONS),
          false,
        );
      }

      const subs = [...byId.values()].sort((a, b) => a.id - b.id);
      if (subs.length === 0) {
        this.applySchoolKeywordResults([], requestId);
        const template = Localization.strings.SCHOOL_SUBSCRIPTION_KEYWORD_NO_MATCH
          || '沒有訂閱符合「{0}」';
        this.props.root.addNotification(template.replace('{0}', subscriptionKeyword), false);
        return;
      }
      this.fanOutSchoolsBySubscriptions(subs, requestObj, requestId);
    };

    keywords.forEach((keyword) => {
      this.props.root.refs.API.request(
        "subscription_get",
        {
          keyword,
          limit: SUBSCRIPTION_KEYWORD_MAX_SUBSCRIPTIONS,
          sort_by: "id",
          sort_dir: "ASC",
        },
        (res) => {
          if (requestId !== this.schoolSearchRequestId) {
            lookupsSettled();
            return;
          }
          const page = Array.isArray(res?.body?.result) ? res.body.result : [];
          // The cap is read off the RAW page: the server decides how much it
          // returns, and a preset narrowing it afterwards must not disguise a
          // full page as a short one.
          if (page.length >= SUBSCRIPTION_KEYWORD_MAX_SUBSCRIPTIONS) {
            capped = true;
          }
          filterSubscriptionsForPreset(page, preset).forEach((sub) => {
            const id = parseInt(sub.id, 10);
            byId.set(id, { ...sub, id });
          });
          lookupsSettled();
        },
        (err) => {
          lookupError = lookupError || err;
          lookupsSettled();
        },
      );
    });
  }

  /**
   * One get_schools_by_subscription per subscription, merged by school id, each
   * row carrying the labels of every subscription it came back from.
   *
   * Individual failures are tolerated but announced — with dozens of requests in
   * flight a single hiccup should not void the list, and an unannounced hole
   * would read as "these are all the schools".
   */
  fanOutSchoolsBySubscriptions = (subs, requestObj, requestId) => {
    const API = this.props.root.refs.API;
    const merged = new Map();
    let pending = subs.length;
    let failed = 0;

    const settle = () => {
      pending -= 1;
      if (pending > 0 || requestId !== this.schoolSearchRequestId) {
        return;
      }
      this.applySchoolKeywordResults([...merged.values()], requestId);
      if (failed > 0) {
        const template = Localization.strings.SCHOOL_SUBSCRIPTION_KEYWORD_PARTIAL
          || '{1} 個訂閱中有 {0} 個載入失敗，列表可能不完整';
        this.props.root.addNotification(
          template.replace('{0}', failed).replace('{1}', subs.length),
          false,
        );
      }
    };

    subs.forEach((sub) => {
      API.request(
        "get_schools_by_subscription",
        { ...requestObj, subscription_id: sub.id },
        (subRes) => {
          if (requestId === this.schoolSearchRequestId) {
            const rows = Array.isArray(subRes?.body?.result) ? subRes.body.result : [];
            rows.forEach((school) => {
              if (!school || school.id == null) {
                return;
              }
              const key = String(school.id);
              const existing = merged.get(key);
              if (existing) {
                existing.matched_subscriptions.push(subscriptionLabel(sub));
              } else {
                merged.set(key, {
                  ...school,
                  matched_subscriptions: [subscriptionLabel(sub)],
                });
              }
            });
          }
          settle();
        },
        () => {
          failed += 1;
          settle();
        },
      );
    });
  }

  /**
   * Publish a client-merged school list: sort by the chosen order, then apply the
   * chosen result limit. Truncation is announced rather than silent — otherwise a
   * capped list reads as "these are all the schools".
   */
  applySchoolKeywordResults = (rows, requestId, options) => {
    if (requestId !== this.schoolSearchRequestId) {
      return;
    }
    const sorted = sortSchoolRows(
      rows,
      this.state.schoolFilters.order_by,
      this.state.schoolFilters.order,
    );
    const limit = parseInt(this.state.schoolFilters.limit, 10) || 100;
    const truncated = sorted.length > limit;
    const visible = truncated ? sorted.slice(0, limit) : sorted;

    this.setState({
      // The untrimmed list is kept so a later header click can re-sort and
      // re-slice from the whole set rather than from this page.
      schoolKeywordAllResults: sorted,
      schoolSearchResults: visible,
      hasSchoolSearched: true,
      schoolLoading: false,
    });
    this.state.schoolKeywordAllResults = sorted;
    this.state.schoolSearchResults = visible;
    this.state.hasSchoolSearched = true;
    this.state.schoolLoading = false;

    // Re-sorts of an already-reported list don't repeat the truncation toast.
    if (truncated && !(options && options.quiet === true)) {
      const template = Localization.strings.SCHOOL_SUBSCRIPTION_KEYWORD_TRUNCATED
        || '只顯示 {1} 間學校中的前 {0} 間，請調高結果數量上限';
      this.props.root.addNotification(
        template.replace('{0}', limit).replace('{1}', sorted.length),
        false,
      );
    }
  }

  schoolSearchOK = (res, requestId, options) => {
    console.log("school search ok", res);
    if (requestId != null && requestId !== this.schoolSearchRequestId) {
      return;
    }
    let schoolList = res.body['result'];

    if (Array.isArray(schoolList)) {
      if (options && options.clientSort === true) {
        schoolList = sortSchoolRows(
          schoolList,
          this.state.schoolFilters.order_by,
          this.state.schoolFilters.order,
        );
      }
      this.setState({
        schoolSearchResults: schoolList,
        hasSchoolSearched: true,
        schoolLoading: false,
      });
      this.state.schoolSearchResults = schoolList;
      this.state.hasSchoolSearched = true;
      this.state.schoolLoading = false;
    } else {
      this.setState({
        schoolSearchResults: [],
        hasSchoolSearched: true,
        schoolLoading: false,
      });
      this.state.schoolSearchResults = [];
      this.state.hasSchoolSearched = true;
      this.state.schoolLoading = false;
      this.props.root.addNotification("搜尋結果為空", false);
    }
  }

  schoolSearchFailed = (err, requestId) => {
    console.log("school search failed", err);
    if (requestId != null && requestId !== this.schoolSearchRequestId) {
      return;
    }
    this.setState({
      schoolSearchResults: [],
      hasSchoolSearched: true,
      schoolLoading: false,
    });
    this.state.schoolSearchResults = [];
    this.state.hasSchoolSearched = true;
    this.state.schoolLoading = false;
    this.props.root.addNotification("搜尋失敗", false);
  }

  handleSchoolSearchEnterPressed = (e) => {
    if (e.key === "Enter") {
      this.handleSchoolSearchClicked();
    }
  }

  handleSchoolRefreshClicked = () => {
    this.handleSchoolSearchClicked();
  }

  /**
   * Export the School List as CSV — the WHOLE filtered set, not the page.
   *
   * The three searches already hold different amounts of their own answer, so
   * each supplies the export differently (schoolResultCount.js has the same
   * three-way split, for the same reason):
   *
   *   * SUBSCRIPTION KEYWORD mode keeps the entire merged union in
   *     `schoolKeywordAllResults` and shows a slice of it, so the union is what
   *     gets exported — matching the "顯示 42 間，共 84 間" the count line prints;
   *   * ONE PICKED SUBSCRIPTION already has every row on screen, because
   *     get_schools_by_subscription applies no LIMIT. Those rows carry no
   *     `matched_subscriptions` (only the client-side keyword merge attaches
   *     it), so the picked subscription's name is written in here — otherwise
   *     the file records nothing about the filter that produced it;
   *   * THE PLAIN LIST is windowed by the server, so what is on screen is one
   *     50–500 page of a set that is usually far larger. Exporting that page
   *     would hand over 100 of N schools under a button that says it exports
   *     the filtered records, so it re-requests at SCHOOL_EXPORT_MAX_ROWS.
   *
   * Only that last case can be incomplete, and only in one way — coming back
   * exactly full — which is reported rather than trusted.
   *
   * REACHABLE AT ANY TIME, including before the first search: the button carries
   * no disabled state (see the comment on it), so this is the one place that
   * knows whether the click can be answered. The plain list always can — it
   * fetches. The two subscription modes cannot while their own search is still
   * in flight, and that is the ONE case worth a word of its own.
   */
  handleSchoolExportClicked = () => {
    if (this.state.schoolExporting) {
      return;
    }
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }

    const picked = this.state.schoolSubscriptionFilter;
    const keywordMode = this.isSchoolSubscriptionKeywordActive();
    // An unfinished search and a filter that genuinely matches nothing arrive
    // here as the SAME empty array, and only one of them is worth waiting out —
    // so the two cache-served modes name the difference instead of letting
    // finishSchoolExport report "nothing to export" for both. The plain list
    // needs no such guard: it re-requests, so a click mid-search still answers.
    if ((keywordMode || !!(picked && picked.id)) && this.state.schoolLoading) {
      this.props.root.addNotification(
        Localization.strings.SCHOOL_EXPORT_SEARCHING || '搜尋尚未完成，請稍候再匯出',
        false,
      );
      return;
    }

    if (keywordMode) {
      this.finishSchoolExport(this.state.schoolKeywordAllResults);
      return;
    }

    if (picked && picked.id) {
      const label = subscriptionLabel(picked);
      this.finishSchoolExport(
        (this.state.schoolSearchResults || []).map(
          (school) => ({ ...school, matched_subscriptions: [label] }),
        ),
      );
      return;
    }

    this.setState({ schoolExporting: true });
    this.state.schoolExporting = true;
    const done = () => {
      this.setState({ schoolExporting: false });
      this.state.schoolExporting = false;
    };
    this.props.root.refs.API.request(
      "get_filtered_school_list",
      this.buildSchoolListRequest({ limit: SCHOOL_EXPORT_MAX_ROWS }),
      (res) => {
        done();
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        if (rows.length >= SCHOOL_EXPORT_MAX_ROWS) {
          const template = Localization.strings.SCHOOL_EXPORT_CAPPED
            || '匯出上限為 {0} 間學校，可能未完 — 請收窄篩選條件';
          this.props.root.addNotification(
            template.replace('{0}', SCHOOL_EXPORT_MAX_ROWS),
            false,
          );
        }
        this.finishSchoolExport(rows);
      },
      (err) => {
        done();
        this.props.root.addNotification(
          err?.body?.remarks || Localization.strings.SCHOOL_EXPORT_FAILED || '匯出失敗',
          false,
        );
      },
    );
  }

  /** Build the CSV and hand it to the browser; an empty set is said, not saved. */
  finishSchoolExport(rows) {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
      this.props.root.addNotification(
        Localization.strings.SCHOOL_EXPORT_EMPTY || '沒有可匯出的學校',
        false,
      );
      return;
    }
    this.downloadCsvFile(buildSchoolListCsv(list), schoolListCsvFileName());
    this.props.root.addNotification(
      (Localization.strings.SCHOOL_EXPORT_DONE || '已匯出 {0} 間學校').replace('{0}', list.length),
      true,
    );
  }

  openSchoolSubscriptionDialog = (school) => {
    if (!school || !school.id) return;
    const dialog = {
      open: true,
      school,
      rows: [],
      loading: true,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: Date.now(),
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.setState({
      schoolNavigationDirection: 'forward',
      schoolSubscriptionDialog: dialog,
    });
    this.state.schoolSubscriptionDialog = dialog;
    this.state.schoolNavigationDirection = 'forward';

    this.fetchSchoolSubscriptions(school.id);
  }

  closeSchoolSubscriptionDialog = () => {
    const dialog = {
      open: false,
      school: null,
      rows: [],
      loading: false,
      addSelected: null,
      addLoading: false,
      addDateStart: getTodayDateString(),
      addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
      pickerKey: Date.now(),
      editingRow: null,
      editingField: null,
      updateLoading: false,
      deleteLoading: false,
      deleteConfirmRow: null,
    };
    this.setState({
      schoolNavigationDirection: 'back',
      schoolSubscriptionDialog: dialog,
    });
    this.state.schoolSubscriptionDialog = dialog;
    this.state.schoolNavigationDirection = 'back';
  }

  fetchSchoolSubscriptions = (schoolId) => {
    const targetId = schoolId || (this.state.schoolSubscriptionDialog.school ? this.state.schoolSubscriptionDialog.school.id : 0);
    if (!targetId || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        loading: true,
      }
    });
    this.state.schoolSubscriptionDialog.loading = true;
    this.props.root.refs.API.request(
      "get_subscriptions_by_school_id",
      { school_id: targetId, include_inactive: true },
      this.schoolSubscriptionListOK,
      this.schoolSubscriptionListFailed
    );
  }

  schoolSubscriptionListOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        rows,
        loading: false,
      }
    });
    this.state.schoolSubscriptionDialog.rows = rows;
    this.state.schoolSubscriptionDialog.loading = false;
  }

  schoolSubscriptionListFailed = (err) => {
    console.log("school subscription list failed", err);
    this.setState({
      schoolSubscriptionDialog: {
        ...this.state.schoolSubscriptionDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.schoolSubscriptionDialog.rows = [];
    this.state.schoolSubscriptionDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_LOAD_FAILED || "載入訂閱失敗", false);
  }

  handleSchoolSubscriptionAdd = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    if (!dialog.school || !dialog.school.id) return;
    if (!dialog.addSelected || !dialog.addSelected.id) {
      this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_SELECT || "請先選擇訂閱", false);
      return;
    }
    const dateExpire = getSubscriptionEndDate(dialog.addDateStart, dialog.addDurationMonths);
    if (!dialog.addDateStart || !dateExpire) {
      this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_START_REQUIRED || "請選擇開始日", false);
      return;
    }
    const payload = {
      school_id: dialog.school.id,
      subscription_id: dialog.addSelected.id,
      date_start: dialog.addDateStart,
      date_expire: dateExpire,
    };
    this.setState({
      schoolSubscriptionDialog: {
        ...dialog,
        addLoading: true,
      }
    });
    this.state.schoolSubscriptionDialog.addLoading = true;
    this.props.root.refs.API.request(
      "school_subscription_add",
      payload,
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_ADDED || "已加入訂閱", true);
        const resetDialog = {
          ...this.state.schoolSubscriptionDialog,
          addSelected: null,
          addLoading: false,
          addDateStart: getTodayDateString(),
          addDurationMonths: DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
          pickerKey: Date.now(),
        };
        this.setState({ schoolSubscriptionDialog: resetDialog });
        this.state.schoolSubscriptionDialog = resetDialog;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_LOAD_FAILED || "新增失敗";
        this.props.root.addNotification(msg, false);
        this.setState({
          schoolSubscriptionDialog: {
            ...this.state.schoolSubscriptionDialog,
            addLoading: false,
          }
        });
        this.state.schoolSubscriptionDialog.addLoading = false;
      }
    );
  }

  handleSchoolSubscriptionEditStart = (row, editingField) => {
    const editingRow = {
      subscription_id: row.subscription_id,
      school_subscription_id: row.school_subscription_id,
      date_start: row.date_start || '',
      date_expire: row.date_expire || '',
      remark: row.school_subscription_remark || '',
    };
    const updated = { ...this.state.schoolSubscriptionDialog, editingRow, editingField };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionEditCancel = () => {
    const updated = { ...this.state.schoolSubscriptionDialog, editingRow: null, editingField: null };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionEditBlur = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (this.state.schoolSubscriptionDialog.updateLoading) return;

    this.handleSchoolSubscriptionUpdate();
  }

  handleSchoolSubscriptionUpdate = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    if (!dialog.school || !dialog.editingRow) return;
    const { school_subscription_id, date_start, date_expire, remark } = dialog.editingRow;
    const payload = {
      // school_id is part of the route path on the new surface
      // (PATCH /api/school/{school_id}/subscription/{school_subscription_id}),
      // so it has to be in the payload even though the dialog already holds it.
      school_id: dialog.school.id,
      school_subscription_id,
      date_start,
      date_expire,
      remark,
    };
    const updated = { ...dialog, updateLoading: true };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog.updateLoading = true;
    this.props.root.refs.API.request(
      "school_subscription_update",
      payload,
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_UPDATED || "訂閱記錄已更新", true);
        const reset = { ...this.state.schoolSubscriptionDialog, editingRow: null, editingField: null, updateLoading: false };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_UPDATE_FAILED || "更新失敗";
        this.props.root.addNotification(msg, false);
        const reset = { ...this.state.schoolSubscriptionDialog, updateLoading: false };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog.updateLoading = false;
      }
    );
  }

  handleSchoolSubscriptionDelete = (row) => {
    const updated = { ...this.state.schoolSubscriptionDialog, deleteConfirmRow: row };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionDeleteCancel = () => {
    const updated = { ...this.state.schoolSubscriptionDialog, deleteConfirmRow: null };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
  }

  handleSchoolSubscriptionDeleteConfirm = () => {
    const dialog = this.state.schoolSubscriptionDialog;
    const row = dialog.deleteConfirmRow;
    if (!row) return;
    const updated = { ...dialog, deleteLoading: true };
    this.setState({ schoolSubscriptionDialog: updated });
    this.state.schoolSubscriptionDialog = updated;
    this.props.root.refs.API.request(
      "school_subscription_delete",
      {
        // school_id is part of the route path on the new surface
        // (DELETE /api/school/{school_id}/subscription/{school_subscription_id}).
        school_id: dialog.school ? dialog.school.id : undefined,
        school_subscription_id: row.school_subscription_id,
      },
      () => {
        this.props.root.addNotification(Localization.strings.SCHOOL_SUBSCRIPTION_DELETED || "訂閱已移除", true);
        const reset = { ...this.state.schoolSubscriptionDialog, deleteLoading: false, deleteConfirmRow: null };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
        this.fetchSchoolSubscriptions(dialog.school.id);
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SCHOOL_SUBSCRIPTION_DELETE_FAILED || "移除失敗";
        this.props.root.addNotification(msg, false);
        const reset = { ...this.state.schoolSubscriptionDialog, deleteLoading: false, deleteConfirmRow: null };
        this.setState({ schoolSubscriptionDialog: reset });
        this.state.schoolSubscriptionDialog = reset;
      }
    );
  }

  // ------------------------------------------------------------------
  // EdCity Login > EdCity School ID Mapping (school_edcity_scrn)
  // ------------------------------------------------------------------

  edcityScrnSearchTextChanged = (e) => {
    this.setState({ edcityScrnSearchText: e.target.value });
    this.state.edcityScrnSearchText = e.target.value;
  }

  handleEdcityScrnSearchEnterPressed = (e) => {
    if (e.key === "Enter") {
      this.handleEdcityScrnSearch();
    }
  }

  handleEdcityScrnMappedFilterChanged = (value) => {
    const next = value || "ALL";
    this.setState({ edcityScrnMappedFilter: next });
    this.state.edcityScrnMappedFilter = next;
    this.handleEdcityScrnSearch();
  }

  handleEdcityScrnSort = (column) => {
    const next = nextEdcityScrnSort(
      this.state.edcityScrnSortBy,
      this.state.edcityScrnSortDir,
      column,
    );
    const sorted = sortEdcityScrnRows(this.state.edcityScrnRows, next.sortBy, next.sortDir);
    this.setState({
      edcityScrnSortBy: next.sortBy,
      edcityScrnSortDir: next.sortDir,
      edcityScrnRows: sorted,
    });
    this.state.edcityScrnSortBy = next.sortBy;
    this.state.edcityScrnSortDir = next.sortDir;
    this.state.edcityScrnRows = sorted;
    this.handleEdcityScrnSearch({ quiet: true });
  }

  handleEdcityScrnSearch = (options) => {
    if (!this.props.root || !this.props.root.refs || !this.props.root.refs.API) {
      return;
    }
    // Button onClick passes a synthetic event; only { quiet: true } from
    // handleEdcityScrnSort should skip the loading flash.
    const quiet = !!(options && options.quiet === true && !options.nativeEvent);
    const requestObj = {
      limit: 500,
      order_by: this.state.edcityScrnSortBy || DEFAULT_EDCITY_SCRN_SORT_BY,
      order: this.state.edcityScrnSortDir || DEFAULT_EDCITY_SCRN_SORT_DIR,
    };
    if (this.state.edcityScrnSearchText && this.state.edcityScrnSearchText.trim() !== "") {
      requestObj.keyword = this.state.edcityScrnSearchText.trim();
    }
    if (this.state.edcityScrnMappedFilter === "1" || this.state.edcityScrnMappedFilter === "0") {
      requestObj.mapped = parseInt(this.state.edcityScrnMappedFilter);
    }
    if (!quiet) {
      this.setState({ edcityScrnRows: [], edcityScrnLoading: true });
      this.state.edcityScrnRows = [];
      this.state.edcityScrnLoading = true;
    }
    this.props.root.refs.API.request("admin_edcity_scrn_list", requestObj, this.edcityScrnSearchOK, this.edcityScrnSearchFailed);
  }

  edcityScrnSearchOK = (res) => {
    const rows = sortEdcityScrnRows(
      Array.isArray(res?.body?.result) ? res.body.result : [],
      this.state.edcityScrnSortBy,
      this.state.edcityScrnSortDir,
    );
    this.setState({
      edcityScrnRows: rows,
      hasEdcityScrnSearched: true,
      edcityScrnLoading: false,
    });
    this.state.edcityScrnRows = rows;
    this.state.hasEdcityScrnSearched = true;
    this.state.edcityScrnLoading = false;
  }

  edcityScrnSearchFailed = (err) => {
    console.log("edcity scrn list failed", err);
    const keepRows = this.state.hasEdcityScrnSearched && this.state.edcityScrnRows.length > 0;
    this.setState({
      edcityScrnRows: keepRows ? this.state.edcityScrnRows : [],
      hasEdcityScrnSearched: true,
      edcityScrnLoading: false,
    });
    if (!keepRows) {
      this.state.edcityScrnRows = [];
    }
    this.state.hasEdcityScrnSearched = true;
    this.state.edcityScrnLoading = false;
    if (!keepRows) {
      this.props.root.addNotification(err?.body?.remarks || Localization.strings.EDCITY_SCRN_LOAD_FAILED || '載入對接列表失敗', false);
    }
  }

  // Jump from a mapped row to the School List tab, searched for that school.
  gotoSchoolListForEdcityRow = (row) => {
    const keyword = row.school_name || row.school_name_eng || "";
    if (!keyword) {
      return;
    }
    // Clear school-list filters that could hide the school, close any school
    // sub-page left open, and suppress ensureActiveTabData's stale auto-search
    // (we run our own with the keyword already in place).
    const filters = { ...this.state.schoolFilters, school_type: "" };
    const editSchoolDialog = this.state.editSchoolDialog.open
      ? { ...this.state.editSchoolDialog, open: false }
      : this.state.editSchoolDialog;
    const schoolSubscriptionDialog = this.state.schoolSubscriptionDialog.open
      ? { ...this.state.schoolSubscriptionDialog, open: false }
      : this.state.schoolSubscriptionDialog;
    this.setState({
      schoolSearchText: keyword,
      schoolFilters: filters,
      schoolSubscriptionFilter: null,
      schoolSubscriptionKeyword: "",
      hasSchoolSearched: true,
      editSchoolDialog,
      schoolSubscriptionDialog,
    });
    this.state.schoolSearchText = keyword;
    this.state.schoolFilters = filters;
    this.state.schoolSubscriptionFilter = null;
    this.state.schoolSubscriptionKeyword = "";
    this.state.hasSchoolSearched = true;
    this.state.editSchoolDialog = editSchoolDialog;
    this.state.schoolSubscriptionDialog = schoolSubscriptionDialog;
    this.changeTab(3);
    this.handleSchoolSearchClicked();
  }

  // Options for the school pickers, from the school list loaded at mount.
  getEdcitySchoolSelectData = () => {
    const list = this.state.schoolList || [];
    return list.map((s) => ({
      value: String(s.school_id),
      label: `[${s.school_id}] ${s.shcool_name || '-'}${s.school_eng_name ? ' / ' + s.school_eng_name : ''}`,
    }));
  }

  // ----- create dialog -----

  openEdcityScrnCreateDialog = () => {
    if ((this.state.schoolList || []).length === 0) {
      this.loadSchoolList();
    }
    const dialog = createEdcityScrnCreateDialogState({ open: true });
    this.setState({ edcityScrnCreateDialog: dialog });
    this.state.edcityScrnCreateDialog = dialog;
  }

  closeEdcityScrnCreateDialog = () => {
    if (this.state.edcityScrnCreateDialog.loading) {
      return;
    }
    const dialog = createEdcityScrnCreateDialogState();
    this.setState({ edcityScrnCreateDialog: dialog });
    this.state.edcityScrnCreateDialog = dialog;
  }

  handleEdcityScrnCreateFieldChange = (field) => (e) => {
    const dialog = { ...this.state.edcityScrnCreateDialog, [field]: e.target.value };
    this.setState({ edcityScrnCreateDialog: dialog });
    this.state.edcityScrnCreateDialog = dialog;
  }

  handleEdcityScrnCreateSchoolChange = (value) => {
    const dialog = { ...this.state.edcityScrnCreateDialog, school_id: value || "" };
    this.setState({ edcityScrnCreateDialog: dialog });
    this.state.edcityScrnCreateDialog = dialog;
  }

  handleEdcityScrnCreateSubmit = () => {
    const dialog = this.state.edcityScrnCreateDialog;
    const code = (dialog.school_code || "").trim();
    if (!/^\d{12}$/.test(code)) {
      this.props.root.addNotification(Localization.strings.EDCITY_SCRN_CODE_HINT || 'SCRN 須為 12 位數字', false);
      return;
    }
    const requestObj = {
      school_code: code,
      note: (dialog.note || "").trim(),
    };
    if (dialog.school_id) {
      requestObj.school_id = parseInt(dialog.school_id);
    }
    const loadingDialog = { ...dialog, loading: true };
    this.setState({ edcityScrnCreateDialog: loadingDialog });
    this.state.edcityScrnCreateDialog = loadingDialog;
    this.props.root.refs.API.request(
      "admin_edcity_scrn_create",
      requestObj,
      (res) => {
        if (res.body && res.body.result == 1) {
          this.props.root.addNotification(Localization.strings.EDCITY_SCRN_CREATE_OK || '已新增對接記錄', true);
          const reset = createEdcityScrnCreateDialogState();
          this.setState({ edcityScrnCreateDialog: reset });
          this.state.edcityScrnCreateDialog = reset;
          this.handleEdcityScrnSearch();
        } else {
          this.edcityScrnCreateFailed(res);
        }
      },
      this.edcityScrnCreateFailed
    );
  }

  edcityScrnCreateFailed = (err) => {
    console.log("edcity scrn create failed", err);
    const dialog = { ...this.state.edcityScrnCreateDialog, loading: false };
    this.setState({ edcityScrnCreateDialog: dialog });
    this.state.edcityScrnCreateDialog = dialog;
    this.props.root.addNotification(err?.body?.remarks || Localization.strings.EDCITY_SCRN_CREATE_FAILED || '新增對接記錄失敗', false);
  }

  // ----- connect-with-school dialog -----

  openEdcityScrnConnectDialog = (row) => {
    if ((this.state.schoolList || []).length === 0) {
      this.loadSchoolList();
    }
    const dialog = createEdcityScrnConnectDialogState({
      open: true,
      row,
      school_id: (row.school_id && parseInt(row.school_id) > 0) ? String(row.school_id) : "",
      note: row.note || "",
    });
    this.setState({ edcityScrnConnectDialog: dialog });
    this.state.edcityScrnConnectDialog = dialog;
  }

  closeEdcityScrnConnectDialog = () => {
    if (this.state.edcityScrnConnectDialog.loading) {
      return;
    }
    const dialog = createEdcityScrnConnectDialogState();
    this.setState({ edcityScrnConnectDialog: dialog });
    this.state.edcityScrnConnectDialog = dialog;
  }

  handleEdcityScrnConnectSchoolChange = (value) => {
    const dialog = { ...this.state.edcityScrnConnectDialog, school_id: value || "" };
    this.setState({ edcityScrnConnectDialog: dialog });
    this.state.edcityScrnConnectDialog = dialog;
  }

  handleEdcityScrnConnectNoteChange = (e) => {
    const dialog = { ...this.state.edcityScrnConnectDialog, note: e.target.value };
    this.setState({ edcityScrnConnectDialog: dialog });
    this.state.edcityScrnConnectDialog = dialog;
  }

  // school_id 0 clears the connection; >0 connects.
  submitEdcityScrnConnect = (schoolId) => {
    const dialog = this.state.edcityScrnConnectDialog;
    if (!dialog.row) {
      return;
    }
    const loadingDialog = { ...dialog, loading: true };
    this.setState({ edcityScrnConnectDialog: loadingDialog });
    this.state.edcityScrnConnectDialog = loadingDialog;
    this.props.root.refs.API.request(
      "admin_edcity_scrn_update",
      {
        school_code: dialog.row.school_code,
        school_id: schoolId,
        note: (dialog.note || "").trim(),
      },
      (res) => {
        if (res.body && res.body.result == 1) {
          const okMsg = schoolId > 0
            ? (Localization.strings.EDCITY_SCRN_CONNECT_OK || '已對接學校')
            : (Localization.strings.EDCITY_SCRN_DISCONNECT_OK || '已清除對接');
          this.props.root.addNotification(okMsg, true);
          const reset = createEdcityScrnConnectDialogState();
          this.setState({ edcityScrnConnectDialog: reset });
          this.state.edcityScrnConnectDialog = reset;
          this.handleEdcityScrnSearch();
        } else {
          this.edcityScrnConnectFailed(res);
        }
      },
      this.edcityScrnConnectFailed
    );
  }

  handleEdcityScrnConnectSubmit = () => {
    const dialog = this.state.edcityScrnConnectDialog;
    if (!dialog.school_id) {
      this.props.root.addNotification(Localization.strings.EDCITY_SCRN_PICK_SCHOOL || '請選擇學校', false);
      return;
    }
    this.submitEdcityScrnConnect(parseInt(dialog.school_id));
  }

  handleEdcityScrnDisconnect = () => {
    this.submitEdcityScrnConnect(0);
  }

  edcityScrnConnectFailed = (err) => {
    console.log("edcity scrn update failed", err);
    const dialog = { ...this.state.edcityScrnConnectDialog, loading: false };
    this.setState({ edcityScrnConnectDialog: dialog });
    this.state.edcityScrnConnectDialog = dialog;
    this.props.root.addNotification(err?.body?.remarks || Localization.strings.EDCITY_SCRN_UPDATE_FAILED || '更新對接記錄失敗', false);
  }

  // ----- remove confirmation -----

  openEdcityScrnDeleteDialog = (row) => {
    this.setState({ edcityScrnDeleteRow: row });
    this.state.edcityScrnDeleteRow = row;
  }

  closeEdcityScrnDeleteDialog = () => {
    if (this.state.edcityScrnDeleteLoading) {
      return;
    }
    this.setState({ edcityScrnDeleteRow: null });
    this.state.edcityScrnDeleteRow = null;
  }

  handleEdcityScrnDeleteConfirm = () => {
    const row = this.state.edcityScrnDeleteRow;
    if (!row) {
      return;
    }
    this.setState({ edcityScrnDeleteLoading: true });
    this.state.edcityScrnDeleteLoading = true;
    this.props.root.refs.API.request(
      "admin_edcity_scrn_delete",
      { school_code: row.school_code },
      (res) => {
        if (res.body && res.body.result == 1) {
          this.props.root.addNotification(Localization.strings.EDCITY_SCRN_REMOVE_OK || '已移除對接記錄', true);
          this.setState({ edcityScrnDeleteRow: null, edcityScrnDeleteLoading: false });
          this.state.edcityScrnDeleteRow = null;
          this.state.edcityScrnDeleteLoading = false;
          this.handleEdcityScrnSearch();
        } else {
          this.edcityScrnDeleteFailed(res);
        }
      },
      this.edcityScrnDeleteFailed
    );
  }

  edcityScrnDeleteFailed = (err) => {
    console.log("edcity scrn delete failed", err);
    this.setState({ edcityScrnDeleteRow: null, edcityScrnDeleteLoading: false });
    this.state.edcityScrnDeleteRow = null;
    this.state.edcityScrnDeleteLoading = false;
    this.props.root.addNotification(err?.body?.remarks || Localization.strings.EDCITY_SCRN_REMOVE_FAILED || '移除對接記錄失敗', false);
  }

  getSchoolTypeText(schoolType) {
    switch(parseInt(schoolType)) {
      case 0: return '-';
      case 1: return 'Pri';
      case 2: return 'Sec';
      case 3: return 'Uni/Gov';
      case 4: return 'Spec';
      case 5: return 'Mgmt';
      case 6: return 'Biz';
      default: return '-';
    }
  }

  getSchoolLevelText(schoolLevel) {
    switch(parseInt(schoolLevel)) {
      case 0: return '-';
      case 1: return 'Lv 1';
      case 2: return 'Lv 2';
      case 3: return 'Lv 3';
      case 4: return 'Lv 4';
      case 5: return 'Lv 5';
      default: return '-';
    }
  }

  formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  /**
   * ePack (locale) quota cell — the entitlement the admin_view join reports,
   * decided on the subscription END date (see the "School List display column"
   * section of composer_v2/docs/locale-subscription-quota.md).
   *
   * Prints USED / TOTAL. The column still SORTS on `epack_quota`, the remaining
   * balance, which is neither of the printed numbers and is meant to be: the
   * question this column exists to answer is "who is running out", and a ratio
   * cannot answer it (9 / 10 is nearly dry, 9 / 9999 is untouched). First click
   * is ASC, so the schools closest to a full ratio come to the top.
   *
   * Branch on the `epack_quota_unlimited` FLAG, never on epack_quota === 99999:
   * real balances reach that magnitude. The flag's name is the backend's
   * (enforcement really is unlimited for these schools), but the cell reports the
   * CAUSE rather than the effect — the school has never been assigned a plan —
   * because that is the admin's action item. A missing field means the backend
   * predates admin_view, so show "-" rather than a wrong zero.
   */
  renderEpackQuota(school) {
    if (!school || school.epack_quota == null) {
      return '-';
    }
    const notAssigned = String(school.epack_quota_unlimited) === '1';
    if (notAssigned) {
      return (
        <Badge variant="filled" color="red" size="sm">
          {Localization.strings.SCHOOL_EPACK_QUOTA_NOT_ASSIGNED || '尚未分配'}
        </Badge>
      );
    }
    // USED / TOTAL — spent over granted, the same reading as the
    // MAX_LOCALE_NUMBER card in SubscriptionDialog, so the two screens agree for
    // the single-plan schools that are nearly all of them. Red still marks the
    // blocked school; it is now a FULL ratio (15 / 15) where it used to be a
    // bare red 0, which is the same state said out loud.
    //
    // `epack_quota_total` is the deploy-order guard, and it is the reason this
    // does not just print `total - epack_quota`: a composer that ships ahead of
    // its backend gets no such field, and one number rendered as if it were the
    // other is worse than the old cell. Fall back to it instead. (The backend
    // sends `used` rather than letting us subtract, so an over-granted row —
    // school 326 carries 99998 against a max of 20 — cannot print a negative.)
    const quota = parseInt(school.epack_quota, 10) || 0;
    if (school.epack_quota_total == null) {
      return (
        <Badge variant="light" color={quota > 0 ? 'brand' : 'red'} size="sm">
          {quota}
        </Badge>
      );
    }
    const total = parseInt(school.epack_quota_total, 10) || 0;
    const used = parseInt(school.epack_quota_used, 10) || 0;
    return (
      <Badge variant="light" color={used < total ? 'brand' : 'red'} size="sm">
        {`${used} / ${total}`}
      </Badge>
    );
  }

  // Subscription tab handlers
  handleSubscriptionKeywordChange = (e) => {
    this.setState({ subscriptionSearchText: e.target.value });
    this.state.subscriptionSearchText = e.target.value;
  }

  handleSubscriptionSort = (column) => {
    const next = nextSubscriptionSort(
      this.state.subscriptionSortBy,
      this.state.subscriptionSortDir,
      column,
    );
    const sorted = sortSubscriptionRows(this.state.subscriptionList, next.sortBy, next.sortDir);
    this.setState({
      subscriptionSortBy: next.sortBy,
      subscriptionSortDir: next.sortDir,
      subscriptionList: sorted,
    });
    this.state.subscriptionSortBy = next.sortBy;
    this.state.subscriptionSortDir = next.sortDir;
    this.state.subscriptionList = sorted;
    this.handleSubscriptionSearch({ quiet: true });
  }

  handleSubscriptionLimitChange = (e) => {
    this.setState({ subscriptionLimit: e.target.value });
    this.state.subscriptionLimit = e.target.value;
    this.handleSubscriptionSearch();
  }

  handleSubscriptionSearchEnter = (e) => {
    if (e.key === 'Enter') {
      this.handleSubscriptionSearch();
    }
  }

  isSubscriptionTemplate = (value) => {
    return value === true || String(value) === '1';
  }

  handleSubscriptionNameEditStart = (row, field) => {
    const updateKey = `${row.id}:${field}`;
    if (this.state.subscriptionUpdatingFields[updateKey]) return;

    const editingCell = {
      id: row.id,
      field,
      value: row[field] || '',
      originalValue: row[field] || '',
      row,
    };
    this.setState({ subscriptionEditingCell: editingCell });
    this.state.subscriptionEditingCell = editingCell;
  }

  handleSubscriptionNameEditChange = (event) => {
    if (!this.state.subscriptionEditingCell) return;

    const editingCell = {
      ...this.state.subscriptionEditingCell,
      value: event.target.value,
    };
    this.setState({ subscriptionEditingCell: editingCell });
    this.state.subscriptionEditingCell = editingCell;
  }

  handleSubscriptionNameEditKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  handleSubscriptionNameEditCommit = () => {
    const editingCell = this.state.subscriptionEditingCell;
    if (!editingCell) return;

    const value = editingCell.value.trim();
    this.setState({ subscriptionEditingCell: null });
    this.state.subscriptionEditingCell = null;

    if (!value) {
      const message = editingCell.field === 'name'
        ? Localization.strings.SUBSCRIPTION_NAME_REQUIRED
        : Localization.strings.SUBSCRIPTION_NAME_EN_REQUIRED;
      this.props.root.addNotification(message, false);
      return;
    }

    if (value === String(editingCell.originalValue).trim()) return;
    this.updateSubscriptionField(editingCell.row, editingCell.field, value);
  }

  handleSubscriptionTemplateChange = (row, checked) => {
    this.updateSubscriptionField(row, 'is_template', checked ? 1 : 0);
  }

  updateSubscriptionField = (row, field, value) => {
    if (!row || !row.id || !this.props.root.refs?.API) return;

    const updateKey = `${row.id}:${field}`;
    if (this.state.subscriptionUpdatingFields[updateKey]) return;

    const previousValue = row[field];
    const subscriptionList = this.state.subscriptionList.map((item) => (
      item.id === row.id ? { ...item, [field]: value } : item
    ));
    const subscriptionUpdatingFields = {
      ...this.state.subscriptionUpdatingFields,
      [updateKey]: true,
    };
    this.setState({ subscriptionList, subscriptionUpdatingFields });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionUpdatingFields = subscriptionUpdatingFields;

    this.props.root.refs.API.request(
      "subscription_update",
      { id: row.id, [field]: value },
      () => {
        const nextUpdatingFields = { ...this.state.subscriptionUpdatingFields };
        delete nextUpdatingFields[updateKey];
        this.setState({ subscriptionUpdatingFields: nextUpdatingFields });
        this.state.subscriptionUpdatingFields = nextUpdatingFields;
        this.props.root.addNotification(
          Localization.strings.SUBSCRIPTION_UPDATE_SUCCESS || "Subscription updated",
          true
        );
      },
      (err) => {
        const revertedList = this.state.subscriptionList.map((item) => (
          item.id === row.id ? { ...item, [field]: previousValue } : item
        ));
        const nextUpdatingFields = { ...this.state.subscriptionUpdatingFields };
        delete nextUpdatingFields[updateKey];
        this.setState({
          subscriptionList: revertedList,
          subscriptionUpdatingFields: nextUpdatingFields,
        });
        this.state.subscriptionList = revertedList;
        this.state.subscriptionUpdatingFields = nextUpdatingFields;
        const message = err?.body?.remarks
          || err?.body?.detail
          || err?.body?.error
          || Localization.strings.SUBSCRIPTION_UPDATE_FAILED
          || "Failed to update subscription";
        this.props.root.addNotification(message, false);
      }
    );
  }

  handleSubscriptionSearch = (options) => {
    if (!this.props.root.refs || !this.props.root.refs.API) {
      return;
    }

    // Button onClick passes a synthetic event; only { quiet: true } from
    // handleSubscriptionSort should skip the loading flash and toast.
    const quiet = !!(options && options.quiet === true && !options.nativeEvent);
    this._subscriptionSearchQuiet = quiet;

    const payload = {
      sort_by: this.state.subscriptionSortBy || DEFAULT_SUBSCRIPTION_SORT_BY,
      sort_dir: this.state.subscriptionSortDir || DEFAULT_SUBSCRIPTION_SORT_DIR,
      limit: parseInt(this.state.subscriptionLimit) || 100,
    };

    if (this.state.subscriptionSearchText && this.state.subscriptionSearchText.trim() !== "") {
      payload.keyword = this.state.subscriptionSearchText.trim();
    }

    if (!quiet) {
      this.setState({
        subscriptionList: [],
        subscriptionLoading: true,
      });
      this.state.subscriptionList = [];
      this.state.subscriptionLoading = true;
    }

    this.props.root.refs.API.request("subscription_get", payload, this.subscriptionSearchOK, this.subscriptionSearchFailed);
  }

  subscriptionSearchOK = (res) => {
    const quiet = this._subscriptionSearchQuiet;
    this._subscriptionSearchQuiet = false;
    const list = sortSubscriptionRows(
      Array.isArray(res?.body?.result) ? res.body.result : [],
      this.state.subscriptionSortBy,
      this.state.subscriptionSortDir,
    );
    this.setState({
      subscriptionList: list,
      hasSubscriptionSearched: true,
      subscriptionLoading: false,
    });
    this.state.subscriptionList = list;
    this.state.hasSubscriptionSearched = true;
    this.state.subscriptionLoading = false;
    if (!quiet) {
      const foundTemplate = Localization.strings.SUBSCRIPTION_FOUND_COUNT || '找到 {0} 個訂閱計劃';
      this.props.root.addNotification(foundTemplate.replace('{0}', list.length), true);
    }
  }

  subscriptionSearchFailed = (err) => {
    console.log("subscription search failed", err);
    const quiet = this._subscriptionSearchQuiet;
    this._subscriptionSearchQuiet = false;
    const keepRows = quiet && this.state.hasSubscriptionSearched && this.state.subscriptionList.length > 0;
    this.setState({
      subscriptionList: keepRows ? this.state.subscriptionList : [],
      hasSubscriptionSearched: true,
      subscriptionLoading: false,
    });
    if (!keepRows) {
      this.state.subscriptionList = [];
    }
    this.state.hasSubscriptionSearched = true;
    this.state.subscriptionLoading = false;
    if (!keepRows) {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_SEARCH_FAILED, false);
    }
  }

  openSubscriptionLocalesDialog = (subscription) => {
    if (!subscription) return;
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    const localeDialog = createSubscriptionLocalesDialogState({
      open: true,
      subscription,
      loading: true,
    });
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionLocalesDialog: localeDialog,
    });
    this.state.subscriptionLocalesDialog = localeDialog;
    this.state.subscriptionNavigationDirection = 'forward';

    this.props.root.refs.API.request(
      "subscription_epack_list",
      { subscription_id: subscription.id },
      this.subscriptionLocalesOK,
      this.subscriptionLocalesFailed
    );
  }

  subscriptionLocalesOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    const subscriptionId = this.state.subscriptionLocalesDialog.subscription?.id;
    // Refresh the Subscription List's "n/m" Extra Locale badge from what we
    // just fetched, so it does not sit stale behind an assign/remove the admin
    // performed on this page.
    //
    // It MUST be rows.length — the same thing the server counts. This briefly
    // wrote the TEMPLATE count instead, to match a catalogue-joined
    // assigned_extra_locale_count; both halves were reverted 2026-08-30 after
    // subscription 76 ("UAT 學校 202608 [eLAFP]") showed 0/15 with ten locales
    // assigned. Its rows are the 25/26 editions of the pre-made templates —
    // real fulfilment, simply not in the 26/27 catalogue — so the catalogue was
    // never the right definition of what this badge counts. Keep this equal to
    // the server's COUNT(*) so opening the dialog cannot change the number the
    // list already showed.
    const subscriptionList = this.state.subscriptionList.map((subscription) => (
      subscription.id === subscriptionId
        ? { ...subscription, assigned_extra_locale_count: rows.length }
        : subscription
    ));
    this.setState({
      subscriptionList,
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        rows,
        loading: false,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionLocalesDialog.rows = rows;
    this.state.subscriptionLocalesDialog.loading = false;
  }

  subscriptionLocalesFailed = (err) => {
    console.log("subscription locales failed", err);
    this.setState({
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionLocalesDialog.rows = [];
    this.state.subscriptionLocalesDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SUBSCRIPTION_LOCALE_FAILED, false);
  }

  closeSubscriptionLocalesDialog = () => {
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    const localeDialog = createSubscriptionLocalesDialogState();
    this.setState({
      subscriptionNavigationDirection: 'back',
      subscriptionLocalesDialog: localeDialog,
    });
    this.state.subscriptionLocalesDialog = localeDialog;
    this.state.subscriptionNavigationDirection = 'back';
  }

  openSubscriptionPrivilegeDialog = (subscription) => {
    if (!subscription) return;
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionPrivilegeDialog: {
        open: true,
        subscription,
        rows: [],
        loading: true,
      }
    });
    this.state.subscriptionPrivilegeDialog = {
      open: true,
      subscription,
      rows: [],
      loading: true,
    };
    this.state.subscriptionNavigationDirection = 'forward';

    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: subscription.id },
      this.subscriptionPrivilegeOK,
      this.subscriptionPrivilegeFailed
    );
  }

  subscriptionPrivilegeOK = (res) => {
    const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
    const subscriptionId = this.state.subscriptionPrivilegeDialog.subscription?.id;
    const extraLocalePrivilege = rows.find((row) => this.privilegeName(row) === PLAN_EXTRA_LOCALE);
    const maxExtraLocaleNumber = Number(extraLocalePrivilege?.value) || 0;
    const subscriptionList = this.state.subscriptionList.map((subscription) => (
      subscription.id === subscriptionId
        ? { ...subscription, max_extra_locale_number: maxExtraLocaleNumber }
        : subscription
    ));
    this.setState({
      subscriptionList,
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows,
        loading: false,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionPrivilegeDialog.rows = rows;
    this.state.subscriptionPrivilegeDialog.loading = false;
  }

  subscriptionPrivilegeFailed = (err) => {
    console.log("subscription privilege failed", err);
    this.setState({
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionPrivilegeDialog.rows = [];
    this.state.subscriptionPrivilegeDialog.loading = false;
    this.props.root.addNotification(Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED, false);
  }

  closeSubscriptionPrivilegeDialog = () => {
    this.setState({
      subscriptionNavigationDirection: 'back',
      subscriptionPrivilegeDialog: {
        open: false,
        subscription: null,
        rows: [],
        loading: false,
      }
    });
    this.state.subscriptionPrivilegeDialog = {
      open: false,
      subscription: null,
      rows: [],
      loading: false,
    };
    this.state.subscriptionNavigationDirection = 'back';
  }

  // Subscription create dialog handlers
  openSubscriptionCreateDialog = () => {
    const dialog = createSubscriptionCreateDialogState({
      open: true,
      templatesLoading: true,
    });
    this.setState({
      subscriptionNavigationDirection: 'forward',
      subscriptionCreateDialog: dialog,
      subscriptionCopyPrivilegeFrom: null,
    });
    this.state.subscriptionCreateDialog = dialog;
    this.state.subscriptionNavigationDirection = 'forward';
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.loadSubscriptionTemplates();
  }

  openSchoolSubscriptionCreatePage = () => {
    const school = this.state.schoolSubscriptionDialog.school;
    if (!school) return;
    const zhName = (school.name || school.name_eng || '').trim();
    const enName = (school.name_eng || school.name || '').trim();
    const dialog = createSubscriptionCreateDialogState({
      open: true,
      name: `[訂閱]${zhName}`,
      name_en: `[Sub]${enName}`,
      templatesLoading: true,
      returnToSchoolSubscription: true,
    });
    this.setState({
      schoolNavigationDirection: 'forward',
      subscriptionCreateDialog: dialog,
      subscriptionCopyPrivilegeFrom: null,
    });
    this.state.subscriptionCreateDialog = dialog;
    this.state.schoolNavigationDirection = 'forward';
    this.state.subscriptionCopyPrivilegeFrom = null;
    this.loadSubscriptionTemplates();
  }

  closeSubscriptionCreateDialog = () => {
    const returnToSchool = !!this.state.subscriptionCreateDialog.returnToSchoolSubscription;
    const dialog = createSubscriptionCreateDialogState();
    if (returnToSchool) {
      this.setState({
        activeTab: 3,
        schoolNavigationDirection: 'back',
        subscriptionCreateDialog: dialog,
        subscriptionCopyPrivilegeFrom: null,
      });
      this.state.activeTab = 3;
      this.state.schoolNavigationDirection = 'back';
      saveAdminActiveTab(3);
    } else {
      this.setState({
        subscriptionNavigationDirection: 'back',
        subscriptionCreateDialog: dialog,
        subscriptionCopyPrivilegeFrom: null,
      });
      this.state.subscriptionNavigationDirection = 'back';
    }
    this.state.subscriptionCreateDialog = dialog;
    this.state.subscriptionCopyPrivilegeFrom = null;
  }

  loadSubscriptionTemplates = () => {
    if (!this.props.root?.refs?.API) {
      const dialog = {
        ...this.state.subscriptionCreateDialog,
        templatesLoading: false,
      };
      this.setState({ subscriptionCreateDialog: dialog });
      this.state.subscriptionCreateDialog = dialog;
      return;
    }

    this.props.root.refs.API.request(
      "subscription_get",
      {
        is_template: 1,
        limit: 500,
        sort_by: "name",
        sort_dir: "ASC",
      },
      (res) => {
        if (!this.state.subscriptionCreateDialog.open) return;
        const templates = Array.isArray(res?.body?.result) ? res.body.result : [];
        const dialog = {
          ...this.state.subscriptionCreateDialog,
          templates,
          templatesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: dialog });
        this.state.subscriptionCreateDialog = dialog;
      },
      () => {
        if (!this.state.subscriptionCreateDialog.open) return;
        const dialog = {
          ...this.state.subscriptionCreateDialog,
          templates: [],
          templatesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: dialog });
        this.state.subscriptionCreateDialog = dialog;
      }
    );
  }

  handleSubscriptionCopySourceSelect = (subscription) => {
    const sourcePrivilegesLoading = !!subscription?.id;
    const dialog = {
      ...this.state.subscriptionCreateDialog,
      sourcePrivileges: [],
      sourcePrivilegesLoading,
      copyExtraLocales: subscription ? this.state.subscriptionCreateDialog.copyExtraLocales : false,
    };
    this.setState({
      subscriptionCopyPrivilegeFrom: subscription || null,
      subscriptionCreateDialog: dialog,
    });
    this.state.subscriptionCopyPrivilegeFrom = subscription || null;
    this.state.subscriptionCreateDialog = dialog;

    if (!subscription?.id || !this.props.root?.refs?.API) {
      if (sourcePrivilegesLoading) {
        const idleDialog = {
          ...dialog,
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: idleDialog });
        this.state.subscriptionCreateDialog = idleDialog;
      }
      return;
    }

    const sourceId = subscription.id;
    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: sourceId },
      (res) => {
        if (this.state.subscriptionCopyPrivilegeFrom?.id !== sourceId) return;
        const sourcePrivileges = Array.isArray(res?.body?.result) ? res.body.result : [];
        const updatedDialog = {
          ...this.state.subscriptionCreateDialog,
          sourcePrivileges,
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: updatedDialog });
        this.state.subscriptionCreateDialog = updatedDialog;
      },
      () => {
        if (this.state.subscriptionCopyPrivilegeFrom?.id !== sourceId) return;
        const updatedDialog = {
          ...this.state.subscriptionCreateDialog,
          sourcePrivileges: [],
          sourcePrivilegesLoading: false,
        };
        this.setState({ subscriptionCreateDialog: updatedDialog });
        this.state.subscriptionCreateDialog = updatedDialog;
      }
    );
  }

  handleSubscriptionCopyExtraLocalesChange = (event) => {
    const dialog = {
      ...this.state.subscriptionCreateDialog,
      copyExtraLocales: event.currentTarget.checked,
    };
    this.setState({ subscriptionCreateDialog: dialog });
    this.state.subscriptionCreateDialog = dialog;
  }

  openSubscriptionDeleteDialog = (subscription) => {
    if (!subscription || !subscription.id) return;
    const dialog = {
      open: true,
      subscription,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.setState({ subscriptionDeleteDialog: dialog });
    this.state.subscriptionDeleteDialog = dialog;
  }

  closeSubscriptionDeleteDialog = () => {
    const dialog = {
      open: false,
      subscription: null,
      loading: false,
      adminPassword: "",
      assignedSchools: [],
      blocked: false,
    };
    this.setState({ subscriptionDeleteDialog: dialog });
    this.state.subscriptionDeleteDialog = dialog;
  }

  handleSubscriptionDeleteConfirm = () => {
    const dialog = this.state.subscriptionDeleteDialog;
    if (!dialog.subscription || !dialog.subscription.id) return;
    if (!dialog.adminPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_ADMIN_PASSWORD_REQUIRED || '請輸入管理員密碼', false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }

    const loadingDialog = {
      ...dialog,
      loading: true,
    };
    this.setState({ subscriptionDeleteDialog: loadingDialog });
    this.state.subscriptionDeleteDialog = loadingDialog;

    this.props.root.refs.API.request(
      "subscription_delete",
      {
        id: dialog.subscription.id,
        admin_id: adminId,
        admin_password: dialog.adminPassword,
      },
      this.subscriptionDeleteOK,
      this.subscriptionDeleteFailed
    );
  }

  subscriptionDeleteOK = (res) => {
    const result = res?.body?.result;
    if (result && result.success === 1) {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_DELETE_SUCCESS || "Subscription deleted", true);
      this.closeSubscriptionDeleteDialog();
      this.handleSubscriptionSearch();
      return;
    }

    const assignedSchools = Array.isArray(result?.assigned_schools) ? result.assigned_schools : [];
    const isBlocked = assignedSchools.length > 0;
    const message = result?.error || res?.body?.remarks || Localization.strings.SUBSCRIPTION_DELETE_FAILED || "Failed to delete subscription";

    this.setState({
      subscriptionDeleteDialog: {
        ...this.state.subscriptionDeleteDialog,
        loading: false,
        blocked: isBlocked,
        assignedSchools,
      }
    });
    this.state.subscriptionDeleteDialog.loading = false;
    this.state.subscriptionDeleteDialog.blocked = isBlocked;
    this.state.subscriptionDeleteDialog.assignedSchools = assignedSchools;

    this.props.root.addNotification(
      isBlocked
        ? (Localization.strings.SUBSCRIPTION_DELETE_BLOCKED || "Please remove assigned schools first")
        : message,
      false
    );
  }

  subscriptionDeleteFailed = (err) => {
    const message = err?.body?.remarks || err?.remarks || Localization.strings.SUBSCRIPTION_DELETE_FAILED || "Failed to delete subscription";
    this.setState({
      subscriptionDeleteDialog: {
        ...this.state.subscriptionDeleteDialog,
        loading: false,
      }
    });
    this.state.subscriptionDeleteDialog.loading = false;
    this.props.root.addNotification(message, false);
  }

  handleSubscriptionCreateFieldChange = (field) => (e) => {
    const dialog = { ...this.state.subscriptionCreateDialog };
    dialog[field] = e.target.value;
    this.setState({ subscriptionCreateDialog: dialog });
    this.state.subscriptionCreateDialog = dialog;
  }

  handleSubscriptionCreateSubmit = () => {
    const dialog = this.state.subscriptionCreateDialog;
    const name = (dialog.name || "").trim();
    const name_en = (dialog.name_en || "").trim();
    const remarks = (dialog.remarks || "").trim();

    if (name === "") {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_NAME_REQUIRED, false);
      return;
    }
    if (name_en === "") {
      this.props.root.addNotification(Localization.strings.SUBSCRIPTION_NAME_EN_REQUIRED, false);
      return;
    }

    const payload = {
      name,
      name_en,
      remarks,
      is_template: dialog.is_template ? 1 : 0,
    };

    this.setState({
      subscriptionCreateDialog: {
        ...dialog,
        loading: true,
      }
    });
    this.state.subscriptionCreateDialog.loading = true;

    this.props.root.refs.API.request(
      "subscription_create",
      payload,
      this.subscriptionCreateOK,
      this.subscriptionCreateFailed
    );
  }

  subscriptionCreateOK = (res) => {
    const result = res?.body?.result;
    if (!result || result <= 0) {
      const msg = res?.body?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
      this.props.root.addNotification(msg, false);
      this.setState({
        subscriptionCreateDialog: {
          ...this.state.subscriptionCreateDialog,
          loading: false,
        }
      });
      this.state.subscriptionCreateDialog.loading = false;
      return;
    }

    const newSubId = result;
    const createDialog = this.state.subscriptionCreateDialog;
    const createdFromSchool = !!createDialog.returnToSchoolSubscription;
    const createdSubscription = {
      id: newSubId,
      name: (createDialog.name || "").trim(),
      name_en: (createDialog.name_en || "").trim(),
    };
    const tasks = [];
    if (this.state.subscriptionCopyPrivilegeFrom && this.state.subscriptionCopyPrivilegeFrom.id) {
      tasks.push(this.copyPrivilegesFromSubscription(newSubId, this.state.subscriptionCopyPrivilegeFrom.id));
    } else {
      tasks.push(
        this.apiRequestPromise("privilege_get_all", {})
          .then((resAll) => {
            const rows = Array.isArray(resAll?.body?.result) ? resAll.body.result : [];
            if (rows.length === 0) {
              return null;
            }
            const payload = {
              subscription_id: newSubId,
              privilege_array: rows.map((p) => ({
                privilege_id: p.id,
                value: p.value_default,
                value_2: p.value_2_default,
              })),
            };
            return this.apiRequestPromise("subscription_privilege_add", payload);
          })
      );
    }
    if (
      this.state.subscriptionCreateDialog.copyExtraLocales
      && this.state.subscriptionCopyPrivilegeFrom
      && this.state.subscriptionCopyPrivilegeFrom.id
    ) {
      tasks.push(this.copyLocalesFromSubscription(newSubId, this.state.subscriptionCopyPrivilegeFrom.id));
    }

    let copyFailed = false;
    Promise.all(tasks)
      .catch((err) => {
        copyFailed = true;
        const msg = err?.remarks || err?.body?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
        this.props.root.addNotification(msg, false);
      })
      .finally(() => {
        if (!copyFailed) {
          this.props.root.addNotification(Localization.strings.SUBSCRIPTION_CREATE_SUCCESS, true);
        }
        const resetCreateDialog = createSubscriptionCreateDialogState();
        this.state.subscriptionCopyPrivilegeFrom = null;
        this.state.subscriptionCreateDialog = resetCreateDialog;

        if (createdFromSchool && this.state.schoolSubscriptionDialog.open) {
          const schoolDialog = {
            ...this.state.schoolSubscriptionDialog,
            addSelected: createdSubscription,
            pickerKey: Date.now(),
          };
          this.setState({
            activeTab: 3,
            schoolNavigationDirection: 'back',
            subscriptionCopyPrivilegeFrom: null,
            subscriptionCreateDialog: resetCreateDialog,
            schoolSubscriptionDialog: schoolDialog,
          });
          this.state.activeTab = 3;
          this.state.schoolNavigationDirection = 'back';
          this.state.schoolSubscriptionDialog = schoolDialog;
          saveAdminActiveTab(3);
          return;
        }

        this.setState({
          subscriptionSearchText: "",
          subscriptionSortBy: DEFAULT_SUBSCRIPTION_SORT_BY,
          subscriptionSortDir: DEFAULT_SUBSCRIPTION_SORT_DIR,
          subscriptionLimit: 100,
          hasSubscriptionSearched: false,
          subscriptionNavigationDirection: 'back',
          subscriptionCopyPrivilegeFrom: null,
          subscriptionCreateDialog: resetCreateDialog,
        });
        this.state.subscriptionSearchText = "";
        this.state.subscriptionSortBy = DEFAULT_SUBSCRIPTION_SORT_BY;
        this.state.subscriptionSortDir = DEFAULT_SUBSCRIPTION_SORT_DIR;
        this.state.subscriptionLimit = 100;
        this.state.hasSubscriptionSearched = false;
        this.state.subscriptionNavigationDirection = 'back';
        this.handleSubscriptionSearch();
      });
  }

  subscriptionCreateFailed = (err) => {
    const msg = err?.body?.remarks || err?.remarks || Localization.strings.SUBSCRIPTION_CREATE_FAILED;
    this.props.root.addNotification(msg, false);
    this.setState({
      subscriptionCreateDialog: {
        ...this.state.subscriptionCreateDialog,
        loading: false,
      }
    });
    this.state.subscriptionCreateDialog.loading = false;
  }

  apiRequestPromise(cmd, payload) {
    return new Promise((resolve, reject) => {
      if (!this.props.root.refs || !this.props.root.refs.API) {
        reject({ remarks: "API unavailable" });
        return;
      }
      this.props.root.refs.API.request(cmd, payload, (res) => resolve(res), (err) => reject(err));
    });
  }

  copyPrivilegesFromSubscription(newSubId, sourceSubId) {
    return this.apiRequestPromise("subscription_privilege_list", { subscription_id: sourceSubId })
      .then((res) => {
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        if (rows.length === 0) {
          return null;
        }
        return this.apiRequestPromise("subscription_privilege_add", {
          subscription_id: newSubId,
          privilege_array: rows,
        });
      });
  }

  copyLocalesFromSubscription(newSubId, sourceSubId) {
    return this.apiRequestPromise("subscription_epack_list", { subscription_id: sourceSubId })
      .then((res) => {
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        if (rows.length === 0) {
          return null;
        }
        const epackArr = rows.map(r => ({ epack_id: r.epack_id }));
        return this.apiRequestPromise("subscription_epack_add", {
          subscription_id: newSubId,
          epack_array: epackArr,
        });
      });
  }

  refreshSubscriptionLocaleList() {
    const sub = this.state.subscriptionLocalesDialog.subscription;
    if (!sub) return;
    this.setState({
      subscriptionLocalesDialog: {
        ...this.state.subscriptionLocalesDialog,
        loading: true,
      }
    });
    this.props.root.refs.API.request(
      "subscription_epack_list",
      { subscription_id: sub.id },
      this.subscriptionLocalesOK,
      this.subscriptionLocalesFailed
    );
  }

  handleAssignedLocaleSelectionChange = (subscriptionEpackId, checked) => {
    const rowId = String(subscriptionEpackId);
    this.setState((prevState) => {
      const dialog = prevState.subscriptionLocalesDialog;
      const selectedIds = (dialog.selectedAssignedLocaleIds || []).map(String);
      const selectedAssignedLocaleIds = checked
        ? (selectedIds.includes(rowId) ? selectedIds : [...selectedIds, rowId])
        : selectedIds.filter((id) => id !== rowId);
      return {
        subscriptionLocalesDialog: {
          ...dialog,
          selectedAssignedLocaleIds,
        },
      };
    });
  }

  handleRemoveSelectedLocales = () => {
    const dialog = this.state.subscriptionLocalesDialog;
    const ids = [...new Set((dialog.selectedAssignedLocaleIds || []).map(String).filter(Boolean))];
    if (ids.length === 0 || dialog.removing || dialog.assigning) {
      return;
    }
    const confirmTemplate = Localization.strings.SUBSCRIPTION_REMOVE_SELECTED_CONFIRM
      || 'Remove {0} selected LOCALE(s)?';
    const confirmMsg = confirmTemplate.replace('{0}', ids.length);
    const title = Localization.strings.SUBSCRIPTION_LOCALE_TITLE || "";
    const doDelete = async () => {
      this.setState((prevState) => ({
        subscriptionLocalesDialog: {
          ...prevState.subscriptionLocalesDialog,
          removing: true,
        },
      }));

      const results = await Promise.all(ids.map((id) => (
        this.apiRequestPromise("subscription_epack_delete", { id })
          .then(() => ({ ok: true }))
          .catch((error) => ({ ok: false, error }))
      )));
      const failedCount = results.filter((result) => !result.ok).length;
      this.setState((prevState) => ({
        subscriptionLocalesDialog: {
          ...prevState.subscriptionLocalesDialog,
          selectedAssignedLocaleIds: [],
          removing: false,
        },
      }), this.refreshSubscriptionLocaleList);

      if (failedCount > 0) {
        const failureTemplate = Localization.strings.SUBSCRIPTION_REMOVE_SELECTED_FAILED
          || 'Failed to remove {0} LOCALE(s)';
        this.props.root.addNotification(failureTemplate.replace('{0}', failedCount), false);
      }
    };
    const confirmDialog = this.props.root?.refs?.confirmDialog;
    if (confirmDialog) {
      confirmDialog.show(
        { title, content: confirmMsg },
        doDelete,
        () => {}
      );
    } else if (window.confirm(confirmMsg)) {
      doDelete();
    }
  }

  refreshSubscriptionPrivilegeList() {
    const sub = this.state.subscriptionPrivilegeDialog.subscription;
    if (!sub) return;
    this.setState({
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        loading: true,
      }
    });
    this.props.root.refs.API.request(
      "subscription_privilege_list",
      { subscription_id: sub.id },
      this.subscriptionPrivilegeOK,
      this.subscriptionPrivilegeFailed
    );
  }

  handleSubscriptionLocaleSearchModeChange = (searchMode) => {
    if (!['user', 'subscription', 'keyword'].includes(searchMode)) {
      return;
    }
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchMode,
        searchText: '',
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));
  }

  handleSubscriptionLocaleSearchChange = (event) => {
    const searchText = event.currentTarget.value;
    if (this.subscriptionLocaleSearchTimer) {
      clearTimeout(this.subscriptionLocaleSearchTimer);
      this.subscriptionLocaleSearchTimer = null;
    }
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchText,
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));

    const keyword = searchText.trim();
    if (!keyword) {
      return;
    }

    this.subscriptionLocaleSearchTimer = setTimeout(() => {
      this.subscriptionLocaleSearchTimer = null;
      const searchMode = this.state.subscriptionLocalesDialog.searchMode || 'keyword';
      if (searchMode === 'keyword') {
        this.searchSubscriptionLocales(keyword, searchMode);
      } else {
        this.searchSubscriptionLocaleSources(keyword, searchMode);
      }
    }, 350);
  }

  searchSubscriptionLocaleSources(keyword, searchMode) {
    if (
      !this.props.root?.refs?.API
      || !keyword
      || !['user', 'subscription'].includes(searchMode)
    ) {
      return;
    }
    const requestId = ++this.subscriptionLocaleSearchRequestId;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchLoading: true,
        searchSourceResults: [],
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
      },
    }));

    this.props.root.refs.API.request(
      "search_epack_sources",
      {
        keyword,
        search_mode: searchMode,
        exclude_subscription_id: this.state.subscriptionLocalesDialog.subscription?.id,
        limit: 50,
      },
      (res) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState((prevState) => {
          const currentDialog = prevState.subscriptionLocalesDialog;
          if (
            !currentDialog.open
            || currentDialog.searchText.trim() !== keyword
            || (currentDialog.searchMode || 'keyword') !== searchMode
            || currentDialog.selectedSearchSource
          ) {
            return null;
          }
          return {
            subscriptionLocalesDialog: {
              ...currentDialog,
              searchSourceResults: rows,
              searchLoading: false,
            },
          };
        });
      },
      (err) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchSourceResults: [],
            searchLoading: false,
          },
        }));
        const msg = err?.body?.remarks
          || Localization.strings.SUBSCRIPTION_LOCALE_SOURCE_SEARCH_FAILED
          || 'Failed to search users or subscriptions';
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handleSubscriptionLocaleSearchSourceSelect = (row) => {
    const dialog = this.state.subscriptionLocalesDialog;
    const searchMode = dialog.searchMode || 'keyword';
    const sourceId = Number(row?.id);
    const keyword = (dialog.searchText || '').trim();
    if (
      !Number.isInteger(sourceId)
      || sourceId <= 0
      || !keyword
      || !['user', 'subscription'].includes(searchMode)
    ) {
      return;
    }
    const label = searchMode === 'user'
      ? (row.display_name || row.username || '-')
      : (row.name || row.name_en || '-');
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        selectedSearchSource: { ...row, id: sourceId, label },
        searchResults: [],
        selectedLocaleIds: [],
      },
    }), () => {
      this.searchSubscriptionLocales(keyword, searchMode, sourceId);
    });
  }

  handleClearSubscriptionLocaleSearchSource = () => {
    this.subscriptionLocaleSearchRequestId += 1;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        selectedSearchSource: null,
        searchResults: [],
        selectedLocaleIds: [],
        searchLoading: false,
      },
    }));
  }

  searchSubscriptionLocales(keyword, searchMode = 'keyword', sourceId = null) {
    if (!this.props.root?.refs?.API || !keyword) {
      return;
    }
    const requestId = ++this.subscriptionLocaleSearchRequestId;
    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        searchLoading: true,
        searchResults: [],
        selectedLocaleIds: [],
      },
    }));

    this.props.root.refs.API.request(
      "search_epack_by_keyword",
      {
        keyword,
        search_mode: searchMode,
        exclude_subscription_id: this.state.subscriptionLocalesDialog.subscription?.id,
        source_id: sourceId,
        limit: 100,
      },
      (res) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        const rows = Array.isArray(res?.body?.result) ? res.body.result : [];
        this.setState((prevState) => {
          const currentDialog = prevState.subscriptionLocalesDialog;
          if (
            !currentDialog.open
            || currentDialog.searchText.trim() !== keyword
            || (currentDialog.searchMode || 'keyword') !== searchMode
            || (
              sourceId !== null
              && String(currentDialog.selectedSearchSource?.id) !== String(sourceId)
            )
          ) {
            return null;
          }
          return {
            subscriptionLocalesDialog: {
              ...currentDialog,
              searchResults: rows,
              selectedLocaleIds: [],
              searchLoading: false,
            },
          };
        });
      },
      (err) => {
        if (requestId !== this.subscriptionLocaleSearchRequestId) {
          return;
        }
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchResults: [],
            selectedLocaleIds: [],
            searchLoading: false,
          },
        }));
        const msg = err?.body?.remarks
          || Localization.strings.SUBSCRIPTION_LOCALE_SEARCH_FAILED
          || 'Failed to search LOCALEs';
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handleSubscriptionLocaleSelectionChange = (epackId, checked) => {
    const localeId = String(epackId);
    this.setState((prevState) => {
      const dialog = prevState.subscriptionLocalesDialog;
      const selectedLocaleIds = (dialog.selectedLocaleIds || []).map(String);
      let nextSelectedIds;

      // No allowance test here, and none in the render or the assign handler
      // either: PLAN_EXTRA_LOCALE caps catalog templates, this picker can only
      // ever surface ordinary locales, and the server owns the rule for the
      // rows it cannot. See renderSubscriptionLocalesPage. The only refusal
      // left is the double-tick, which would duplicate an id in the selection.
      if (checked) {
        if (selectedLocaleIds.includes(localeId)) {
          return null;
        }
        nextSelectedIds = [...selectedLocaleIds, localeId];
      } else {
        nextSelectedIds = selectedLocaleIds.filter((id) => id !== localeId);
      }

      return {
        subscriptionLocalesDialog: {
          ...dialog,
          selectedLocaleIds: nextSelectedIds,
        },
      };
    });
  }

  /**
   * Why an assign was refused, said in the admin's language wherever the server
   * told us enough to say it.
   *
   * The capacity rule lives on the SERVER (subscription_write.py,
   * add_epacks_to_subscription) and refuses with a typed 409
   * `{"error": "quota_exceeded", "quota": n, "used": n}` — deliberately the same
   * shape /api/extraLocale/select answers, so this reads it exactly the way
   * ExtraLocaleDialog.submitFailed does. That refusal is reachable only for
   * catalog templates; an ordinary attachment is never capped.
   *
   * TWO PLACES TO LOOK, because only one of them is filled today. A route with
   * an `onError` mapper (apiRoutes.js) hands the dict through as
   * `body.error_detail`; `subscription_epack_add` has no mapper, so API.jsx's
   * errorRemarks JSON.stringify's FastAPI's `detail` into `body.remarks` —
   * legible, but as raw JSON in a toast. Parsing that string back gives the
   * admin a sentence now, and the `error_detail` branch means nothing here has
   * to change if the route later gains the mapper.
   *
   * Anything we cannot type (the 403 for a role-2 admin attaching a template,
   * say — a plain string) is shown verbatim, exactly as before.
   */
  subscriptionLocaleAssignFailureMessage = (err) => {
    const body = (err && err.body) || {};
    let detail = body.error_detail || null;
    if (!detail && typeof body.remarks === 'string' && body.remarks.charAt(0) === '{') {
      try {
        const parsed = JSON.parse(body.remarks);
        detail = (parsed && typeof parsed === 'object') ? parsed : null;
      } catch (e) {
        detail = null;
      }
    }
    if (detail && detail.error === 'quota_exceeded') {
      // Reuses the teacher dialog's string: same rule, same two numbers.
      const template = Localization.strings.EXTRA_LOCALE_QUOTA_EXCEEDED
        || "The selection exceeds the plan's allowance ({0} used / limit {1}).";
      return template
        .replace('{0}', detail.used != null ? detail.used : '?')
        .replace('{1}', detail.quota != null ? detail.quota : '?');
    }
    return body.remarks || Localization.strings.SUBSCRIPTION_LOCALE_FAILED;
  }

  handleAssignSelectedLocales = () => {
    const dialog = this.state.subscriptionLocalesDialog;
    const sub = dialog.subscription;
    if (!sub || dialog.assigning || dialog.removing) {
      return;
    }

    const assignedIds = new Set((dialog.rows || []).map((row) => String(row.epack_id)));
    const selectedIds = new Set((dialog.selectedLocaleIds || []).map(String));
    const epackIds = (dialog.searchResults || [])
      .filter((row) => selectedIds.has(String(row.id)) && !assignedIds.has(String(row.id)))
      .map((row) => Number(row.id))
      .filter((id) => Number.isInteger(id) && id > 0);

    // The last of what used to be three client-side capacity gates, and the
    // only one that could write. All three are gone: the PLAN_EXTRA_LOCALE
    // allowance caps catalog templates only, the server enforces that with a
    // typed 409, and this dialog cannot even tell which rows are templates
    // (renderSubscriptionLocalesPage spells out all three reasons). What is
    // left guards an EMPTY write, which the server would answer with a 422.
    if (epackIds.length === 0) {
      return;
    }

    this.setState((prevState) => ({
      subscriptionLocalesDialog: {
        ...prevState.subscriptionLocalesDialog,
        assigning: true,
      },
    }));
    this.props.root.refs.API.request(
      "subscription_epack_add",
      { subscription_id: sub.id, epack_ids: epackIds },
      () => {
        const addedIds = new Set(epackIds.map(String));
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            searchResults: (prevState.subscriptionLocalesDialog.searchResults || [])
              .filter((row) => !addedIds.has(String(row.id))),
            selectedLocaleIds: [],
            assigning: false,
          },
        }), this.refreshSubscriptionLocaleList);
      },
      (err) => {
        const msg = this.subscriptionLocaleAssignFailureMessage(err);
        this.setState((prevState) => ({
          subscriptionLocalesDialog: {
            ...prevState.subscriptionLocalesDialog,
            assigning: false,
          },
        }));
        this.props.root.addNotification(msg, false);
      }
    );
  }

  handlePrivilegeReset = (row) => {
    const id = row.subscription_privilege_id || row.id;
    if (!id) return;
    const payload = {
      id,
      value: row.value_default ?? 0,
      value_2: row.value_2_default ?? 0,
    };
    this.props.root.refs.API.request(
      "subscription_privilege_update",
      payload,
      () => {
        this.refreshSubscriptionPrivilegeList();
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED;
        this.props.root.addNotification(msg, false);
      }
    );
  }

  // Whether a privilege's primary value is boolean (value_type: 0=int, 1=boolean)
  privilegeIsBoolean = (row) => String(row && row.value_type) === '1';

  privilegeName = (row) => String(row?.name || row?.privilege_name || '').toUpperCase();

  // Normalize a stored value (int 0/1 or "true"/"false") to a boolean
  privilegeTruthy = (v) => {
    const s = String(v).toLowerCase();
    return s === '1' || s === 'true';
  };

  // Apply a privilege field value to the dialog rows in local state
  setPrivilegeRowValue = (id, field, value) => {
    const currentRows = this.state.subscriptionPrivilegeDialog.rows || [];
    const changedRow = currentRows.find((r) => (r.subscription_privilege_id || r.id) === id);
    const rows = currentRows.map((r) => {
      const rid = r.subscription_privilege_id || r.id;
      return rid === id ? { ...r, [field]: value } : r;
    });
    const subscriptionId = this.state.subscriptionPrivilegeDialog.subscription?.id;
    const shouldUpdateExtraLocaleMaximum = field === 'value'
      && this.privilegeName(changedRow) === PLAN_EXTRA_LOCALE;
    const subscriptionList = shouldUpdateExtraLocaleMaximum
      ? this.state.subscriptionList.map((subscription) => (
        subscription.id === subscriptionId
          ? { ...subscription, max_extra_locale_number: Number(value) || 0 }
          : subscription
      ))
      : this.state.subscriptionList;
    this.setState({
      subscriptionList,
      subscriptionPrivilegeDialog: {
        ...this.state.subscriptionPrivilegeDialog,
        rows,
      }
    });
    this.state.subscriptionList = subscriptionList;
    this.state.subscriptionPrivilegeDialog.rows = rows;
  }

  // Commit a single privilege field (value or value_2) to the backend.
  // Values are stored as integers; booleans are sent as 1/0. The UI updates
  // optimistically for instant feedback and reverts if the request fails.
  commitPrivilegeField = (row, field, rawValue) => {
    const id = row.subscription_privilege_id || row.id;
    if (!id) {
      return;
    }
    let intVal = parseInt(rawValue, 10);
    if (isNaN(intVal)) {
      intVal = 0;
    }
    if (intVal < 0) {
      intVal = 0;
    }
    const prevVal = row[field];
    const currentInt = parseInt(prevVal, 10);
    if (!isNaN(currentInt) && currentInt === intVal) {
      return; // nothing changed
    }
    this.setPrivilegeRowValue(id, field, intVal);
    const payload = { id };
    payload[field] = intVal;
    this.props.root.refs.API.request(
      "subscription_privilege_update",
      payload,
      () => {
        // Optimistic value already applied; nothing further to do
      },
      (err) => {
        const msg = err?.body?.remarks || Localization.strings.SUBSCRIPTION_PRIVILEGE_FAILED;
        this.props.root.addNotification(msg, false);
        this.setPrivilegeRowValue(id, field, prevVal); // revert
      }
    );
  }

  // STORAGE_LIMIT.value is stored in MB everywhere in the app; this dialog
  // presents and edits it in GB. Convert MB -> GB for display (trimmed).
  mbToGbDisplay = (mb) => {
    const n = Number(mb);
    if (isNaN(n)) {
      return '';
    }
    return parseFloat((n / 1024).toFixed(2)).toString();
  }

  // Commit a GB-entered storage limit back to the backend as MB. Skips the
  // commit when the field is unchanged so a no-edit blur can't corrupt the
  // stored MB value through a lossy GB round-trip.
  commitStoragePrivilegeGb = (row, displayedGbStr, rawGbStr) => {
    if (String(rawGbStr) === String(displayedGbStr)) {
      return; // unchanged
    }
    let gb = parseFloat(rawGbStr);
    if (isNaN(gb) || gb < 0) {
      gb = 0;
    }
    const mb = Math.round(gb * 1024);
    this.commitPrivilegeField(row, 'value', mb);
  }

  handlePasswordChangeOpen = (userId, username) => {
    this.setState({
      passwordChangeDialog: {
        open: true,
        targetUserId: userId,
        targetUsername: username,
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      }
    });
  }

  handlePasswordChangeClose = () => {
    this.setState({
      passwordChangeDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
        isLoading: false,
      }
    });
  }

  handlePasswordChangeFieldChange = (field) => (event) => {
    let dialog = { ...this.state.passwordChangeDialog };
    dialog[field] = event.target.value;
    this.setState({ passwordChangeDialog: dialog });
  }

  handlePasswordChangeSubmit = () => {
    const dialog = this.state.passwordChangeDialog;
    
    // Basic validation
    if (!dialog.adminPassword || !dialog.newPassword || !dialog.confirmPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_FILL_ALL || '請填寫所有欄位', false);
      return;
    }

    if (dialog.newPassword !== dialog.confirmPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_PASSWORD_MISMATCH || '新密碼與確認密碼不相符', false);
      return;
    }

    if (dialog.newPassword.length < 6) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_PASSWORD_TOO_SHORT || '密碼長度至少需要6個字符', false);
      return;
    }

    // Get admin ID automatically
    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }

    // Set loading state
    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ passwordChangeDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
      new_password: dialog.newPassword
    };

    console.log("Sending password change request with admin_id:", adminId);

    this.props.root.refs.API.request(
      "admin_init_user_update_password", 
      requestObj, 
      this.passwordChangeOK, 
      this.passwordChangeFailed
    );
  }

  passwordChangeOK = (res) => {
    console.log("Password change response received", res);
    
    // Check if the operation was actually successful by examining the result field
    if (res && res.body && res.body.result && res.body.result == 1) {
      // True success
      this.props.root.addNotification(
        String(Localization.strings.ADMIN_PW_CHANGED || '用戶 {0} 的密碼已成功更改').replace('{0}', this.state.passwordChangeDialog.targetUsername), 
        true
      );
      this.handlePasswordChangeClose();
    } else {
      // API call succeeded but operation failed (result: 0)
      let errorMessage = Localization.strings.ADMIN_PW_CHANGE_FAILED || '密碼更改失敗';
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);
      
      // Remove loading state but keep dialog open
      let dialog = { ...this.state.passwordChangeDialog };
      dialog.isLoading = false;
      this.setState({ passwordChangeDialog: dialog });
    }
  }

  passwordChangeFailed = (err) => {
    console.log("Password change failed", err);
    let errorMessage = Localization.strings.ADMIN_PW_CHANGE_FAILED || '密碼更改失敗';
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);
    
    // Remove loading state
    let dialog = { ...this.state.passwordChangeDialog };
    dialog.isLoading = false;
    this.setState({ passwordChangeDialog: dialog });
  }

  handleDeleteUserOpen = (userId, username) => {
    this.setState({
      deleteUserDialog: {
        open: true,
        targetUserId: userId,
        targetUsername: username,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleDeleteUserClose = () => {
    this.setState({
      deleteUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleDeleteUserPasswordChange = (event) => {
    let dialog = { ...this.state.deleteUserDialog };
    dialog.adminPassword = event.target.value;
    this.setState({ deleteUserDialog: dialog });
  }

  handleDeleteUserSubmit = () => {
    const dialog = this.state.deleteUserDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_ADMIN_PASSWORD_REQUIRED || '請輸入管理員密碼', false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ deleteUserDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
    };

    this.props.root.refs.API.request(
      "admin_delete_user",
      requestObj,
      this.deleteUserOK,
      this.deleteUserFailed
    );
  }

  deleteUserOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        String(Localization.strings.ADMIN_USER_DELETED || '用戶 {0} 已成功刪除').replace('{0}', this.state.deleteUserDialog.targetUsername),
        true
      );
      this.handleDeleteUserClose();
      this.handleSearchClicked();
    } else {
      let errorMessage = Localization.strings.ADMIN_DELETE_FAILED || '刪除用戶失敗';
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.deleteUserDialog };
      dialog.isLoading = false;
      this.setState({ deleteUserDialog: dialog });
    }
  }

  deleteUserFailed = (err) => {
    let errorMessage = Localization.strings.ADMIN_DELETE_FAILED || '刪除用戶失敗';
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.deleteUserDialog };
    dialog.isLoading = false;
    this.setState({ deleteUserDialog: dialog });
  }

  handleEditUserOpen = (user) => {
    this.setState({
      editUserDialog: {
        open: true,
        targetUserId: user.id,
        targetUsername: user.username,
        displayName: user.display_name || "",
        email: user.email || "",
        role: parseInt(user.role) || 0,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleEditUserClose = () => {
    this.setState({
      editUserDialog: {
        open: false,
        targetUserId: 0,
        targetUsername: "",
        displayName: "",
        email: "",
        role: 0,
        adminPassword: "",
        isLoading: false,
      }
    });
  }

  handleEditUserFieldChange = (field) => (event) => {
    let dialog = { ...this.state.editUserDialog };
    dialog[field] = event.target.value;
    this.setState({ editUserDialog: dialog });
  }

  handleEditUserSubmit = () => {
    const dialog = this.state.editUserDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_ADMIN_PASSWORD_REQUIRED || '請輸入管理員密碼', false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ editUserDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_user_id: dialog.targetUserId,
      display_name: dialog.displayName,
      email: dialog.email,
      role: dialog.role,
    };

    this.props.root.refs.API.request(
      "admin_edit_user",
      requestObj,
      this.editUserOK,
      this.editUserFailed
    );
  }

  editUserOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        String(Localization.strings.ADMIN_USER_UPDATED || '用戶 {0} 的資料已成功更新').replace('{0}', this.state.editUserDialog.targetUsername),
        true
      );
      this.handleEditUserClose();
      this.handleSearchClicked();
    } else {
      let errorMessage = Localization.strings.ADMIN_UPDATE_FAILED || '更新用戶資料失敗';
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.editUserDialog };
      dialog.isLoading = false;
      this.setState({ editUserDialog: dialog });
    }
  }

  editUserFailed = (err) => {
    let errorMessage = Localization.strings.ADMIN_UPDATE_FAILED || '更新用戶資料失敗';
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.editUserDialog };
    dialog.isLoading = false;
    this.setState({ editUserDialog: dialog });
  }

  handleEditSchoolOpen = (school) => {
    this.setState({
      schoolNavigationDirection: 'forward',
      editSchoolDialog: {
        open: true,
        targetSchoolId: school.id,
        name: school.name || "",
        nameEng: school.name_eng || "",
        key: school.key || "",
        locale: school.locale || "",
        schoolType: parseInt(school.school_type) || 0,
        schoolLevel: parseInt(school.school_level) || 0,
        adminPassword: "",
        isLoading: false,
      }
    });
    this.state.schoolNavigationDirection = 'forward';
  }

  handleEditSchoolClose = () => {
    this.setState({
      schoolNavigationDirection: 'back',
      editSchoolDialog: {
        open: false,
        targetSchoolId: 0,
        name: "",
        nameEng: "",
        key: "",
        locale: "",
        schoolType: 0,
        schoolLevel: 0,
        adminPassword: "",
        isLoading: false,
      }
    });
    this.state.schoolNavigationDirection = 'back';
  }

  handleEditSchoolFieldChange = (field) => (event) => {
    let dialog = { ...this.state.editSchoolDialog };
    dialog[field] = event.target.value;
    this.setState({ editSchoolDialog: dialog });
  }

  handleEditSchoolSubmit = () => {
    const dialog = this.state.editSchoolDialog;

    if (!dialog.adminPassword) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_ADMIN_PASSWORD_REQUIRED || '請輸入管理員密碼', false);
      return;
    }

    if (!dialog.name || dialog.name.trim() === "") {
      this.props.root.addNotification("學校名稱不可為空", false);
      return;
    }

    if (!dialog.nameEng || dialog.nameEng.trim() === "") {
      this.props.root.addNotification("英文名稱不可為空", false);
      return;
    }

    if (!dialog.key || dialog.key.trim() === "") {
      this.props.root.addNotification("代碼不可為空", false);
      return;
    }

    const keyPattern = /^[a-zA-Z0-9_]+$/;
    if (!keyPattern.test(dialog.key.trim())) {
      this.props.root.addNotification("代碼只能包含英文字母、數字及底線", false);
      return;
    }

    let adminId = 0;
    try {
      adminId = this.props.root.getMyUserId();
    } catch (e) {
      console.log("Could not get admin ID:", e);
    }

    if (!adminId || adminId <= 0) {
      this.props.root.addNotification(Localization.strings.ADMIN_ERR_NO_ADMIN_ID || '無法獲取管理員身份，請重新登入', false);
      return;
    }

    let loadingDialog = { ...dialog };
    loadingDialog.isLoading = true;
    this.setState({ editSchoolDialog: loadingDialog });

    const requestObj = {
      admin_id: adminId,
      admin_password: dialog.adminPassword,
      target_school_id: dialog.targetSchoolId,
      name: dialog.name.trim(),
      name_eng: dialog.nameEng.trim(),
      key: dialog.key.trim(),
      locale: dialog.locale.trim(),
      school_type: dialog.schoolType,
      school_level: dialog.schoolLevel,
    };

    this.props.root.refs.API.request(
      "admin_edit_school",
      requestObj,
      this.editSchoolOK,
      this.editSchoolFailed
    );
  }

  editSchoolOK = (res) => {
    if (res && res.body && res.body.result && res.body.result == 1) {
      this.props.root.addNotification(
        `學校 (ID: ${this.state.editSchoolDialog.targetSchoolId}) 的資料已成功更新`,
        true
      );
      this.handleEditSchoolClose();
      this.handleSchoolSearchClicked();
    } else {
      let errorMessage = "更新學校資料失敗";
      if (res && res.body && res.body.remarks) {
        errorMessage += ": " + res.body.remarks;
      }
      this.props.root.addNotification(errorMessage, false);

      let dialog = { ...this.state.editSchoolDialog };
      dialog.isLoading = false;
      this.setState({ editSchoolDialog: dialog });
    }
  }

  editSchoolFailed = (err) => {
    let errorMessage = "更新學校資料失敗";
    if (err && err.body && err.body.remarks) {
      errorMessage += ": " + err.body.remarks;
    } else if (err && err.remarks) {
      errorMessage += ": " + err.remarks;
    }
    this.props.root.addNotification(errorMessage, false);

    let dialog = { ...this.state.editSchoolDialog };
    dialog.isLoading = false;
    this.setState({ editSchoolDialog: dialog });
  }

  renderBatchRowsTable(rows) {
    return (
      <ScrollArea.Autosize mah={280} type="auto">
        <Table striped highlightOnHover verticalSpacing="xs" stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>CSV 行</Table.Th>
              <Table.Th>用戶名稱</Table.Th>
              <Table.Th w={90}>學校編號</Table.Th>
              <Table.Th>狀態</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, i) => (
              <Table.Tr key={i}>
                <Table.Td>{row.line}</Table.Td>
                <Table.Td>{row.username || '-'}</Table.Td>
                <Table.Td>{row.school_id != null ? row.school_id : '-'}</Table.Td>
                <Table.Td>
                  {row.status === 'error' ? (
                    <Text c="red" fz="sm">{this.getBatchReasonText(row.reason)}</Text>
                  ) : (
                    <Text c="teal" fz="sm">{row.status === 'inserted' ? '已建立' : '可建立'}</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    );
  }

  renderBatchCreateUserDialog() {
    const dialog = this.state.batchCreateDialog;
    return (
      <Modal
        opened={dialog.open}
        onClose={this.handleBatchCreateClose}
        closeOnClickOutside={!dialog.isLoading}
        closeOnEscape={!dialog.isLoading}
        withCloseButton={!dialog.isLoading}
        title={<Text fw={600}>{Localization.strings.ADMIN_BATCH_CREATE_USERS || '批量建立用戶 (CSV)'}</Text>}
        size="lg"
        centered
        zIndex={2000}
      >
        {dialog.step === 'upload' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
              <div>上傳 CSV 檔案以一次過建立多名用戶。第一行必須是欄位標題：</div>
              <div style={{ marginTop: 6 }}>
                <strong>必要欄位:</strong> username (用戶名稱)、password (密碼, 至少6個字符)、school_id (學校編號或 School Key)
              </div>
              <div>
                <strong>可選欄位:</strong> display_name (顯示名稱)、email (電郵)、role (權限: 0 一般用戶 / 1 進階用戶 / 2 管理員, 留空為 0)
              </div>
              <div style={{ marginTop: 6, color: '#888' }}>
                每次最多 500 行，多餘的欄位會被忽略。上傳後會先驗證資料並顯示預覽，確認後才會寫入資料庫。密碼會在伺服器端加密儲存。
              </div>
            </Box>
            <Group>
              <Button variant="light" leftSection={<IconDownload size={16} />} onClick={this.handleBatchTemplateDownload}>
                下載 CSV 範本
              </Button>
              <FileButton key={dialog.fileInputKey} onChange={this.handleBatchFileSelected} accept=".csv,text/csv">
                {(props) => (
                  <Button {...props} leftSection={<IconUpload size={16} />} loading={dialog.isLoading}>
                    上傳 CSV 檔案
                  </Button>
                )}
              </FileButton>
            </Group>
          </Stack>
        )}

        {dialog.step === 'confirm' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <div><strong>檔案:</strong> {dialog.fileName} (共 {dialog.reviewRows.length} 行資料)</div>
              <div style={{ marginTop: 4 }}>
                <Text component="span" c="teal" fw={600}>{dialog.validCount} 名用戶可建立</Text>
                {dialog.invalidCount > 0 && (
                  <Text component="span" c="red" fw={600} ml="md">{dialog.invalidCount} 行有問題 (將被略過)</Text>
                )}
              </div>
            </Box>
            {this.renderBatchRowsTable(dialog.reviewRows)}
            <TextInput
              type="password"
              label="管理員密碼"
              required
              description="輸入您的管理員密碼以確認建立以上用戶"
              value={dialog.adminPassword}
              onChange={this.handleBatchAdminPasswordChange}
              disabled={dialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleBatchCreateBackToUpload} disabled={dialog.isLoading}>
                重新選擇檔案
              </Button>
              <Button
                onClick={this.handleBatchConfirmSubmit}
                loading={dialog.isLoading}
                disabled={dialog.validCount <= 0}
                leftSection={<IconUsersPlus size={16} />}
              >
                確認建立 {dialog.validCount} 名用戶
              </Button>
            </Group>
          </Stack>
        )}

        {dialog.step === 'result' && (
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: dialog.insertedCount > 0 ? '#e6f7ee' : '#fdecea', borderRadius: 6 }}>
              <Text fw={600}>已成功建立 {dialog.insertedCount} 名用戶</Text>
              {dialog.invalidCount > 0 && (
                <Text c="red" fz="sm" mt={4}>{dialog.invalidCount} 行未能建立，原因見下表</Text>
              )}
            </Box>
            {this.renderBatchRowsTable(dialog.resultRows)}
            <Group justify="flex-end" mt="sm">
              <Button onClick={this.handleBatchCreateClose}>完成</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    );
  }

  renderActiveTab() {
    if (!this.isTabAllowed(this.state.activeTab)) {
      // Same restore-from-localStorage guard as the Extra Locale cases below,
      // widened to every platform-only page for a school admin.
      return this.createUser();
    }
    switch (this.state.activeTab) {
      case 0: return this.createUser();
      case 1: return this.userList();
      case 2: return this.createSchool();
      case 3: return this.schoolList();
      case 4: return this.subscriptionTab();
      case EDCITY_SCRN_TAB_INDEX: return this.edcityScrnTab();
      case SUBSCRIPTION_BINDING_TAB_INDEX: return this.subscriptionBindingTab();
      case EXTRA_LOCALE_TAB_INDEX:
        // A role-2 admin can restore this index from localStorage without ever
        // seeing the (platform-admin-only) sidebar item — fall back rather
        // than render a page whose every request would 403.
        return this.isPlatformAdmin() ? this.extraLocaleTab() : this.createUser();
      case EXTRA_LOCALE_REPORT_TAB_INDEX:
        // Same restore-from-localStorage guard, same fallback.
        return this.isPlatformAdmin() ? this.extraLocaleReportTab() : this.createUser();
      case DASHBOARD_TAB_INDEX:
        return this.isPlatformAdmin() ? this.dashboardTab() : this.createUser();
      default: return null;
    }
  }

  render() {
    const tasks = this.getTasks();
    const tabIcons = [
      <IconUserPlus size={18} />,
      <IconUsers size={18} />,
      <IconSchool size={18} />,
      <IconBuildings size={18} />,
      <IconLicense size={18} />,
    ];
    return (
      <div>
        <Modal
          opened={this.state.open}
          onClose={this.handleClose}
          withCloseButton={false}
          padding={0}
          size="85%"
          radius="md"
          centered
          zIndex={1500}
          styles={{
            content: { height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
            body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 },
          }}
        >
          {/* Brand header */}
          <Group justify="space-between" px="lg" style={{ height: 56, flexShrink: 0, backgroundColor: BRAND_PRIMARY }}>
            <Text c="white" fw={600} fz="lg">{Localization.strings.ADMIN_TASK}</Text>
            <CloseButton onClick={this.handleClose} variant="transparent" c="white" size="lg" />
          </Group>

          {/* Body: left sidebar nav + right content */}
          <Flex style={{ flex: 1, minHeight: 0 }}>
            <Box
              style={{
                width: 220,
                flexShrink: 0,
                borderRight: '1px solid #e9ecef',
                backgroundColor: '#f8f9fa',
                overflowY: 'auto',
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              {tasks.map((label, index) => (
                <NavLink
                  key={index}
                  active={this.state.activeTab === index}
                  label={label}
                  leftSection={tabIcons[index]}
                  onClick={() => this.changeTab(index)}
                />
              ))}

              {/* EdCity Login section — platform admins only (school admins see
                  Create User + User List and nothing else; see isSchoolAdminOnly) */}
              {this.isPlatformAdmin() && (
                <>
                  <Divider my={8} mx="sm" />
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" px="md" py={4} style={{ letterSpacing: 0.5 }}>
                    {Localization.strings.EDCITY_LOGIN_SECTION || 'EdCity 登入'}
                  </Text>
                  <NavLink
                    key="edcity-scrn-mapping"
                    active={this.state.activeTab === EDCITY_SCRN_TAB_INDEX}
                    label={Localization.strings.EDCITY_SCRN_TAB || 'EdCity 學校 ID 對接'}
                    leftSection={<IconPlugConnected size={18} />}
                    onClick={() => this.changeTab(EDCITY_SCRN_TAB_INDEX)}
                  />
                  <NavLink
                    key="subscription-binding"
                    active={this.state.activeTab === SUBSCRIPTION_BINDING_TAB_INDEX}
                    label={Localization.strings.SUBSCRIPTION_BINDING_TAB || '訂閱綁定'}
                    leftSection={<IconAdjustments size={18} />}
                    onClick={() => this.changeTab(SUBSCRIPTION_BINDING_TAB_INDEX)}
                  />
                </>
              )}

              {/* Extra Locale (預製教材範本) section — platform admins only */}
              {this.isPlatformAdmin() && (
                <>
                  <Divider my={8} mx="sm" />
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" px="md" py={4} style={{ letterSpacing: 0.5 }}>
                    {Localization.strings.EXTRA_LOCALE_ADMIN_SECTION || '預製教材範本'}
                  </Text>
                  <NavLink
                    key="extra-locale"
                    active={this.state.activeTab === EXTRA_LOCALE_TAB_INDEX}
                    label={Localization.strings.EXTRA_LOCALE_ADMIN_TAB || '範本管理'}
                    leftSection={<IconBooks size={18} />}
                    onClick={() => this.changeTab(EXTRA_LOCALE_TAB_INDEX)}
                  />
                  <NavLink
                    key="extra-locale-report"
                    active={this.state.activeTab === EXTRA_LOCALE_REPORT_TAB_INDEX}
                    label={Localization.strings.EXTRA_LOCALE_REPORT_TAB || '通知電郵設定'}
                    leftSection={<IconMail size={18} />}
                    onClick={() => this.changeTab(EXTRA_LOCALE_REPORT_TAB_INDEX)}
                  />
                </>
              )}

              {/* Dashboards — own section below Extra Locales, platform admins only */}
              {this.isPlatformAdmin() && (
                <>
                  <Divider my={8} mx="sm" />
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" px="md" py={4} style={{ letterSpacing: 0.5 }}>
                    {Localization.strings.DASHBOARD_SECTION || '數據概覽'}
                  </Text>
                  <NavLink
                    key="platform-dashboard"
                    active={this.state.activeTab === DASHBOARD_TAB_INDEX}
                    label={Localization.strings.DASHBOARD_TAB || '數據概覽'}
                    leftSection={<IconChartBar size={18} />}
                    onClick={() => this.changeTab(DASHBOARD_TAB_INDEX)}
                  />
                </>
              )}
            </Box>
            <ScrollArea style={{ flex: 1, minHeight: 0 }}>
              <Box p="xl" style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                {this.renderActiveTab()}
              </Box>
            </ScrollArea>
          </Flex>
        </Modal>

        {/* Batch Create User (CSV) Dialog */}
        {this.renderBatchCreateUserDialog()}

        {/* EdCity SCRN mapping: Import Mapping Excel dialog */}
        {this.renderEdcityImportDialog()}

        {/* Extra Locale (預製教材範本) dialogs */}
        {this.renderExtraLocaleEditDialog()}
        {this.renderExtraLocaleDeleteDialog()}
        {this.renderExtraLocaleManualDeleteDialog()}

        {/* Password Change Dialog */}
        <Modal
          opened={this.state.passwordChangeDialog.open}
          onClose={this.handlePasswordChangeClose}
          closeOnClickOutside={!this.state.passwordChangeDialog.isLoading}
          closeOnEscape={!this.state.passwordChangeDialog.isLoading}
          withCloseButton={!this.state.passwordChangeDialog.isLoading}
          title={<Text fw={600}>{Localization.strings.ADMIN_PW_DIALOG_TITLE || '更改用戶密碼'}</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>{Localization.strings.ADMIN_TARGET_USER || '目標用戶'}:</strong> {this.state.passwordChangeDialog.targetUsername} (ID: {this.state.passwordChangeDialog.targetUserId})
            </Box>
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_PASSWORD || '管理員密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_DESC || '輸入您的管理員密碼以確認身份'}
              value={this.state.passwordChangeDialog.adminPassword}
              onChange={this.handlePasswordChangeFieldChange('adminPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
            />
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_NEW_USER_PASSWORD || '目標用戶新密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_MIN_DESC || '密碼長度至少6個字符'}
              value={this.state.passwordChangeDialog.newPassword}
              onChange={this.handlePasswordChangeFieldChange('newPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
            />
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_CONFIRM_NEW_PASSWORD || '目標用戶新密碼(再輸入一次)'}
              required
              description={Localization.strings.ADMIN_CONFIRM_NEW_PASSWORD_DESC || '請再次輸入新密碼'}
              value={this.state.passwordChangeDialog.confirmPassword}
              onChange={this.handlePasswordChangeFieldChange('confirmPassword')}
              disabled={this.state.passwordChangeDialog.isLoading}
              error={(this.state.passwordChangeDialog.newPassword !== this.state.passwordChangeDialog.confirmPassword && this.state.passwordChangeDialog.confirmPassword !== '') ? (Localization.strings.ADMIN_ERR_PASSWORDS_DIFFER || '密碼不相符') : undefined}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handlePasswordChangeClose} disabled={this.state.passwordChangeDialog.isLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button color="orange" onClick={this.handlePasswordChangeSubmit} loading={this.state.passwordChangeDialog.isLoading}>
                {Localization.strings.CHANGE_PASSWORD || '更改密碼'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete User Dialog */}
        <Modal
          opened={this.state.deleteUserDialog.open}
          onClose={this.handleDeleteUserClose}
          closeOnClickOutside={!this.state.deleteUserDialog.isLoading}
          closeOnEscape={!this.state.deleteUserDialog.isLoading}
          withCloseButton={!this.state.deleteUserDialog.isLoading}
          title={<Text fw={600} c="red">{Localization.strings.ADMIN_DELETE_USER || '刪除用戶'}</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#fdecea', borderRadius: 6, border: '1px solid #f5c6cb' }}>
              <strong>{Localization.strings.ADMIN_WARNING || '警告'}:</strong>{' '}
              {String(Localization.strings.ADMIN_DELETE_WARNING || '此操作會將用戶帳號名稱更改為 "Deleted-{0}"，該用戶將無法再登入。').replace('{0}', this.state.deleteUserDialog.targetUsername)}
            </Box>
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>{Localization.strings.ADMIN_TARGET_USER || '目標用戶'}:</strong> {this.state.deleteUserDialog.targetUsername} (ID: {this.state.deleteUserDialog.targetUserId})
            </Box>
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_PASSWORD || '管理員密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_DESC || '輸入您的管理員密碼以確認身份'}
              value={this.state.deleteUserDialog.adminPassword}
              onChange={this.handleDeleteUserPasswordChange}
              disabled={this.state.deleteUserDialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleDeleteUserClose} disabled={this.state.deleteUserDialog.isLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button color="red" onClick={this.handleDeleteUserSubmit} loading={this.state.deleteUserDialog.isLoading}>
                {Localization.strings.ADMIN_CONFIRM_DELETE || '確認刪除'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit User Dialog */}
        <Modal
          opened={this.state.editUserDialog.open}
          onClose={this.handleEditUserClose}
          closeOnClickOutside={!this.state.editUserDialog.isLoading}
          closeOnEscape={!this.state.editUserDialog.isLoading}
          withCloseButton={!this.state.editUserDialog.isLoading}
          title={<Text fw={600}>{Localization.strings.ADMIN_EDIT_USER || '編輯用戶資料'}</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>{Localization.strings.ADMIN_TARGET_USER || '目標用戶'}:</strong> {this.state.editUserDialog.targetUsername} (ID: {this.state.editUserDialog.targetUserId})
            </Box>
            <TextInput
              label={Localization.strings.DISPLAY_NAME || '顯示名稱'}
              value={this.state.editUserDialog.displayName}
              onChange={this.handleEditUserFieldChange('displayName')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <TextInput
              label={Localization.strings.EMAIL || '電郵'}
              value={this.state.editUserDialog.email}
              onChange={this.handleEditUserFieldChange('email')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <Select
              label={Localization.strings.ADMIN_ROLE_LABEL || '權限'}
              allowDeselect={false}
              disabled={this.state.editUserDialog.isLoading}
              value={String(this.state.editUserDialog.role)}
              onChange={(v) => this.handleEditUserFieldChange('role')({ target: { value: Number(v) } })}
              data={this.assignableRoleOptions({ currentRole: this.state.editUserDialog.role })}
            />
            <Divider />
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_PASSWORD || '管理員密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_DESC || '輸入您的管理員密碼以確認身份'}
              value={this.state.editUserDialog.adminPassword}
              onChange={this.handleEditUserFieldChange('adminPassword')}
              disabled={this.state.editUserDialog.isLoading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleEditUserClose} disabled={this.state.editUserDialog.isLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button onClick={this.handleEditUserSubmit} loading={this.state.editUserDialog.isLoading}>
                {Localization.strings.ADMIN_SAVE_CHANGES || '儲存變更'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Subscription delete dialog */}
        <Modal
          opened={this.state.subscriptionDeleteDialog.open}
          onClose={this.closeSubscriptionDeleteDialog}
          closeOnClickOutside={!this.state.subscriptionDeleteDialog.loading}
          closeOnEscape={!this.state.subscriptionDeleteDialog.loading}
          withCloseButton={!this.state.subscriptionDeleteDialog.loading}
          title={<Text fw={600} c="red">{Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete Subscription'}</Text>}
          size="md"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <strong>{Localization.strings.SUBSCRIPTION || 'Subscription'}:</strong>{' '}
              {this.state.subscriptionDeleteDialog.subscription
                ? `${this.state.subscriptionDeleteDialog.subscription.name || '-'} (${this.state.subscriptionDeleteDialog.subscription.name_en || '-'})`
                : '-'}
            </Box>
            <TextInput
              type="password"
              label={Localization.strings.ADMIN_PASSWORD || '管理員密碼'}
              required
              description={Localization.strings.ADMIN_PASSWORD_DESC || '輸入您的管理員密碼以確認身份'}
              value={this.state.subscriptionDeleteDialog.adminPassword}
              onChange={(e) => {
                const dialog = {
                  ...this.state.subscriptionDeleteDialog,
                  adminPassword: e.target.value,
                };
                this.setState({ subscriptionDeleteDialog: dialog });
                this.state.subscriptionDeleteDialog = dialog;
              }}
              disabled={this.state.subscriptionDeleteDialog.loading}
            />
            {this.state.subscriptionDeleteDialog.blocked ? (
              <Box>
                <Text c="red" fw={700} mb="xs">
                  {Localization.strings.SUBSCRIPTION_DELETE_BLOCKED || 'Delete the assigned school subscriptions first.'}
                </Text>
                <Text mb={4}>{Localization.strings.SUBSCRIPTION_ASSIGNED_SCHOOLS || 'Assigned schools'}</Text>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>{Localization.strings.NAME || 'Name'}</Table.Th>
                      <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN || 'English Name'}</Table.Th>
                      <Table.Th>{Localization.strings.SCHOOL_SUBSCRIPTION_START || 'Start Date'}</Table.Th>
                      <Table.Th>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || 'Expire Date'}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {this.state.subscriptionDeleteDialog.assignedSchools.map((row, idx) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{row.school_id}</Table.Td>
                        <Table.Td>{row.name || '-'}</Table.Td>
                        <Table.Td>{row.name_eng || '-'}</Table.Td>
                        <Table.Td>{row.date_start || '-'}</Table.Td>
                        <Table.Td>{row.date_expire || '-'}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            ) : (
              <Text c="dimmed">
                {Localization.strings.SUBSCRIPTION_DELETE_CONFIRM || 'Are you sure you want to delete this subscription?'}
              </Text>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.closeSubscriptionDeleteDialog} disabled={this.state.subscriptionDeleteDialog.loading}>
                {this.state.subscriptionDeleteDialog.blocked
                  ? (Localization.strings.CLOSE || 'Close')
                  : (Localization.strings.CANCEL || 'Cancel')}
              </Button>
              {!this.state.subscriptionDeleteDialog.blocked && (
                <Button color="red" onClick={this.handleSubscriptionDeleteConfirm} loading={this.state.subscriptionDeleteDialog.loading}>
                  {Localization.strings.SUBSCRIPTION_DELETE || Localization.strings.DELETE || 'Delete'}
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>

        {/* Legacy school subscription modal kept closed; content now renders in the school detail page. */}
        <Modal
          opened={false}
          onClose={this.closeSchoolSubscriptionDialog}
          title={<Text fw={600}>{Localization.strings.SCHOOL_SUBSCRIPTION_DIALOG_TITLE || '學校訂閱'}{this.state.schoolSubscriptionDialog.school ? ` - ${this.state.schoolSubscriptionDialog.school.name || this.state.schoolSubscriptionDialog.school.name_eng || ''}` : ''}</Text>}
          size="76rem"
          centered
          zIndex={2000}
          styles={{
            content: {
              height: '76vh',
              minHeight: '36rem',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
            body: {
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            },
          }}
        >
          <Stack gap="md" style={{ height: '100%', minHeight: 0 }}>
            <Box w="45%" miw={260} maw={440}>
              <SubscriptionPicker
                key={this.state.schoolSubscriptionDialog.pickerKey}
                root={this.props.root}
                placeholder={Localization.strings.SCHOOL_SUBSCRIPTION_SEARCH_PLACEHOLDER || '搜尋訂閱'}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onSelect={(sub) => {
                  const dialog = { ...this.state.schoolSubscriptionDialog, addSelected: sub };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
              />
            </Box>
            <Group justify="flex-start" align="flex-end">
              <TextInput
                type="date"
                label={Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}
                value={this.state.schoolSubscriptionDialog.addDateStart}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onChange={(e) => {
                  const dialog = { ...this.state.schoolSubscriptionDialog, addDateStart: e.target.value };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
                w={180}
              />
              <Select
                label={Localization.strings.SCHOOL_SUBSCRIPTION_DURATION || '期限'}
                value={this.state.schoolSubscriptionDialog.addDurationMonths}
                data={[
                  { value: '6', label: Localization.strings.SCHOOL_SUBSCRIPTION_HALF_YEAR || '半年' },
                  { value: '12', label: Localization.strings.SCHOOL_SUBSCRIPTION_ONE_YEAR || '1 年' },
                  { value: '24', label: Localization.strings.SCHOOL_SUBSCRIPTION_TWO_YEARS || '2 年' },
                  { value: '36', label: Localization.strings.SCHOOL_SUBSCRIPTION_THREE_YEARS || '3 年' },
                ]}
                allowDeselect={false}
                disabled={this.state.schoolSubscriptionDialog.addLoading || this.state.schoolSubscriptionDialog.loading}
                onChange={(value) => {
                  const dialog = {
                    ...this.state.schoolSubscriptionDialog,
                    addDurationMonths: value || DEFAULT_SUBSCRIPTION_DURATION_MONTHS,
                  };
                  this.setState({ schoolSubscriptionDialog: dialog });
                  this.state.schoolSubscriptionDialog = dialog;
                }}
                w={180}
              />
              <Button
                onClick={this.handleSchoolSubscriptionAdd}
                loading={this.state.schoolSubscriptionDialog.addLoading}
                disabled={this.state.schoolSubscriptionDialog.loading}
                leftSection={<IconPlus size={16} />}
              >
                {Localization.strings.SCHOOL_SUBSCRIPTION_ADD || '為學校加入訂閱'}
              </Button>
            </Group>
            <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {this.state.schoolSubscriptionDialog.loading ? (
                <Group justify="center" p="md"><Loader size="sm" /><Text c="dimmed">{Localization.strings.LOADING_DATA}</Text></Group>
              ) : (
                <Table.ScrollContainer minWidth={900}>
                  <Table striped verticalSpacing="sm" style={{ tableLayout: 'fixed', minWidth: 900 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: 56 }}>ID</Table.Th>
                        <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_ZH}</Table.Th>
                        <Table.Th>{Localization.strings.SUBSCRIPTION_NAME_EN}</Table.Th>
                        <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}</Table.Th>
                        <Table.Th style={{ width: 142 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}</Table.Th>
                        <Table.Th style={{ width: 92 }}>{Localization.strings.SCHOOL_SUBSCRIPTION_STATUS || '狀態'}</Table.Th>
                        <Table.Th>{Localization.strings.REMARK}</Table.Th>
                        <Table.Th style={{ width: 76 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {this.state.schoolSubscriptionDialog.rows.map((row, idx) => {
                        const dialog = this.state.schoolSubscriptionDialog;
                        const isEditing = dialog.editingRow?.school_subscription_id === row.school_subscription_id;
                        const editingRow = dialog.editingRow;
                        const isUpdateLoading = dialog.updateLoading;
                        const isDeleteLoading = dialog.deleteLoading;
                        const isBusy = isUpdateLoading || isDeleteLoading || dialog.loading;
                        const canStartEditing = !editingRow && !isBusy;
                        const displayedDates = isEditing ? editingRow : row;
                        const isActive = isSubscriptionActive(displayedDates.date_start, displayedDates.date_expire);
                        const editableCellStyle = {
                          width: '100%',
                          minHeight: 30,
                          padding: 0,
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          font: 'inherit',
                          textAlign: 'left',
                          cursor: canStartEditing ? 'text' : 'default',
                        };
                        return (
                          <Table.Tr
                            key={row.school_subscription_id || idx}
                            onBlur={isEditing ? this.handleSchoolSubscriptionEditBlur : undefined}
                          >
                            <Table.Td>{row.subscription_id}</Table.Td>
                            <Table.Td>{row.subscription_name || '-'}</Table.Td>
                            <Table.Td>{row.subscription_name_en || '-'}</Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  type="date"
                                  size="xs"
                                  value={editingRow.date_start}
                                  autoFocus={dialog.editingField === 'date_start'}
                                  disabled={isUpdateLoading}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, date_start: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_start')}
                                >
                                  {row.date_start || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  type="date"
                                  size="xs"
                                  value={editingRow.date_expire}
                                  autoFocus={dialog.editingField === 'date_expire'}
                                  disabled={isUpdateLoading}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, date_expire: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'date_expire')}
                                >
                                  {row.date_expire || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge color={isActive ? 'green' : 'red'} variant="light" radius="sm">
                                {isActive
                                  ? (Localization.strings.SCHOOL_SUBSCRIPTION_ACTIVE || 'Active')
                                  : (Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRED || 'Expired')}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {isEditing ? (
                                <TextInput
                                  size="xs"
                                  value={editingRow.remark}
                                  autoFocus={dialog.editingField === 'remark'}
                                  disabled={isUpdateLoading}
                                  placeholder={Localization.strings.REMARK || '備註'}
                                  onChange={(e) => {
                                    const updated = { ...dialog, editingRow: { ...editingRow, remark: e.target.value } };
                                    this.setState({ schoolSubscriptionDialog: updated });
                                    this.state.schoolSubscriptionDialog = updated;
                                  }}
                                />
                              ) : (
                                <Box
                                  component="button"
                                  type="button"
                                  disabled={!canStartEditing}
                                  style={editableCellStyle}
                                  onClick={() => this.handleSchoolSubscriptionEditStart(row, 'remark')}
                                >
                                  {row.school_subscription_remark || '-'}
                                </Box>
                              )}
                            </Table.Td>
                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                              {isEditing ? (
                                <Group gap={4} wrap="nowrap">
                                  <Tooltip label={Localization.strings.SAVE || '儲存'}>
                                    <ActionIcon color="green" variant="subtle" onClick={this.handleSchoolSubscriptionUpdate} loading={isUpdateLoading}>
                                      <IconCheck size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label={Localization.strings.CANCEL || '取消'}>
                                    <ActionIcon color="red" variant="subtle" onClick={this.handleSchoolSubscriptionEditCancel} disabled={isUpdateLoading}>
                                      <IconX size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              ) : (
                                <Tooltip label={Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}>
                                  <ActionIcon variant="subtle" color="red" onClick={() => this.handleSchoolSubscriptionDelete(row)} disabled={isBusy} loading={isDeleteLoading}>
                                    <IconTrash size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                      {this.state.schoolSubscriptionDialog.rows.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={8} style={{ textAlign: 'center', padding: 12 }}>
                            {Localization.strings.SCHOOL_SUBSCRIPTION_NONE || '暫無訂閱'}
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Box>
          </Stack>
        </Modal>

        {/* Delete subscription confirmation dialog */}
        <Modal
          opened={!!this.state.schoolSubscriptionDialog.deleteConfirmRow}
          onClose={this.handleSchoolSubscriptionDeleteCancel}
          title={<Text fw={600} c="red">{Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}</Text>}
          size="sm"
          centered
          zIndex={2100}
        >
          <Stack gap="md">
            <Text>{Localization.strings.SCHOOL_SUBSCRIPTION_DELETE_CONFIRM || '確定要從學校移除此訂閱嗎？'}</Text>
            {this.state.schoolSubscriptionDialog.deleteConfirmRow && (
              <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
                <div><strong>{Localization.strings.SUBSCRIPTION_NAME_ZH || '訂閱名稱'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.subscription_name || '-'}</div>
                <div><strong>{Localization.strings.SUBSCRIPTION_NAME_EN || 'Name (EN)'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.subscription_name_en || '-'}</div>
                <div><strong>{Localization.strings.SCHOOL_SUBSCRIPTION_START || '開始日'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.date_start || '-'}</div>
                <div><strong>{Localization.strings.SCHOOL_SUBSCRIPTION_EXPIRE || '到期日'}:</strong> {this.state.schoolSubscriptionDialog.deleteConfirmRow.date_expire || '-'}</div>
              </Box>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.handleSchoolSubscriptionDeleteCancel} disabled={this.state.schoolSubscriptionDialog.deleteLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button color="red" onClick={this.handleSchoolSubscriptionDeleteConfirm} loading={this.state.schoolSubscriptionDialog.deleteLoading}>
                {Localization.strings.SCHOOL_SUBSCRIPTION_DELETE || '移除訂閱'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* EdCity SCRN mapping: create dialog */}
        <Modal
          opened={this.state.edcityScrnCreateDialog.open}
          onClose={this.closeEdcityScrnCreateDialog}
          closeOnClickOutside={!this.state.edcityScrnCreateDialog.loading}
          closeOnEscape={!this.state.edcityScrnCreateDialog.loading}
          withCloseButton={!this.state.edcityScrnCreateDialog.loading}
          title={<Text fw={600}>{Localization.strings.EDCITY_SCRN_CREATE || '新增對接記錄'}</Text>}
          size="md"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <TextInput
              label={Localization.strings.EDCITY_SCRN_CODE || 'SCRN'}
              required
              description={Localization.strings.EDCITY_SCRN_CODE_HINT || 'EDB 學校註冊編號 (SCRN)，須為 12 位數字'}
              placeholder="e.g. 190101000133"
              maxLength={12}
              value={this.state.edcityScrnCreateDialog.school_code}
              onChange={this.handleEdcityScrnCreateFieldChange('school_code')}
              disabled={this.state.edcityScrnCreateDialog.loading}
              error={
                this.state.edcityScrnCreateDialog.school_code !== ""
                  && !/^\d{12}$/.test(this.state.edcityScrnCreateDialog.school_code.trim())
                  ? (Localization.strings.EDCITY_SCRN_CODE_HINT || 'SCRN 須為 12 位數字')
                  : undefined
              }
            />
            <TextInput
              label={Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱'}
              placeholder="EdCity 提供的學校名稱"
              maxLength={255}
              value={this.state.edcityScrnCreateDialog.note}
              onChange={this.handleEdcityScrnCreateFieldChange('note')}
              disabled={this.state.edcityScrnCreateDialog.loading}
            />
            <Select
              label={Localization.strings.EDCITY_SCRN_CONNECT || '對接學校'}
              description={Localization.strings.EDCITY_SCRN_PICK_SCHOOL_OPTIONAL || '可留空，稍後再對接'}
              placeholder={Localization.strings.EDCITY_SCRN_PICK_SCHOOL || '選擇本系統中的學校'}
              searchable
              clearable
              limit={50}
              data={this.getEdcitySchoolSelectData()}
              value={this.state.edcityScrnCreateDialog.school_id || null}
              onChange={this.handleEdcityScrnCreateSchoolChange}
              disabled={this.state.edcityScrnCreateDialog.loading}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.closeEdcityScrnCreateDialog} disabled={this.state.edcityScrnCreateDialog.loading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button
                onClick={this.handleEdcityScrnCreateSubmit}
                loading={this.state.edcityScrnCreateDialog.loading}
                leftSection={<IconPlus size={16} />}
              >
                {Localization.strings.EDCITY_SCRN_CREATE || '新增對接記錄'}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* EdCity SCRN mapping: connect-with-school dialog */}
        <Modal
          opened={this.state.edcityScrnConnectDialog.open}
          onClose={this.closeEdcityScrnConnectDialog}
          closeOnClickOutside={!this.state.edcityScrnConnectDialog.loading}
          closeOnEscape={!this.state.edcityScrnConnectDialog.loading}
          withCloseButton={!this.state.edcityScrnConnectDialog.loading}
          title={<Text fw={600}>{Localization.strings.EDCITY_SCRN_CONNECT || '對接學校'}</Text>}
          size="md"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            {this.state.edcityScrnConnectDialog.row && (
              <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
                <div><strong>{Localization.strings.EDCITY_SCRN_CODE || 'SCRN'}:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{this.state.edcityScrnConnectDialog.row.school_code}</span>
                </div>
                <div><strong>{Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱'}:</strong>{' '}
                  {this.state.edcityScrnConnectDialog.row.note || '-'}
                </div>
              </Box>
            )}
            <Select
              label={Localization.strings.EDCITY_SCRN_PICK_SCHOOL || '選擇本系統中的學校'}
              placeholder={Localization.strings.EDCITY_SCRN_PICK_SCHOOL || '選擇本系統中的學校'}
              searchable
              clearable
              limit={50}
              data={this.getEdcitySchoolSelectData()}
              value={this.state.edcityScrnConnectDialog.school_id || null}
              onChange={this.handleEdcityScrnConnectSchoolChange}
              disabled={this.state.edcityScrnConnectDialog.loading}
            />
            <TextInput
              label={Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱'}
              maxLength={255}
              value={this.state.edcityScrnConnectDialog.note}
              onChange={this.handleEdcityScrnConnectNoteChange}
              disabled={this.state.edcityScrnConnectDialog.loading}
            />
            <Group justify="space-between" mt="sm">
              <Box>
                {this.state.edcityScrnConnectDialog.row
                  && this.state.edcityScrnConnectDialog.row.school_id != null
                  && parseInt(this.state.edcityScrnConnectDialog.row.school_id) > 0 && (
                  <Button
                    variant="outline"
                    color="red"
                    onClick={this.handleEdcityScrnDisconnect}
                    disabled={this.state.edcityScrnConnectDialog.loading}
                    leftSection={<IconUnlink size={16} />}
                  >
                    {Localization.strings.EDCITY_SCRN_DISCONNECT || '清除對接'}
                  </Button>
                )}
              </Box>
              <Group>
                <Button variant="default" onClick={this.closeEdcityScrnConnectDialog} disabled={this.state.edcityScrnConnectDialog.loading}>
                  {Localization.strings.CANCEL || '取消'}
                </Button>
                <Button
                  onClick={this.handleEdcityScrnConnectSubmit}
                  loading={this.state.edcityScrnConnectDialog.loading}
                  disabled={!this.state.edcityScrnConnectDialog.school_id}
                  leftSection={<IconLink size={16} />}
                >
                  {Localization.strings.EDCITY_SCRN_CONNECT || '對接學校'}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Modal>

        {/* EdCity SCRN mapping: remove confirmation dialog */}
        <Modal
          opened={!!this.state.edcityScrnDeleteRow}
          onClose={this.closeEdcityScrnDeleteDialog}
          title={<Text fw={600} c="red">{Localization.strings.EDCITY_SCRN_REMOVE || '移除'}</Text>}
          size="sm"
          centered
          zIndex={2000}
        >
          <Stack gap="md">
            <Text>{Localization.strings.EDCITY_SCRN_REMOVE_CONFIRM || '確定要移除此 EdCity 對接記錄嗎？'}</Text>
            {this.state.edcityScrnDeleteRow && (
              <Box style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6, fontSize: 14 }}>
                <div><strong>{Localization.strings.EDCITY_SCRN_CODE || 'SCRN'}:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{this.state.edcityScrnDeleteRow.school_code}</span>
                </div>
                <div><strong>{Localization.strings.EDCITY_SCRN_SCHOOL_NAME || 'EdCity 學校名稱'}:</strong>{' '}
                  {this.state.edcityScrnDeleteRow.note || '-'}
                </div>
                {this.state.edcityScrnDeleteRow.school_id != null && parseInt(this.state.edcityScrnDeleteRow.school_id) > 0 && (
                  <div><strong>{Localization.strings.EDCITY_SCRN_OUR_SCHOOL_NAME || '學校名稱'}:</strong>{' '}
                    {this.state.edcityScrnDeleteRow.school_name || `ID ${this.state.edcityScrnDeleteRow.school_id}`}
                  </div>
                )}
              </Box>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={this.closeEdcityScrnDeleteDialog} disabled={this.state.edcityScrnDeleteLoading}>
                {Localization.strings.CANCEL || '取消'}
              </Button>
              <Button color="red" onClick={this.handleEdcityScrnDeleteConfirm} loading={this.state.edcityScrnDeleteLoading} leftSection={<IconTrash size={16} />}>
                {Localization.strings.EDCITY_SCRN_REMOVE || '移除'}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    )
  }
}

export default AdminTaskDialog;
